import IntegrationsInteroperability from '@/pages/profile-settings/IntegrationsInteroperability.jsx';
import { institutionDisplayName } from "./institutionDisplay.js";
import { institutionAppointmentCalendarCounts } from "./institutionDashboardCalendar.mjs";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { TOP_BAR_TITLE_CLASS_NAME } from "@/sot/primitives/TopBar.jsx";
import AvatarThumb44 from "@/sot/primitives/AvatarThumb44.jsx";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import surveillanceIcon from "@/icons/afrimed/security.svg";
import moreVertIcon from "@/icons/afrimed/more_vert.svg";
import mailIcon from "@/icons/afrimed/mail.svg";
import callIcon from "@/icons/afrimed/call.svg";
import badgeIcon from "@/icons/afrimed/badge.svg";
import hospitalIcon from "@/icons/afrimed/hospital.svg";
import listIcon from "@/icons/afrimed/list.svg";
import calendarClockIcon from "@/icons/afrimed/calendar_clock.svg";
import descriptionIcon from "@/icons/afrimed/description.svg";
import lightModeIcon from "@/icons/afrimed/light_mode.svg";
import darkModeIcon from "@/icons/afrimed/dark_mode.svg";
import languageIcon from "@/icons/afrimed/language.svg";
import settingsIcon from "@/icons/afrimed/settings.svg";
import closeIcon from "@/icons/afrimed/close.svg";
import accountCircleFilledIcon from "@/icons/afrimed/account_circle_filled.svg?no-inline";
import {
  Building2,
  BookOpen,
  CalendarClock,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  CreditCard,
  FileText,
  Filter,
  Landmark,
  LayoutDashboard,
  MapPin,
  MessageSquareWarning,
  Menu,
  Plus,
  Search,
  Settings,
  SquarePen,
  Stethoscope,
  Users,
  UsersRound,
  UserRound,
  LogOut,
  X,
} from "lucide-react";

import RouteFallback from "@/sot/primitives/RouteFallback.jsx";
import Sheet from "@/sot/primitives/Sheet.jsx";
import AvatarThumb68 from "@/sot/primitives/AvatarThumb68.jsx";
import TextField56 from "@/sot/primitives/TextField56.jsx";
import DatePicker from "@/sot/primitives/datePicker.jsx";
import { AfriTimePickerPanel, formatAfriTime } from "@/sot/primitives/AfriTimePicker.jsx";
import CoverCropModal from "@/components/media/CoverCropModal.jsx";
import AvatarHeaderPositioned from "@/sot/primitives/AvatarHeaderPositioned.jsx";
import AfriSwitch from "@/components/ui/AfriSwitch.jsx";
import FocusChip from "@/sot/primitives/FocusChip.jsx";
import Snackbar from "@/sot/primitives/Snackbar.jsx";
import SelectCountrySADC from "@/components/fields/SelectCountrySADC.jsx";
import InputPhoneSADC, { getPhoneMarketByIso, getPhoneMarketFromE164 } from "@/components/auth/InputPhoneSADC.jsx";
import ProfilePictureUpload from "@/components/ui/ProfilePictureUpload.jsx";
import InstitutionProfileCover from "./InstitutionProfileCover.jsx";
import RegistrationDocumentPickerContent from "@/components/ui/RegistrationDocumentPickerContent.jsx";
import DoctorSalutationDropdown from "@/pages/shared/DoctorSalutationDropdown.jsx";
import { DOCTOR_SALUTATION_OPTIONS, normalizeProfileSalutation, resolveAddressedProfileName } from "@/pages/shared/profileAddressedName.js";
import AccountPasswordLifecyclePanel from "@/pages/profile-settings/AccountPasswordLifecyclePanel.jsx";
import AccountEmailChangeAction from "@/pages/profile-settings/AccountEmailChangeAction.jsx";
import { FeedbackForm } from "@/pages/feedback/FeedbackPortal.jsx";
import InstitutionRegionalHealthWorkspace from "@/pages/institutions/InstitutionRegionalHealthWorkspace.jsx";
import InstitutionAlertJourney from "@/pages/institutions/InstitutionAlertJourney.jsx";
import InstitutionFollowupWorkspace from "@/pages/institutions/InstitutionFollowupWorkspace.jsx";
import AfrimedSearchBar from "@/components/ui/AfrimedSearchBar.jsx";
import Select from "@/components/ui/Select.jsx";
import Checkbox from "@/components/ui/Checkbox.jsx";
import MemberActionMenu from "@/components/ui/MemberActionMenu.jsx";
import { useAuth } from "@/contexts/AuthContext.jsx";
import { useTheme } from "@/contexts/ThemeContext.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import { persistThemeForUser } from "@/settings/theme.js";
import { logout } from "@/lib/auth/logout.js";
import Button from "@/components/ui/Button.jsx";
import BasicDialogCritical from "@/components/ui/BasicDialogCritical.jsx";
import WorkspaceAccountContract, { WORKSPACE_ACCOUNT_GRID_CLASS } from "@/sot/layouts/WorkspaceAccountContract.jsx";
import SettingsScreenShell from "@/sot/layouts/SettingsScreenShell.jsx";
import { requestAccountPasswordReset } from "@/services/passwordRecoveryService.js";
import { updateOwnAccountDisplayName } from "@/services/accountIdentityService.js";
import supabase from "@/lib/supabase";
import {
  assignInstitutionSurveillanceAuthority,
  assignMedicalInstitutionFacilityMember,
  createMedicalInstitutionFacilityMemberInvitation,
  createMedicalInstitutionFacility,
  createInstitutionClinician,
  findInstitutionClinicianExactMatch,
  updateInstitutionClinician,
  changeInstitutionClinicianStatus,
  getMedicalInstitutionFacilityLogoUrl,
  getInstitutionMemberIntakeErrorMessage,
  getMemberStatus,
  MEMBER_ADMIN_ROLES,
  inviteInstitutionScopedMember,
  loadInstitutionScopedInvitationSnapshot,
  loadInstitutionWorkspace,
  resolveMoHWorkspaceDisposition,
  loadMedicalInstitutionNetwork,
  uploadInstitutionClinicianEvidence,
  removeInstitutionClinicianEvidence,
  loadInstitutionClinicianInvitationManagerSnapshot,
  loadInstitutionClinicianCoverDisplay,
  loadMedicalInstitutionFacilityCoverDisplay,
  sendInstitutionClinicianInvitation,
  resendInstitutionClinicianInvitation,
  revokeInstitutionClinicianInvitation,
  revokeInstitutionScopedMemberInvitation,
  revokeInstitutionSurveillanceAuthority,
  updateInstitutionMemberStatus,
  uploadMedicalInstitutionFacilityLogo,
  removeMedicalInstitutionFacilityLogo,
  uploadMedicalInstitutionFacilityCover,
  removeMedicalInstitutionFacilityCover,
  searchMedicalInstitutionAdminCandidates,
  sendInstitutionEvaluationReviewerInvite,
  updateMedicalInstitutionFacility,
} from "./institutionService.js";
import { ServiceEditor, ServicesPage } from "./services/ServicesPage.jsx";
import { InstitutionPatientEditor, InstitutionPatientsList, patientPath } from "./patients/InstitutionPatientsPage.jsx";
import { formatInstitutionPatientAddress } from "./patients/patientAddress.mjs";
import InstitutionPatientDetail from "./patients/InstitutionPatientDetail.jsx";
import InstitutionStatusFilterChip from "./InstitutionStatusFilterChip.jsx";
import InstitutionLifecycleBadge from "./InstitutionLifecycleBadge.jsx";
import GovernedCsvImport from "./GovernedCsvImport.jsx";
import InstitutionRegistryCtas from "./InstitutionRegistryCtas.jsx";
import InstitutionHome from "./InstitutionHome.jsx";
import InstitutionHelp from "./InstitutionHelp.jsx";
import InstitutionNotifications from "./InstitutionNotifications.jsx";
import InstitutionPageShell from "./InstitutionPageShell.jsx";
import { INSTITUTION_TOP_BAR_NAVIGATION } from "./InstitutionTopBar.jsx";
import {
  recordInstitutionHomeFacilityView,
  recordInstitutionHomeMemberView,
} from "./institutionHomeHistory.js";
import {
  addMedicalInstitutionCustomSpecialty,
  addMedicalInstitutionSpecialtyId,
  getMedicalInstitutionSpecialtyState,
  normalizeMedicalInstitutionCustomSpecialties,
  normalizeMedicalInstitutionSpecialties,
  normalizeMedicalInstitutionSpecialtyIds,
  normalizeMedicalInstitutionSpecialtyText,
  removeMedicalInstitutionCustomSpecialty,
  removeMedicalInstitutionSpecialtyId,
} from "./medicalInstitutionSpecialties.js";
import { normaliseClinicianFocusAreas, serialiseClinicianFocusAreas } from "./clinicianFocusAreas.js";
import {
  buildMedicalInstitutionMemberSearchIndex,
  filterMedicalInstitutionMemberSearchIndex,
  hasMedicalInstitutionSearchQuery,
} from "./memberSearchIndex.js";
import { loadLanguagesFromCSVs } from "@/utils/languages.js";
import "./institutions.css";

const MI_ICON_ROOT = "/icons/medical-institution";
const INSTITUTION_PROFILE_MORE_VERT_ICON = `${MI_ICON_ROOT}/more_vert.svg`;
const CLINICIAN_ICON_ROOT = "/icons/clinicians";
// AfriMed icon source of truth: Design System icon family, Figma node 55594:2483.
const ICONS = {
  logo: `${MI_ICON_ROOT}/Logo-mark-26.37 x 32.svg`,
  wordmark: `${MI_ICON_ROOT}/AfriMed-word-mark-80.28 x 16.2.svg`,
  canonicalBrand: "/assets/images/afrimed_logo.svg",
  dashboard: `${MI_ICON_ROOT}/dashboard 24 x 24.svg`,
  patients: `${MI_ICON_ROOT}/patients.svg`,
  dashboardPatients: `${MI_ICON_ROOT}/Patient_Filled.svg`,
  rating: `${MI_ICON_ROOT}/overall-rating.svg`,
  appointments: `${MI_ICON_ROOT}/appointments-nav-24 x 24.svg`,
  services: `${MI_ICON_ROOT}/services.svg`,
  clinicians: `${MI_ICON_ROOT}/doctors-24 x 24.svg`,
  members: `${MI_ICON_ROOT}/person.svg`,
  pagePatients: `${MI_ICON_ROOT}/groups_fill.svg`,
  settings: `${MI_ICON_ROOT}/settings.svg`,
  help: `${MI_ICON_ROOT}/help.svg`,
  bell: `${MI_ICON_ROOT}/notifications-bell.svg`,
  search: `${MI_ICON_ROOT}/search.svg`,
  // Figma Medical Institutions registry 61317:34889: facility/building icon,
  // not the legacy dotted status mark.
  facilityHospital: "/icons/facilities/hospital 18 x 18.svg",
  pageMedicalInstitutions: `${MI_ICON_ROOT}/hospital_Surface_Fill.svg`,
  menuMedicalInstitutions: `${MI_ICON_ROOT}/hospital_Fill.svg`,
  facilityAvatar: `${MI_ICON_ROOT}/hospital_avatar.svg`,
  facilityListAvatar: `${MI_ICON_ROOT}/hospital_avatar.svg`,
  facilityAvatarPlaceholder: `${MI_ICON_ROOT}/medical_institutes_avatar_placeholder.svg`,
  facilityEdit: `${MI_ICON_ROOT}/edit button.svg`,
  coverUpload: "/icons/patient/upload_image.svg",
  coverTakePhoto: "/icons/patient/take_photo.svg",
  coverDelete: "/icons/doctor/delete_critical.svg",
  facilityMember: `${MI_ICON_ROOT}/assignment_ind.svg`,
  facilityPerson: `${MI_ICON_ROOT}/person.svg`,
  facilityMail: `${MI_ICON_ROOT}/mail.svg`,
  facilityCall: `${MI_ICON_ROOT}/call.svg`,
  facilityRole: `${MI_ICON_ROOT}/badge_role.svg`,
  facilityChevronBack: `${MI_ICON_ROOT}/chevron_back.svg`,
  facilityAvailability: `${MI_ICON_ROOT}/calendar_clock.svg`,
  facilityDelete: "/icons/doctor/DeleteBackgroundCritical.svg",
  hospitalMedia: `${MI_ICON_ROOT}/Media.svg`,
  homeHealth: `${MI_ICON_ROOT}/Logo-mark-26.37 x 32.svg`,
  inventory: `${MI_ICON_ROOT}/inventory.svg`,
  payments: `${MI_ICON_ROOT}/radio_button_unchecked.svg`,
  governance: `${MI_ICON_ROOT}/radio_button_partial.svg`,
  filter: `${MI_ICON_ROOT}/unfold_more.svg`,
  // Add actions use the AfriMed CTA plus, never the medical-institution status mark.
  add: `${CLINICIAN_ICON_ROOT}/add.svg`,
  expand: `${MI_ICON_ROOT}/expand-more - 18 x 18.svg`,
  unfoldMore: `${MI_ICON_ROOT}/unfold_more.svg`,
  pastYear: `${MI_ICON_ROOT}/past-year.svg`,
  navigateBefore: `${MI_ICON_ROOT}/navigate-before.svg`,
  navigateNext: `${MI_ICON_ROOT}/navigate-next.svg`,
  badgeUp: `${MI_ICON_ROOT}/arrow_circle_up-18 x 18.svg`,
  radioAppointment: `${MI_ICON_ROOT}/radio_button_checked_fill.svg`,
  radioEvisit: `${MI_ICON_ROOT}/radio_button_checked_fill-1.svg`,
  radioPending: `${MI_ICON_ROOT}/radio_button_checked_fill.svg`,
  riskNormal: `${MI_ICON_ROOT}/radio_button_checked.svg`,
  riskCaution: `${MI_ICON_ROOT}/radio_button_partial.svg`,
  riskCritical: `${MI_ICON_ROOT}/radio_button_unchecked.svg`,
  chartMark: `${MI_ICON_ROOT}/mark.svg`,
  signOut: `/icons/appointments/dashboard/log-out-critical.svg`,
  clinicianAdd: `${CLINICIAN_ICON_ROOT}/add.svg`,
  clinicianEmail: `${CLINICIAN_ICON_ROOT}/mail.svg`,
  clinicianExpand: `${CLINICIAN_ICON_ROOT}/expand-more.svg`,
  clinicianFilter: `${CLINICIAN_ICON_ROOT}/filter-list.svg`,
  clinicianPhone: `${CLINICIAN_ICON_ROOT}/phone.svg`,
  clinicianAvatar: `${CLINICIAN_ICON_ROOT}/Profile_Avatar.svg`,
  clinicianListAvatar: `${CLINICIAN_ICON_ROOT}/clinician_list_avatar.svg`,
  clinicianUploadedEdit: `${CLINICIAN_ICON_ROOT}/edit_ped_full.svg`,
  clinicianDate: `${CLINICIAN_ICON_ROOT}/date_range_Fill.svg`,
  clinicianExperience: `${CLINICIAN_ICON_ROOT}/medical_services.svg`,
  clinicianDiploma: `${CLINICIAN_ICON_ROOT}/diploma-bold.svg`,
  clinicianDiplomaLarge: `${CLINICIAN_ICON_ROOT}/solar_diploma-bold.svg`,
  clinicianFormationAdd: `${CLINICIAN_ICON_ROOT}/add_primary.svg`,
  clinicianStamp: `${CLINICIAN_ICON_ROOT}/AfriMed_Stamp_Mark.svg`,
  clinicianThumbnail: `${CLINICIAN_ICON_ROOT}/Image-thumbnail.svg`,
  clinicianLink: `${CLINICIAN_ICON_ROOT}/link.svg`,
  clinicianNewTab: `${CLINICIAN_ICON_ROOT}/new_tab.svg`,
  clinicianReport: `${CLINICIAN_ICON_ROOT}/report.svg`,
  clinicianDelete: `${CLINICIAN_ICON_ROOT}/delete.svg`,
  fieldEmail: mailIcon,
  fieldPhone: callIcon,
  fieldRole: badgeIcon,
  fieldFacility: hospitalIcon,
  fieldProgramme: listIcon,
  fieldDate: calendarClockIcon,
  fieldReason: descriptionIcon,
  clinicianStatus: `${CLINICIAN_ICON_ROOT}/radio_button_unchecked.svg`,
  surveillance: surveillanceIcon,
  regionalHealth: surveillanceIcon,
  themeLight: lightModeIcon,
  themeDark: darkModeIcon,
  // The Design System does not publish a monitor glyph in its icon library.
  // Settings is its approved device-preference fallback for System theme.
  themeSystem: settingsIcon,
  language: languageIcon,
  close: closeIcon,
  accountCircleFilled: accountCircleFilledIcon,
  moreVert: moreVertIcon,
};

const FIGMA_NODES_USED = [
  "55594:2483",
  "59898:19344",
  "61521:79625",
  "61521:79626",
  "61521:79823",
  "61521:79830",
  "61521:79815",
  "61317:34839",
  "61317:34889",
  "61521:79954",
  "60888:37792",
  "61193:16616",
  "61365:144048",
  "61216:105301",
  "61242:83051",
  "61245:108252",
  "61521:79628",
  "60781:68895",
  "61361:140770",
  "61354:139824",
  "60802:55616",
  "61521:79955",
  "61521:79954",
  "61521:80106",
  "61521:79842",
  "61521:79843",
  "61521:79853",
  "61521:79869",
  "61521:79885",
  "61521:79902",
  "61521:79919",
  "61521:79936",
  "61521:90700",
  "61586:99517",
  "61521:95607",
  "61583:74444",
  "61586:118535",
  "55594:2485",
];

const INSTITUTION_ROUTE_PATHS = [
  "/institutions/dashboard",
  "/institutions/settings",
  "/institutions/users",
  "/institutions/patients",
  "/institutions/facilities",
  "/institutions/members",
  "/institutions/surveillance-team",
  "/institutions/clinicians",
  "/institutions/services",
  "/institutions/payments",
  "/institutions/governance",
  "/institutions/regional-health",
  "/institutions/account",
];

const MENU_ITEMS = [
  { to: "/institutions/dashboard", label: "Dashboard", labelKey: "navigation.dashboard", icon: LayoutDashboard },
  { to: "/institutions/clinicians", label: "Clinicians", labelKey: "navigation.clinicians", iconSrc: ICONS.clinicians },
  { to: "/institutions/members", label: "Members", labelKey: "navigation.members", iconSrc: ICONS.members },
  { to: "/institutions/surveillance-team", label: "Surveillance Team", labelKey: "navigation.surveillanceTeam", iconSrc: ICONS.surveillance, requiresRegionalHealth: true },
  { to: "/institutions/services", label: "Services", labelKey: "navigation.services", iconSrc: ICONS.services },
  { to: "/institutions/regional-health", label: "Regional Health", labelKey: "navigation.regionalHealth", iconSrc: ICONS.regionalHealth, requiresRegionalHealth: true },
  { to: "/institutions/patients", label: "Patients", labelKey: "navigation.patients", iconSrc: ICONS.patients },
  { to: "/institutions/settings", label: "Settings", labelKey: "navigation.settings", iconSrc: ICONS.settings, icon: Settings },
];

function canAccessInstitutionConfiguration(workspace) {
  const userId = String(workspace?.user?.id || "").trim();
  const institutionId = String(workspace?.activeInstitution?.id || "").trim();
  if (!userId || !institutionId) return false;

  const activeMembership = (workspace?.memberships || []).find((member) =>
    String(member?.institution_id || "").trim() === institutionId &&
    String(member?.user_id || "").trim() === userId &&
    getMemberStatus(member) === "active"
  );
  if (String(activeMembership?.role || "").trim().toLowerCase() === "owner") return true;

  const platformRoles = (workspace?.userRoles || []).map((role) => String(role || "").trim().toLowerCase());
  return Boolean(activeMembership) && platformRoles.includes("super_admin");
}

const HEADER_TABS = [
  { label: "Regional Health", labelKey: "navigation.regionalHealth", to: "/institutions/regional-health", enabled: true, requiresRegionalHealth: true },
];

const INSTITUTION_SHELL_LABEL_KEYS = {
  Dashboard: "navigation.dashboard",
  Clinicians: "navigation.clinicians",
  Members: "navigation.members",
  Services: "navigation.services",
  "Regional Health": "navigation.regionalHealth",
  Patients: "navigation.patients",
  Settings: "navigation.settings",
};

const RISK_LEVEL_ICON = {
  normal: ICONS.riskNormal,
  caution: ICONS.riskCaution,
  critical: ICONS.riskCritical,
};

const LEGACY_SETUP_TEST_COPY = "Requires setup";

const SCOPED_MEMBER_AUDIENCE_OPTIONS = [
  { value: "researcher", label: "Researcher" },
  { value: "programme_staff", label: "Programme staff" },
  { value: "public_health_professional", label: "Public-health professional" },
  { value: "community_health_worker", label: "Community health worker" },
  { value: "research_field_staff", label: "Research • field staff" },
];
const CLINICIAN_PROFESSION_OPTIONS = [
  { value: "clinician", label: "Clinician / doctor" },
  { value: "nurse", label: "Nurse" },
  { value: "midwife", label: "Midwife" },
  { value: "pharmacist", label: "Pharmacist" },
  { value: "laboratory_professional", label: "Laboratory professional" },
  { value: "imaging_professional", label: "Imaging professional" },
  { value: "allied_health_professional", label: "Allied health professional" },
];
const CLINICIAN_STATUS_OPTIONS = [
  { value: "all", label: "All" },
  { value: "draft", label: "Draft" },
  { value: "invitation_not_sent", label: "Invitation not sent" },
  { value: "invitation_pending", label: "Invitation pending" },
  { value: "active", label: "Active" },
  { value: "declined", label: "Declined" },
  { value: "expired", label: "Expired" },
  { value: "revoked", label: "Revoked" },
  { value: "suspended", label: "Suspended" },
  { value: "removed", label: "Removed" },
];

const CLINICIAN_STATUS_MESSAGE_KEYS = {
  all: "all", draft: "draft", pending_invite: "pendingInvite", invited: "invited",
  onboarding: "onboarding", active: "active", suspended: "suspended", removed: "removed",
};

const CLINICIAN_EDITOR_MESSAGE_ALIASES = {
  add: "actions.add", edit: "actions.edit", cancel: "actions.cancel", save: "actions.save",
  clear: "actions.clear", apply: "actions.apply", copyLink: "actions.copyLink",
  openInNewTab: "actions.openInNewTab", blockClinician: "actions.blockClinician", delete: "actions.delete",
  firstName: "fields.firstName", lastName: "fields.lastName", email: "fields.email",
  phoneNumber: "fields.phoneNumber", countryCode: "fields.countryCode", gender: "fields.gender",
  dateOfBirth: "fields.dateOfBirth", yearsOfExperience: "fields.yearsOfExperience",
  registrationNumber: "fields.registrationNumber", registrationDate: "fields.registrationDate", registrationYear: "fields.registrationYear",
  registrationCountry: "fields.registrationCountry", assignedFacilities: "fields.assignedFacilities",
  facilitySelectionRequired: "facilitySelectionRequired",
};

function formatRole(role) {
  const raw = String(role || "").replaceAll("_", " ").trim();
  return raw ? raw.replace(/\b\w/g, (char) => char.toUpperCase()) : "Setup pending";
}

function institutionControlledLabel(t, value, fallback = "Setup pending") {
  const normalized = String(value || "").trim().toLowerCase().replace(/[\s-]+/g, "_");
  const labels = {
    owner: "Owner",
    super_admin: "Super Admin",
    active: "Active",
    draft: "Draft",
    setup_pending: "Setup pending",
    not_assigned: "Not assigned",
    hospital: "Hospital",
    member: "Member",
    clinician: "Clinician",
    facility_staff: "Facility staff",
    nurse: "Nurse",
    laboratory_officer: "Laboratory Officer",
    facility_surveillance_officer: "Facility Surveillance Officer",
    facility_supervisor: "Facility Supervisor",
  };
  return t(`institutionControlled.${normalized}`, labels[normalized] || formatRole(value || fallback));
}

function clinicianStatusLabel(t, status) {
  const normalized = String(status || "draft").toLowerCase();
  return t(`institutionClinicians.status.${CLINICIAN_STATUS_MESSAGE_KEYS[normalized] || normalized}`, formatRole(normalized));
}

function clinicianRelationshipStatus(clinician, invitation = null) {
  const rosterStatus = String(clinician?.status || "draft").toLowerCase();
  if (["active", "suspended", "removed"].includes(rosterStatus)) return rosterStatus;
  const invitationStatus = String(invitation?.status || "").toLowerCase();
  if (["pending", "declined", "expired", "revoked"].includes(invitationStatus)) return invitationStatus === "pending" ? "invitation_pending" : invitationStatus;
  return rosterStatus === "pending_invite" ? "invitation_not_sent" : rosterStatus;
}

function clinicianRelationshipStatusLabel(t, status) {
  const labels = {
    invitation_not_sent: "Invitation not sent",
    invitation_pending: "Invitation pending",
    active: "Active",
    declined: "Declined",
    expired: "Expired",
    revoked: "Revoked",
    suspended: "Suspended",
    removed: "Removed",
  };
  return t(`institutionClinicians.relationshipStatus.${status}`, labels[status] || clinicianStatusLabel(t, status));
}

function formatDate(value) {
  if (!value) return "Not available";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not available";
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(date);
}

function formatDateTime(value) {
  if (!value) return "Not available";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not available";
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(date);
}

function money(value, currency = "BWP") {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "Setup pending";
  return `${amount.toLocaleString()} ${currency || "BWP"}`;
}

function StatusPill({ children, tone = "neutral" }) {
  return <span className={`institutions-pill institutions-pill--${tone}`}>{children}</span>;
}

function truncateId(value) {
  const id = String(value || "").replaceAll("-", "").trim();
  return id ? id.slice(0, 7) : "Setup pending";
}

function getFacilityName(facility) {
  return facility?.facility_name || facility?.name || facility?.title || "Setup pending";
}

function getMemberName(member) {
  const raw = member?.full_name || member?.name || member?.display_name || member?.email || "";
  if (!isInternalProofValue(raw) && !isInternalProofValue(member?.email)) {
    return raw || "Institution member";
  }
  const role = String(getMemberProfession(member)).toLowerCase();
  if (role.includes("owner") || role.includes("admin")) return "Institution Administrator";
  if (role.includes("clinician") || role.includes("doctor")) return "Clinician";
  return "Institution member";
}

function getMemberEmail(member) {
  const email = member?.email || "";
  return isInternalProofValue(email) ? "Demo member account" : email || "No email recorded";
}

function getMemberProfession(member) {
  return member?.profession || member?.professional_role || member?.role || "Institution member";
}

function getMemberAvatar(member) {
  return member?.avatar_url || member?.profile_photo_url || member?.photo_url || member?.image_url || member?.profile_image_url || "";
}

function getMemberInitials(member) {
  return getMemberName(member).split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join("").toUpperCase() || "A";
}

function InstitutionMemberAvatar({ member, size = 44, className = "" }) {
  const source = getMemberAvatar(member);
  const isLarge = Number(size) > 44;
  const Avatar = isLarge ? AvatarThumb68 : AvatarThumb44;
  return (
    <span
      className={["institutions-member-avatar", isLarge ? "institutions-member-avatar--large" : "", className].filter(Boolean).join(" ")}
      aria-hidden="true"
    >
      <Avatar
        size={size}
        src={source}
        alt=""
        shape="circle"
        fallbackSrc=""
        visualTreatment="plain"
        imgClassName={source ? "" : "opacity-0"}
      />
      {!source ? <span className="institutions-member-avatar__initials">{getMemberInitials(member)}</span> : null}
    </span>
  );
}

function isInternalProofValue(value) {
  return /\b3b1b\b|rh3b1b-/i.test(String(value || ""));
}

function getProgrammeScope(assignments = [], t = null) {
  if (!assignments.length) return t ? t("institutionSurveillance.noProgrammeAssigned", "No programme assigned") : "No programme assigned";
  const names = Array.from(new Set(assignments.map((assignment) => assignment.programmeName).filter(Boolean)));
  return names.length ? names.join(", ") : (t ? t("institutionSurveillance.allProgrammes", "All institution programmes") : "All institution programmes");
}

function getAuthorityEventTitle(item, t) {
  const action = String(item?.action || "").toLowerCase();
  const setupPending = t("institutionControlled.setup_pending", "Setup pending");
  const labels = {
    member_invited: "Member invited",
    member_suspended: "Member suspended",
    member_removed: "Member removed",
    authority_assigned: "Facility authority assigned",
    authority_revoked: "Facility authority removed",
    evaluation_reviewer_invited: "Evaluation reviewer invited",
    evaluation_reviewer_accepted: "Evaluation reviewer accepted",
    dhis2_public_demo_checked: "DHIS2 public demo checked",
    institution_heatmap_snapshot_viewed: "Reporting map access checked",
    setup_pending: setupPending,
  };
  return t(`institutionSurveillance.historyEvents.${action || "setup_pending"}`, labels[action] || setupPending);
}

function getAuthorityEventDetail(item) {
  const member = getMemberName({
    full_name: item?.recipientName,
    email: item?.recipientEmail,
    role: item?.role,
  });
  const programme = item?.programId || item?.programmeName ? item?.programmeName || "Programme scope recorded" : "All institution programmes";
  const facility = item?.orgUnitName || "Facility scope pending";
  return [member, programme, facility].filter(Boolean).join(" · ");
}

function getAuthorityEventReason(item) {
  const raw = String(item?.reason || "").trim();
  if (!raw) return "";
  if (raw === "no_authorised_geography") return "No authorised reporting geography available";
  if (raw === "incomplete_facility_mapping") return "Facility reporting geography is incomplete";
  if (raw === "no_aggregate_data") return "No aggregate reporting data is available";
  if (raw === "all_cells_suppressed") return "All aggregate cells are disclosure-controlled";
  if (raw === "ready") return "Governed reporting map is available";
  if (!isInternalProofValue(raw)) return raw;
  if (/retained/i.test(raw)) return "Active facility authority retained";
  if (/revocation/i.test(raw)) return "Facility authority removed";
  if (/membership/i.test(raw)) return "Membership recorded";
  if (/facility authority/i.test(raw)) return "Facility authority assigned";
  return "Authority record updated";
}

function isHeatmapObservationEvent(item) {
  return String(item?.action || "").toLowerCase() === "institution_heatmap_snapshot_viewed";
}

function authorityObservationKey(item) {
  const observedDay = String(item?.createdAt || item?.created_at || "").slice(0, 10);
  return [
    item?.action,
    item?.actorUserId || item?.actor_user_id,
    item?.recipientUserId || item?.recipient_user_id,
    item?.assignmentId || item?.assignment_id,
    item?.programId || item?.program_id,
    item?.orgUnitId || item?.org_unit_id,
    item?.role,
    item?.reason,
    observedDay,
  ].map((value) => String(value || "")).join("|");
}

function groupAuthorityHistory(events = []) {
  const rows = [];
  const observationGroups = new Map();

  events.forEach((item) => {
    if (!isHeatmapObservationEvent(item)) {
      rows.push({ type: "event", item });
      return;
    }

    const key = authorityObservationKey(item);
    const existing = observationGroups.get(key);
    if (existing) {
      existing.items.push(item);
      return;
    }

    const group = { type: "observation-group", item, items: [item] };
    observationGroups.set(key, group);
    rows.push(group);
  });

  return rows;
}

function getSnapshotFacilityName(facility) {
  const raw = facility?.facilityName || facility?.facility_name || facility?.healthcareLocationName || facility?.name || "Facility";
  if (!isInternalProofValue(raw)) return raw;
  const code = String(facility?.orgUnitCode || facility?.org_unit_code || facility?.regionalHealthOrgUnitCode || "").toUpperCase();
  return code.includes("_B") ? "Demo Facility B" : "Demo Facility A";
}

function getSnapshotOrgUnitName(facility) {
  return facility?.orgUnitName || facility?.org_unit_name || facility?.regionalHealthOrgUnitName || "";
}

function getFacilityType(link) {
  const facility = link?.facility || link || {};
  return facility.type || facility.facility_type || facility.category || link?.facility_role || "Facility";
}

function getFacilityTypeIcon(type) {
  return MEDICAL_INSTITUTION_TYPE_OPTIONS.find((option) => option.value.toLowerCase() === String(type || "").toLowerCase())?.icon || ICONS.facilityHospital;
}

function getFacilityLogo(facility) {
  return facility?.logoUrl || facility?.logo_url || facility?.image_url || facility?.photo_url || facility?.thumbnail_url || "";
}

function getFacilityAddress(facility, notProvided = "Not provided") {
  return [facility?.address, facility?.city, facility?.country].filter(Boolean).join(", ") || notProvided;
}

function getFacilityDomain(facility, notProvided = "Not provided") {
  const raw = facility?.domain || facility?.website || facility?.url || "";
  if (!raw) return notProvided;
  try {
    return new URL(/^https?:\/\//i.test(String(raw)) ? raw : `https://${raw}`).hostname.replace(/^www\./i, "");
  } catch {
    return String(raw).replace(/^https?:\/\//i, "").replace(/^www\./i, "").replace(/[/#?].*$/, "").replace(/\/$/, "");
  }
}

function getFacilityDescription(facility, notProvided = "Not provided") {
  return facility?.description || facility?.about || facility?.summary || notProvided;
}

function getFacilityHours(facility, setupPending = "Setup pending") {
  const hours = facility?.openingHours || facility?.opening_hours || facility?.hours || facility?.business_hours;
  if (!hours) return { days: setupPending, time: "" };
  if (typeof hours === "string") return { days: hours, time: "" };
  const schedule = normaliseOpeningHours(hours);
  if (schedule.isOpen24Hours) return { days: "M,T,W,T,F,S,S", time: "24hrs" };
  const summary = formatOpeningHoursSummary(hours);
  return {
    days: summary.days === "S,M,T,W,T,F,S" ? "M,T,W,T,F,S,S" : summary.days,
    time: summary.time === "24h" ? "24hrs" : summary.time,
  };
}

function getFacilitySpecialties(facility) {
  const raw = facility?.specialties || facility?.specialities || facility?.services || facility?.tags;
  const list = Array.isArray(raw)
    ? raw.map((item) => item?.name || item)
    : typeof raw === "string"
      ? raw.split(",").map((item) => item.trim())
      : [];
  return list.filter(Boolean);
}

function SetupState({ title = "", children }) {
  const { t } = useLanguage();
  const resolvedTitle = title || t("institutionControlled.setup_pending", "Setup pending");
  return (
    <div className="institutions-empty" data-testid="institution-setup-pending">
      <span data-testid="institution-setup-pending-icon"><InstitutionIcon src={ICONS.surveillance} size={22} useMask /></span>
      <div>
        <strong>{resolvedTitle}</strong>
        <p>{children}</p>
      </div>
    </div>
  );
}

function InstitutionIcon({ src, fallback: Fallback, size = 20, className = "", useMask = false }) {
  if (src) {
    if (useMask) {
      return (
        <span
          aria-hidden="true"
          className={`institutions-mask-icon ${className}`.trim()}
          style={{ width: size, height: size, "--institutions-icon-mask": `url("${src}")` }}
        />
      );
    }
    return <img src={src} alt="" draggable="false" className={className || "institutions-img-icon"} style={{ width: size, height: size }} />;
  }
  return Fallback ? <Fallback size={size} aria-hidden="true" /> : null;
}

function MedicalInstitutionBackButton({ label = "Back", onClick }) {
  return (
    <button type="button" className="topbar-back" onClick={onClick} aria-label={label}>
      <InstitutionIcon src={ICONS.facilityChevronBack} size={20} useMask />
    </button>
  );
}

function useMobileSheetPresentation() {
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches);

  useEffect(() => {
    const media = window.matchMedia("(max-width: 767px)");
    const sync = () => setIsMobile(media.matches);
    sync();
    media.addEventListener("change", sync);
    return () => media.removeEventListener("change", sync);
  }, []);

  return isMobile;
}

function InstitutionSideSheet({ open, title, description, iconSrc, onClose, pending = false, children, footer, labelledBy, variant = "default", placement = "end", titleRow = "default", returnFocusRef }) {
  const sheetRef = useRef(null);
  const onCloseRef = useRef(onClose);
  const pendingRef = useRef(pending);
  const mobileSheetPresentation = useMobileSheetPresentation();

  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);
  useEffect(() => { pendingRef.current = pending; }, [pending]);

  const requestClose = React.useCallback(() => {
    if (pendingRef.current) return;
    onCloseRef.current?.();
    window.requestAnimationFrame(() => returnFocusRef?.current?.focus?.());
  }, [returnFocusRef]);

  useEffect(() => {
    if (!open || typeof document === "undefined") return undefined;
    const previousOverflow = document.body.style.overflow;
    const onKeyDown = (event) => {
      if (event.key === "Escape" && !event.target.closest?.(".afri-time-picker__panel") && !document.querySelector('[role="listbox"]')) requestClose();
      if (event.key !== "Tab" || !sheetRef.current) return;
      const focusable = Array.from(sheetRef.current.querySelectorAll('button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'));
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKeyDown);
    window.requestAnimationFrame(() => sheetRef.current?.querySelector('[data-side-sheet-initial-focus], input:not([disabled]):not([type="hidden"]), textarea:not([disabled]), select:not([disabled]), button:not(.institutions-side-sheet-close):not([disabled]), [tabindex]:not([tabindex="-1"])')?.focus?.());
    return () => { document.body.style.overflow = previousOverflow; document.removeEventListener("keydown", onKeyDown); };
  }, [open, requestClose]);

  if (!open || typeof document === "undefined") return null;

  const sheetContents = <>
    <header className="institutions-side-sheet-head">
      <div className={`institutions-side-sheet-title institutions-side-sheet-title--${titleRow}`}>
        {iconSrc ? <span className="institutions-side-sheet-leading-icon"><InstitutionIcon src={iconSrc} size={24} useMask /></span> : null}
        <div>
          <h2 id={labelledBy}>{title}</h2>
          {description ? <p>{description}</p> : null}
        </div>
      </div>
      <button type="button" className="institutions-side-sheet-close" aria-label="Close" onClick={requestClose} disabled={pending}>
        <X size={20} aria-hidden="true" />
      </button>
    </header>
    <div className="institutions-side-sheet-body">{children}</div>
  </>;

  if (mobileSheetPresentation) {
    return (
      <Sheet
        open
        onClose={requestClose}
        ariaLabelledBy={labelledBy}
        footer={footer ? <div className="institution-mobile-sheet-footer">{footer}</div> : null}
      >
        <div ref={sheetRef} className={`institution-mobile-side-sheet institutions-side-sheet--${variant}`}>
          {sheetContents}
        </div>
      </Sheet>
    );
  }

  return createPortal(
    <div
      className={`institutions-side-sheet-backdrop institutions-side-sheet-backdrop--${placement}`}
      role="presentation"
      onMouseDown={requestClose}
    >
      <section
        ref={sheetRef}
        className={`institutions-side-sheet institutions-side-sheet--${variant} institutions-side-sheet--placement-${placement}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        onMouseDown={(event) => event.stopPropagation()}
      >
        {sheetContents}
        <footer className="institutions-side-sheet-footer">{footer}</footer>
      </section>
    </div>,
    document.body,
  );
}

function InstitutionImageMediaSheet({ open, title, available = true, hasCustomImage, pending = false, unavailableMessage, onClose, onUpload, onTakePhoto, onRemove }) {
  const { t } = useLanguage();
  const c = (key, fallback, values) => t(`institutionMedicalInstitutionDetail.media.${key}`, fallback, values);
  return <Sheet open={open} onClose={onClose}>
    <div className="mx-auto w-full max-w-[412px] relative z-[60] pointer-events-auto" aria-labelledby="institution-image-media-sheet-title">
      <div className="pt-6 pb-6">
        <p id="institution-image-media-sheet-title" className="text-title-large text-on-surface mx-4">{title}</p>
      </div>
      {available ? <div className="rounded-[16px] overflow-hidden bg-[rgb(var(--clr-surface-cont-highest))]" role="menu" aria-label={c("actions", "{title} actions", { title })}>
      <button type="button" role="menuitem" onClick={onUpload} disabled={pending} className="w-full flex items-center gap-3 px-2 py-3 disabled:opacity-60">
        <img src={ICONS.coverUpload} alt="" className="h-5 w-5 ml-2" />
        <span className="flex-1 text-left text-body-large text-on-surface">{c("uploadImage", "Upload image")}</span>
        <span aria-hidden="true" className="mr-2 text-body-medium text-on-surface-var">›</span>
      </button>
      <div aria-hidden="true" className="h-px bg-[rgba(24,50,119,0.10)] ml-4 mr-3" />
      <button type="button" role="menuitem" onClick={onTakePhoto} disabled={pending} className="w-full flex items-center gap-3 px-2 py-3 disabled:opacity-60">
        <img src={ICONS.coverTakePhoto} alt="" className="h-5 w-5 ml-2" />
        <span className="flex-1 text-left text-body-large text-on-surface">{c("takePhoto", "Take photo")}</span>
        <span aria-hidden="true" className="mr-2 text-body-medium text-on-surface-var">›</span>
      </button>
      {hasCustomImage ? <><div aria-hidden="true" className="h-px bg-[rgba(24,50,119,0.10)] ml-4 mr-3" />
        <button type="button" role="menuitem" onClick={onRemove} disabled={pending} className="w-full flex items-center gap-3 px-2 py-3 disabled:opacity-60">
          <img src={ICONS.coverDelete} alt="" className="h-5 w-5 ml-2" />
          <span className="flex-1 text-left text-body-large text-[rgb(var(--schemes-critical))]">{c("removeImage", "Remove image")}</span>
          <span aria-hidden="true" className="mr-2 text-body-medium text-on-surface-var">›</span>
        </button>
      </> : null}
      <div aria-hidden="true" className="h-px bg-[rgba(24,50,119,0.06)]" />
      </div> : <div className="rounded-[16px] overflow-hidden bg-[rgb(var(--clr-surface-cont-highest))]"><p className="px-4 py-3 text-body-large text-on-surface-variant" role="status">{unavailableMessage}</p><div aria-hidden="true" className="h-px bg-[rgba(24,50,119,0.06)]" /></div>}
    </div>
  </Sheet>;
}

function InstitutionEvidenceSourceDialog({ open, onClose, onPickFiles, onTakePhoto, onRemove, hasDocument, error, title, t }) {
  const dialogRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => { if (event.key === "Escape") { event.preventDefault(); onClose?.(); } };
    document.addEventListener("keydown", onKeyDown);
    window.requestAnimationFrame(() => dialogRef.current?.querySelector("button:not([disabled])")?.focus?.());
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!open || typeof document === "undefined") return null;
  return createPortal(
    <div className="fixed inset-0 z-[260] flex items-end sm:items-center justify-center" role="presentation">
      <button type="button" aria-label={t("common.close", "Close")} onClick={onClose} className="absolute inset-0 bg-black/40" />
      <section ref={dialogRef} role="dialog" aria-modal="true" aria-label={title} className="relative w-full sm:w-[420px] max-w-[92vw] rounded-t-3xl sm:rounded-2xl bg-[rgb(var(--clr-surface-cont-highest))] shadow-[0_-12px_32px_rgba(16,24,40,0.16)] sm:shadow-[0_16px_48px_rgba(16,24,40,0.22)] p-5 pt-4">
        <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-[rgba(255,255,255,0.16)]" aria-hidden="true" />
        <h3 className="text-title-large text-on-surface mb-2 text-center">{title}</h3>
        <RegistrationDocumentPickerContent hasFiles={hasDocument} t={t} onPickFiles={onPickFiles} onTakePhoto={onTakePhoto} onRemove={onRemove} />
        {error ? <p className="mt-3 text-body-small text-[rgb(var(--clr-error))]" role="alert">{error}</p> : null}
      </section>
    </div>,
    document.body,
  );
}

function MetricCard({ label, value, setup, icon: Icon, iconSrc, requiresSetup = false }) {
  const hasValue = value !== null && value !== undefined;
  return (
    <article className="institutions-metric">
      <div className="institutions-metric-top">
        <span className="institutions-metric-label">{label}</span>
        {Icon || iconSrc ? (
          <span className="institutions-icon-tile">
            <InstitutionIcon src={iconSrc} fallback={Icon} size={24} />
          </span>
        ) : null}
      </div>
      <strong className={requiresSetup || !hasValue ? "institutions-metric-setup" : ""}>{hasValue ? value : "Setup pending"}</strong>
      <p>{setup}</p>
    </article>
  );
}

const DASHBOARD_WEEKDAYS = ["S", "M", "T", "W", "T", "F", "S"];

function formatDashboardMonth(date) {
  return new Intl.DateTimeFormat(undefined, { month: "long", year: "numeric" }).format(date);
}

function monthKey(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

function buildMonthOptions(anchorDate) {
  const options = [];
  for (let offset = -6; offset <= 5; offset += 1) {
    options.push(new Date(anchorDate.getFullYear(), anchorDate.getMonth() + offset, 1));
  }
  return options;
}

function formatShortDashboardDate(value, timeZone) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", year: "numeric", ...(timeZone ? { timeZone } : {}) }).format(date);
}

function getAppointmentPatientName(row) {
  return formatInstitutionPatientAddress({
    title: row?.patient_title,
    lastName: row?.patient_last_name,
    fullName: row?.patient_name || row?.patientName || row?.patient?.full_name || row?.patient?.name,
  });
}

function getAppointmentDoctorName(row) {
  return row?.doctor_name || row?.doctorName || row?.doctor?.full_name || row?.doctor?.name || "Doctor";
}

function getDoctorDisplayName(link) {
  const doctor = link?.doctor || {};
  const profileName = doctor.full_name || doctor.name || doctor.display_name;
  const partsName = [doctor.first_name, doctor.last_name].filter(Boolean).join(" ");
  return profileName || partsName || link?.name || "Clinician";
}

function getDoctorInitials(name) {
  const words = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!words.length) return "DR";
  return words.slice(0, 2).map((word) => word[0]).join("").toUpperCase();
}

function getDoctorIdentifier(link) {
  const doctor = link?.doctor || {};
  return doctor.registration_number || doctor.registry_number || doctor.license_number || doctor.id || link?.doctor_profile_id || link?.id;
}

function getDoctorEmail(link) {
  const doctor = link?.doctor || {};
  return doctor.email || doctor.contact_email || link?.email || "Not available";
}

function getDoctorPhone(link) {
  const doctor = link?.doctor || {};
  return doctor.phone || doctor.phone_number || doctor.mobile || link?.phone || "Not available";
}

function getDoctorSpecialty(link) {
  const doctor = link?.doctor || {};
  return doctor.specialty || doctor.speciality || doctor.specialization || doctor.primary_specialty || "Setup pending";
}

function getDoctorPhoto(link) {
  const doctor = link?.doctor || {};
  return doctor.avatar_url || doctor.photo_url || doctor.profile_photo_url || doctor.image_url || "";
}

function getDoctorStatus(link) {
  return link?.status || link?.affiliation_status || "active";
}

function getServiceName(service) {
  return service?.name || service?.service_name || service?.title || "Setup pending";
}

function getServiceSpecialty(service) {
  return service?.specialty || service?.speciality || service?.category || service?.service_type || "Setup pending";
}

function getServiceNotes(service) {
  return service?.description || service?.notes || service?.summary || "Data appears once activity is available";
}

function getServiceActiveLabel(service) {
  const active = service?.is_active;
  if (active === false || String(active).toLowerCase() === "false") return "Not Active";
  return "Active";
}

function getPatientKey(row) {
  return row?.patient_id || row?.patient?.id || row?.patient_email || row?.patient?.email || getAppointmentPatientName(row);
}

function buildInstitutionPatients(rows = []) {
  const patients = new Map();
  rows.forEach((row) => {
    const key = getPatientKey(row);
    if (!key) return;
    const existing = patients.get(key) || {
      id: key,
      name: getAppointmentPatientName(row),
      programme: row?.programme || row?.coverage_programme || row?.insurance_provider || "Coverage pending",
      lastContact: row?.last_visit_at || row?.appointment_date || row?.scheduled_at || row?.created_at,
      careTeam: getAppointmentDoctorName(row),
      status: row?.status || "active",
    };
    const nextDate = row?.last_visit_at || row?.appointment_date || row?.scheduled_at || row?.created_at;
    if (nextDate && (!existing.lastContact || new Date(nextDate) > new Date(existing.lastContact))) {
      existing.lastContact = nextDate;
      existing.careTeam = getAppointmentDoctorName(row);
    }
    patients.set(key, existing);
  });
  return Array.from(patients.values());
}

function buildDashboardCalendarCells(monthDate) {
  const y = monthDate.getFullYear();
  const m = monthDate.getMonth();
  const firstWeekday = new Date(y, m, 1).getDay();
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const cells = [];

  for (let index = 0; index < firstWeekday; index += 1) {
    cells.push({ key: `empty-${index}`, day: null });
  }

  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push({ key: `day-${day}`, day });
  }

  const minCells = cells.length > 35 ? 42 : 35;
  while (cells.length < minCells) {
    cells.push({ key: `empty-tail-${cells.length}`, day: null });
  }

  return cells;
}

function DashboardMetricCard({ iconSrc, iconMask = false, label, value, footerLabel, footerPct }) {
  return (
    <article className="institutions-dashboard-metric">
      <div className="institutions-dashboard-metric-label text-title-medium">
        <span className="institutions-dashboard-metric-icon">
          <InstitutionIcon src={iconSrc} size={20} useMask={iconMask} />
        </span>
        <span>{label}</span>
      </div>
      <strong>{value}</strong>
      {footerLabel ? (
        <div className="institutions-dashboard-metric-footer">
          <span className="text-label-medium">{footerLabel}</span>
          {footerPct ? (
            <span className="institutions-trend-badge">
              <InstitutionIcon src={ICONS.badgeUp} size={16} />
              {footerPct}
            </span>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}

function DashboardSegmented({ active = "Today", onChange, t }) {
  return (
    <div className="institutions-dashboard-segmented" role="tablist" aria-label={t("institutionDashboard.timeRange", "Time range")}>
      {["Today", "Week", "Month", "Year"].map((item, index, items) => {
        const isActive = item === active;
        const activeIndex = items.indexOf(active);
        const showDivider = index !== items.length - 1 && index !== activeIndex && index + 1 !== activeIndex;
        return (
          <React.Fragment key={item}>
            <button type="button" role="tab" aria-selected={isActive} className={isActive ? "is-active" : ""} onClick={() => onChange?.(item)}>
              {t(`institutionDashboard.ranges.${item.toLowerCase()}`, item)}
            </button>
            {showDivider ? <i aria-hidden="true" /> : null}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function DashboardCalendar({ month, snapshot, loading, onMonthChange, onPrev, onNext, t, formatDate }) {
  const cells = useMemo(() => buildDashboardCalendarCells(month), [month]);
  const monthOptions = useMemo(() => buildMonthOptions(month), [month]);
  const [monthMenuOpen, setMonthMenuOpen] = useState(false);
  const now = new Date();
  const available = snapshot?.available === true && Array.isArray(snapshot?.rows);
  const dayCounts = useMemo(() => {
    return available
      ? institutionAppointmentCalendarCounts(snapshot.rows, snapshot.timezone, month.getFullYear(), month.getMonth() + 1)
      : new Map();
  }, [available, snapshot, month]);
  return (
    <article className="institutions-dashboard-calendar">
      <div className="institutions-dashboard-card-title">{t("institutionDashboard.appointmentCalendar", "Institution appointments")}</div>
      <div className="institutions-dashboard-calendar-controls">
        <div className="institutions-dropdown-anchor">
          <button
            type="button"
            className="institutions-dashboard-month-button"
            aria-haspopup="menu"
            aria-expanded={monthMenuOpen}
            onClick={() => setMonthMenuOpen((open) => !open)}
          >
            <span>{formatDate(month, { month: "long", year: "numeric" })}</span>
            <InstitutionIcon src={ICONS.expand} size={18} />
          </button>
          {monthMenuOpen ? (
            <div className="institutions-dashboard-menu institutions-dashboard-menu--left" role="menu" aria-label={t("institutionDashboard.selectMonth", "Select month")}>
              {monthOptions.map((option) => {
                const selected = monthKey(option) === monthKey(month);
                return (
                  <button
                    key={monthKey(option)}
                    type="button"
                    role="menuitemradio"
                    aria-checked={selected}
                    className={selected ? "is-active" : ""}
                    onClick={() => {
                      onMonthChange(option);
                      setMonthMenuOpen(false);
                    }}
                  >
                    {formatDate(option, { month: "long", year: "numeric" })}
                  </button>
                );
              })}
            </div>
          ) : null}
        </div>
        <div className="institutions-dashboard-calendar-nav">
          <button type="button" aria-label={t("institutionDashboard.previousMonth", "Previous month")} onClick={onPrev}>
            <InstitutionIcon src={ICONS.navigateBefore} size={24} />
          </button>
          <button type="button" aria-label={t("institutionDashboard.nextMonth", "Next month")} onClick={onNext}>
            <InstitutionIcon src={ICONS.navigateNext} size={24} />
          </button>
        </div>
      </div>
      <p className="text-body-small text-on-surface-variant" role="status">
        {loading ? t("institutionDashboard.appointmentLoading", "Loading scoped appointments…") : available
          ? `${snapshot.count} ${t("institutionDashboard.calendarLinkedAppointments", "linked appointments in this month")} · ${snapshot.timezone}`
          : t("institutionDashboard.calendarUnavailable", "Calendar unavailable until an authorised Institution appointment source is connected.")}
      </p>
      <div className="institutions-dashboard-calendar-weekdays" aria-hidden="true">
        {(String(t("institutionDashboard.weekdays", "S,M,T,W,T,F,S"))).split(",").map((day, index) => <span key={`${day}-${index}`}>{day}</span>)}
      </div>
      <div className="institutions-dashboard-calendar-grid">
        {cells.map((cell) => {
          if (!cell.day) return <span key={cell.key} aria-hidden="true" />;
          const isToday =
            month.getFullYear() === now.getFullYear() &&
            month.getMonth() === now.getMonth() &&
            cell.day === now.getDate();
          const count = available ? (dayCounts.get(cell.day) || 0) : null;
          return (
            <span key={cell.key} className={isToday ? "is-today" : ""} title={count === null ? t("institutionDashboard.unavailable", "Unavailable") : `${count} ${t("institutionDashboard.legend.appointment", "Appointment")}`}>
              <b>{cell.day}</b>
              {count > 0 ? <i className="institution-appointment-calendar-marker" aria-hidden="true" /> : null}
            </span>
          );
        })}
      </div>
      {available ? <div className="institutions-dashboard-legend"><span><InstitutionIcon src={ICONS.radioAppointment} size={18} />{t("institutionDashboard.legend.appointment", "Appointment")}</span></div> : null}
    </article>
  );
}

function RiskLevelBadge({ level = "not_recorded" }) {
  const { t } = useLanguage();
  const normalized = String(level || "not_recorded").toLowerCase();
  if (normalized === "not_recorded") return <span className="text-body-small text-on-surface-variant">{t("institutionDashboard.notRecorded", "Not recorded")}</span>;
  const label = normalized.replace(/\b\w/g, (char) => char.toUpperCase());
  return (
    <span className={`institutions-risk-badge institutions-risk-badge--${normalized}`}>
      <InstitutionIcon src={RISK_LEVEL_ICON[normalized] || RISK_LEVEL_ICON.normal} size={16} />
      {label}
    </span>
  );
}

function AppointmentDoctorCell({ row }) {
  return (
    <span className="institutions-doctor-cell">
      <span aria-hidden="true">{String(getAppointmentDoctorName(row)).charAt(0).toUpperCase() || "D"}</span>
      <small>{getAppointmentDoctorName(row)}</small>
    </span>
  );
}

function AppointmentTableRow({ row, timezone }) {
  const { t } = useLanguage();
  const label = (key, fallback) => t(`institutionDashboard.${key}`, fallback);
  const patientName = getAppointmentPatientName(row);
  const age = row?.patient_age || row?.age || row?.patient?.age;
  const condition = row?.primary_condition || row?.condition || row?.diagnosis || label("noDiagnosis", "No diagnosis recorded");
  const risk = row?.risk_level || row?.riskLevel || "not_recorded";
  const insurance = row?.insurance_provider || row?.insurance || row?.patient?.insurance_provider || label("notProvided", "Not provided");
  const insurancePlan = row?.insurance_plan || row?.insurancePlan || row?.patient?.insurance_plan || "";
  const lastVisit = row?.last_visit_at || null;

  return (
    <><div className="institutions-dashboard-table-row institutions-dashboard-table-row--body institutions-dashboard-appointment-desktop">
      <span>
        <strong>{patientName}{row?.synthetic ? ` · ${label("synthetic", "Synthetic")}` : ""}</strong>
        {Number.isFinite(age) ? <small>{label("ageYears", "{count} years old").replace("{count}", age)}</small> : null}
      </span>
      <span>{condition}</span>
      <span><RiskLevelBadge level={risk} /></span>
      <span>
        <strong>{insurance}</strong>
        {insurancePlan ? <small>{insurancePlan}</small> : null}
      </span>
      <span>{lastVisit ? formatShortDashboardDate(lastVisit, timezone) : label("noPriorVisit", "No prior visit")}</span>
      <span><AppointmentDoctorCell row={row} /></span>
      <span />
    </div><article className="institutions-dashboard-appointment-mobile" data-testid="institution-appointment-mobile-row"><div className="flex items-start justify-between gap-3"><div className="min-w-0"><strong className="text-title-small">{patientName}</strong><p className="text-body-small text-on-surface-variant">{Number.isFinite(age) ? `${label("ageYears", "{count} years old").replace("{count}", age)} · ` : ""}{row?.synthetic ? `${label("synthetic", "Synthetic")} · ` : ""}{formatShortDashboardDate(row?.appointment_date, timezone)}</p></div><span className="text-label-small text-on-surface-variant">{row?.status}</span></div><div className="grid grid-cols-2 gap-x-3 gap-y-2 pt-3 text-body-small"><div><small>{label("columns.primaryCondition", "Primary condition")}</small><p>{condition}</p></div><div><small>{label("columns.riskLevel", "Risk level")}</small><p><RiskLevelBadge level={risk} /></p></div><div><small>{label("columns.insurance", "Insurance")}</small><p>{insurance}</p>{insurancePlan ? <small>{insurancePlan}</small> : null}</div><div><small>{label("columns.lastVisit", "Last visit")}</small><p>{lastVisit ? formatShortDashboardDate(lastVisit, timezone) : label("noPriorVisit", "No prior visit")}</p></div><div className="col-span-2"><small>{label("columns.doctors", "Clinicians")}</small><p>{getAppointmentDoctorName(row)}</p></div></div></article></>
  );
}

const INSTITUTION_THEME_OPTIONS = [
  { value: "dark", label: "Dark", labelKey: "theme.dark", iconSrc: ICONS.themeDark },
  { value: "light", label: "Light", labelKey: "theme.light", iconSrc: ICONS.themeLight },
  { value: "system", label: "System", labelKey: "theme.system", iconSrc: ICONS.themeSystem },
];

function InstitutionThemeOption({ active, label, iconSrc, onClick }) {
  return (
    <button
      type="button"
      role="menuitemradio"
      aria-checked={active}
      onClick={onClick}
      className={`institutions-theme-option ${active ? "is-active" : ""}`}
    >
      <InstitutionIcon src={iconSrc} size={18} useMask />
      <span>{label}</span>
      <span className="institutions-theme-option-indicator" aria-hidden="true" />
    </button>
  );
}

function InstitutionLanguageOption({ active, label, onClick }) {
  return (
    <button
      type="button"
      role="menuitemradio"
      aria-checked={active}
      onClick={onClick}
      className={`institutions-theme-option ${active ? "is-active" : ""}`}
    >
      <InstitutionIcon src={ICONS.language} size={18} useMask />
      <span>{label}</span>
      <span className="institutions-theme-option-indicator" aria-hidden="true" />
    </button>
  );
}

function PageShell({ workspace, children, title, icon: Icon, iconSrc, iconMask = false, dashboardHeader = false, dashboardHeaderTitle = "", institutionContextHeader = true, onInstitutionSelect, clinicianSearch = false, pageTopBarTitle = "", pageTopBarSubtitle = "", pageTopBarActions = null, pageTopBarCentered, pageTopBarNavigation = INSTITUTION_TOP_BAR_NAVIGATION.NONE, pageTopBarBackTo = "", pageTopBarBackLabel = "", profileDetail = false, contentClassName = "afrimed-rail afrimed-rail--comfortable pt-4 pb-24" }) {
  const institution = workspace?.activeInstitution;
  const auth = useAuth() || {};
  const { theme, setTheme } = useTheme();
  const { languageCode, setLanguage, t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [institutionMenuOpen, setInstitutionMenuOpen] = useState(false);
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [logoutPending, setLogoutPending] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [mobileSearch, setMobileSearch] = useState("");
  const [facilitySearchOpen, setFacilitySearchOpen] = useState(false);
  const [clinicianSearchOpen, setClinicianSearchOpen] = useState(false);
  const [facilitySearchNetwork, setFacilitySearchNetwork] = useState(null);
  const [facilitySearchError, setFacilitySearchError] = useState("");
  const [sharedProfileAvatar, setSharedProfileAvatar] = useState("");
  const meMenuRef = useRef(null);
  const institutionSwitcherRef = useRef(null);
  const mobileMenuTriggerRef = useRef(null);
  const facilitySearchTriggerRef = useRef(null);
  const headerAvatarName = String(
    auth?.profile?.full_name ||
      auth?.profile?.display_name ||
      auth?.user?.user_metadata?.full_name ||
      auth?.user?.user_metadata?.name ||
      auth?.user?.email ||
      "A",
  ).trim();
  const userInitial = headerAvatarName.charAt(0).toUpperCase() || "A";
  const headerAvatarUrl = String(
    auth?.profile?.avatar_url ||
      auth?.profile?.profile_image_url ||
      sharedProfileAvatar ||
      auth?.user?.user_metadata?.avatar_url ||
      "",
  ).trim();
  const activeInstitutionName = institutionDisplayName(institution);
  const useInstitutionContextHeader = dashboardHeader || Boolean(institutionContextHeader && institution?.id);
  const headerTitle = useInstitutionContextHeader
    ? dashboardHeaderTitle || activeInstitutionName
    : title;
  const institutionOptions = workspace?.institutions || [];
  // `workspace.institutions` is already the authenticated user's authorised
  // parent-Institution set. It is intentionally not a Regional Health switcher.
  const showInstitutionSwitcher = useInstitutionContextHeader && typeof onInstitutionSelect === "function";
  const canSwitchInstitution = showInstitutionSwitcher && institutionOptions.length > 1;
  const regionalHealthRoles = [workspace?.scopedRole, ...(workspace?.userRoles || [])]
    .map((role) => String(role || "").trim().toLowerCase());
  const canUseRegionalHealth = regionalHealthRoles.some((role) => ["super_admin", "admin", "owner", "moh_admin", "institution_admin", "facility_admin", "institution_evaluation_reviewer"].includes(role));
  const canManageInstitutions = regionalHealthRoles.some((role) => ["super_admin", "admin", "owner", "moh_admin", "institution_admin", "facility_admin"].includes(role));
  const facilitySearchFacilities = Array.isArray(facilitySearchNetwork?.facilities) ? facilitySearchNetwork.facilities : [];
  const clinicianSearchRoster = Array.isArray(workspace?.linkedClinicians) ? workspace.linkedClinicians : [];
  // The saved-facility Member surface uses active parent workspace members as
  // accountable creators, then facility memberships where present. Rebuild from
  // those exact authorised collections whenever either async source hydrates.
  const facilitySearchMembers = useMemo(() => buildMedicalInstitutionMemberSearchIndex({
    facilities: facilitySearchFacilities,
    parentMembers: workspace?.members || [],
    institutionId: institution?.id,
  }), [facilitySearchFacilities, institution?.id, workspace?.members]);
  const facilitySearchResolved = Array.isArray(facilitySearchNetwork?.facilities);
  // Search remains usable while its authorised facility index is loading. Once
  // resolved, suppress a directory search only when the caller genuinely has
  // no available facilities.
  const facilitySearchAvailable = canManageInstitutions && (!facilitySearchResolved || facilitySearchFacilities.length > 0);
  const showFacilitySearch = facilitySearchAvailable;
  // The header remains available for an authorised empty roster. It opens a
  // query-gated surface rather than preloading a directory.
  const clinicianSearchAvailable = canManageInstitutions;
  const showContextSearch = clinicianSearch ? clinicianSearchAvailable : showFacilitySearch;
  const canConfigureInstitution = canAccessInstitutionConfiguration(workspace);
  const visibleMenuItems = MENU_ITEMS.filter((item) =>
    (!item.requiresRegionalHealth || canUseRegionalHealth) &&
    (item.to !== "/institutions/settings" || canConfigureInstitution)
  );
  const visibleHeaderTabs = HEADER_TABS.filter((tab) => !tab.requiresRegionalHealth || canUseRegionalHealth);
  const institutionShellLabel = (key, fallback) => t(`institutionShell.${key}`, fallback);
  const facilitySearchErrorLabel = facilitySearchError === "load_failed"
    ? institutionShellLabel("facilitySearchError", "Medical Institutions could not be loaded. Try again.")
    : facilitySearchError;
  const localizedInstitutionLabel = (itemOrLabel) => {
    const label = typeof itemOrLabel === "string" ? itemOrLabel : itemOrLabel?.label;
    const labelKey = typeof itemOrLabel === "string"
      ? INSTITUTION_SHELL_LABEL_KEYS[label]
      : itemOrLabel?.labelKey;
    return labelKey ? institutionShellLabel(labelKey) : label;
  };
  const localizeThemeOption = (option) => institutionShellLabel(option.labelKey);
  useEffect(() => setMenuOpen(false), [location.pathname]);
  useEffect(() => setMobileDrawerOpen(false), [location.pathname]);
  useEffect(() => setInstitutionMenuOpen(false), [location.pathname, location.search]);
  useEffect(() => {
    if (clinicianSearch || !institution?.id || !canManageInstitutions) {
      setFacilitySearchNetwork(null);
      setFacilitySearchError("");
      return undefined;
    }
    // The header and mobile search are query-gated. Do not load the entire
    // authorised facility directory while Home is simply hydrating.
    if (!facilitySearchOpen && !mobileSearch.trim()) return undefined;
    if (facilitySearchNetwork) return undefined;
    let active = true;
    setFacilitySearchError("");
    loadMedicalInstitutionNetwork({ institutionId: institution.id })
      .then((value) => { if (active) setFacilitySearchNetwork(value); })
      .catch(() => { if (active) setFacilitySearchError("load_failed"); });
    return () => { active = false; };
  }, [canManageInstitutions, clinicianSearch, facilitySearchNetwork, facilitySearchOpen, institution?.id, mobileSearch]);
  useEffect(() => {
    if (!mobileSearch.trim() || facilitySearchOpen || clinicianSearchOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setMobileSearch("");
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [clinicianSearchOpen, facilitySearchOpen, mobileSearch]);
  useEffect(() => {
    if (facilitySearchResolved && !facilitySearchAvailable) setMobileSearch("");
  }, [facilitySearchAvailable, facilitySearchResolved]);
  useEffect(() => {
    let alive = true;
    const userId = String(auth?.user?.id || "").trim();
    if (!userId || auth?.profile?.avatar_url || auth?.profile?.profile_image_url) {
      setSharedProfileAvatar("");
      return () => {
        alive = false;
      };
    }

    supabase
      .from("profiles")
      .select("avatar_url")
      .eq("user_id", userId)
      .maybeSingle()
      .then(({ data, error }) => {
        if (!alive || error) return;
        setSharedProfileAvatar(String(data?.avatar_url || "").trim());
      })
      .catch(() => {
        // Initials remain the calm fallback if the shared profile cannot be read.
      });

    return () => {
      alive = false;
    };
  }, [auth?.profile?.avatar_url, auth?.profile?.profile_image_url, auth?.user?.id]);
  const closeMobileDrawer = React.useCallback(() => {
    setMobileDrawerOpen(false);
    window.requestAnimationFrame(() => mobileMenuTriggerRef.current?.focus?.());
  }, []);

  useEffect(() => {
    if (!mobileDrawerOpen) return undefined;
    const onKey = (event) => event.key === "Escape" && closeMobileDrawer();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = previousOverflow; document.removeEventListener("keydown", onKey); };
  }, [closeMobileDrawer, mobileDrawerOpen]);
  useEffect(() => {
    if (!menuOpen) return undefined;
    const closeOnOutsidePointer = (event) => {
      if (!meMenuRef.current?.contains(event.target)) setMenuOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("mousedown", closeOnOutsidePointer);
    document.addEventListener("touchstart", closeOnOutsidePointer, { passive: true });
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsidePointer);
      document.removeEventListener("touchstart", closeOnOutsidePointer);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [menuOpen]);
  useEffect(() => {
    if (!institutionMenuOpen) return undefined;
    const closeOnOutsidePointer = (event) => {
      if (!institutionSwitcherRef.current?.contains(event.target)) setInstitutionMenuOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setInstitutionMenuOpen(false);
    };
    document.addEventListener("mousedown", closeOnOutsidePointer);
    document.addEventListener("touchstart", closeOnOutsidePointer, { passive: true });
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsidePointer);
      document.removeEventListener("touchstart", closeOnOutsidePointer);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [institutionMenuOpen]);
  const institutionRoute = (to) => {
    if (!String(to || "").startsWith("/institutions/") || !institution?.id) return to;
    const [pathname, search = ""] = String(to).split("?");
    const params = new URLSearchParams(search);
    params.set("institution", institution.id);
    return `${pathname}?${params.toString()}`;
  };
  const goToMenuItem = (to) => {
    setMenuOpen(false);
    navigate(institutionRoute(to));
  };
  const openFacilitySearch = (event) => {
    event?.preventDefault();
    event?.stopPropagation();
    if (!facilitySearchAvailable) return;
    setFacilitySearchOpen(true);
  };
  const closeFacilitySearch = () => {
    setFacilitySearchOpen(false);
    window.requestAnimationFrame(() => facilitySearchTriggerRef.current?.focus());
  };
  const openClinicianSearch = (event) => {
    event?.preventDefault();
    event?.stopPropagation();
    if (!clinicianSearchAvailable) return;
    setClinicianSearchOpen(true);
  };
  const closeClinicianSearch = () => {
    setClinicianSearchOpen(false);
    window.requestAnimationFrame(() => facilitySearchTriggerRef.current?.focus());
  };
  const openClinicianSearchResult = (clinician) => {
    if (!clinician?.id || !institution?.id) return;
    setClinicianSearchOpen(false);
    setMobileSearch("");
    navigate(clinicianPath(institution.id, clinician.id));
  };
  const openFacilitySearchResult = (facility) => {
    if (!facility?.id || !institution?.id) return;
    recordInstitutionHomeFacilityView(auth?.user?.id, { parentInstitutionId: institution.id, facilityId: facility.id });
    setFacilitySearchOpen(false);
    setMobileSearch("");
    navigate(institutionPath(institution.id, `/${facility.id}`));
  };
  const openFacilityMemberSearchResult = (member) => {
    if (!member?.facilityId || !institution?.id) return;
    recordInstitutionHomeMemberView(auth?.user?.id, {
      parentInstitutionId: institution.id,
      facilityId: member.facilityId,
      membershipId: member.membershipId,
      userId: member.userId,
    });
    setFacilitySearchOpen(false);
    setMobileSearch("");
    navigate(`${institutionPath(institution.id, `/${member.facilityId}`)}#medical-institution-members`);
  };
  const goToInstitutionFeedback = () => {
    setMenuOpen(false);
    const returnTo = `${location.pathname}${location.search}`;
    const params = new URLSearchParams({
      from: returnTo,
      returnTo,
      role: "institution",
      section: location.pathname.includes("/regional-health") ? "regional-health" : "institution",
    });
    navigate(institutionRoute(`/institutions/feedback?${params.toString()}`));
  };
  const goHome = () => {
    setMobileDrawerOpen(false);
    navigate(institutionRoute("/institutions/home"));
  };
  const chooseInstitution = (nextInstitution) => {
    setInstitutionMenuOpen(false);
    if (nextInstitution?.id && nextInstitution.id !== institution?.id) {
      if (typeof onInstitutionSelect === "function") {
        onInstitutionSelect(nextInstitution.id);
        return;
      }
      const params = new URLSearchParams(location.search);
      params.set("institution", nextInstitution.id);
      navigate({ pathname: location.pathname, search: `?${params.toString()}` });
    }
  };
  const handleSignOut = async () => {
    setMenuOpen(false);
    if (mobileDrawerOpen) {
      closeMobileDrawer();
      window.requestAnimationFrame(() => setLogoutOpen(true));
      return;
    }
    setLogoutOpen(true);
  };
  const handleInstitutionThemeSelect = React.useCallback(
    (nextTheme) => {
      setTheme(nextTheme);
      persistThemeForUser(auth?.user?.id, nextTheme);
    },
    [auth?.user?.id, setTheme],
  );
  const confirmSignOut = async () => {
    if (logoutPending) return;
    try {
      setLogoutPending(true);
      await logout({ redirectTo: "/login?logout=1" });
    } catch (error) {
      console.error("Institution logout failed:", error);
      window.location.href = "/login?logout=1";
    } finally {
      setLogoutPending(false);
    }
  };
  const renderAccountAvatar = () =>
    headerAvatarUrl ? (
      <AvatarThumb44
        src={headerAvatarUrl}
        alt=""
        size={28}
        shape="circle"
        role="doctor"
        className="institutions-avatar-photo"
      />
    ) : (
      <span className="institutions-avatar" aria-hidden="true">{userInitial}</span>
    );

  return (
    <div className={`institutions-shell${profileDetail ? " profile-page-shell institutions-shell--profile-detail" : ""}`}>
      <main className="institutions-main">
        <header className="institutions-topbar">
          <div className="institutions-topbar-inner afrimed-rail afrimed-rail--comfortable">
            <button ref={mobileMenuTriggerRef} type="button" className="institutions-mobile-menu-trigger" aria-label={institutionShellLabel("accessibility.openNavigation")} onClick={() => setMobileDrawerOpen(true)}><Menu size={28} /></button>
            <button type="button" className="institutions-mobile-home-brand" aria-label={t("institutionHome.title", "Home")} onClick={goHome}><img className="institutions-mobile-wordmark" src={ICONS.canonicalBrand} alt="AfriMed" /></button>
            <div className="institutions-heading-group" data-testid="institution-header-leading">
              {useInstitutionContextHeader ? (
                <>
                  <div className="institutions-title institutions-title--institution" data-testid="institution-header-title">
                    <button type="button" className="institutions-home-mark" aria-label="AfriMed" title="AfriMed™" onClick={goHome}><img src="/icons/afrimed-icon@512.svg" alt="" className="institutions-home-mark-icon" draggable="false" /></button>
                    <div className="institutions-institution-switcher" ref={canSwitchInstitution ? institutionSwitcherRef : null}>
                      {canSwitchInstitution ? (
                        <button
                          type="button"
                          className="institutions-institution-button"
                          aria-haspopup="menu"
                          aria-expanded={institutionMenuOpen}
                          aria-label={institutionShellLabel("switchInstitution", "Switch Institution: {name}").replace("{name}", activeInstitutionName)}
                          title={activeInstitutionName}
                          onClick={() => setInstitutionMenuOpen((open) => !open)}
                        >
                          <h1 className={TOP_BAR_TITLE_CLASS_NAME}>{activeInstitutionName}</h1>
                          <InstitutionIcon src={ICONS.unfoldMore} size={18} />
                        </button>
                      ) : (
                        <div className="institutions-institution-static" title={activeInstitutionName}>
                          <h1 className={TOP_BAR_TITLE_CLASS_NAME}>{activeInstitutionName}</h1>
                        </div>
                      )}
                      {institutionMenuOpen ? (
                        <div className="institutions-institution-menu" role="menu">
                          {institutionOptions.map((item) => (
                            <button
                              key={item.id}
                              type="button"
                              role="menuitemradio"
                              aria-checked={item.id === institution?.id}
                              className={item.id === institution?.id ? "is-active" : ""}
                              onClick={() => chooseInstitution(item)}
                            >
                              <span>{institutionDisplayName(item)}</span>
                            </button>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </>
              ) : (
                <div className="institutions-title" data-testid="institution-header-title">
                  <span className={iconMask ? "institutions-title-token-icon" : ""} aria-hidden="true"><InstitutionIcon src={iconSrc} fallback={Icon} size={24} useMask={iconMask} /></span>
                  <h1 className={TOP_BAR_TITLE_CLASS_NAME}>{localizedInstitutionLabel(headerTitle)}</h1>
                </div>
              )}
            </div>
            {useInstitutionContextHeader ? (
              <nav className="institutions-header-live-tab-lane institutions-header-tabs" aria-label={dashboardHeader ? institutionShellLabel("accessibility.dashboardSections") : institutionShellLabel("accessibility.currentSection")}>
                {dashboardHeader ? visibleHeaderTabs.map((tab) => (
                  <button
                    key={tab.label}
                    type="button"
                    className={location.pathname === tab.to ? "is-active" : ""}
                    aria-disabled={!tab.enabled}
                    onClick={() => (tab.enabled ? navigate(institutionRoute(tab.to)) : null)}
                  >
                    {localizedInstitutionLabel(tab)}
                  </button>
                )) : <span className="institutions-header-current-section" aria-current="page">{localizedInstitutionLabel(title)}</span>}
              </nav>
            ) : null}
            <div className="institutions-header-actions" data-testid="institution-header-trailing">
              {showContextSearch ? (
                <button
                  ref={facilitySearchTriggerRef}
                  type="button"
                  className="institutions-search"
                  data-testid={clinicianSearch ? "institution-clinician-search-trigger" : "medical-institution-search-trigger"}
                  data-facility-search-trigger={!clinicianSearch || undefined}
                  aria-label={clinicianSearch ? t("institutionClinicians.search", "Search clinicians") : institutionShellLabel("searchMedicalInstitutions", "Search medical institutions")}
                  aria-haspopup="dialog"
                  aria-controls={clinicianSearch ? "institution-clinician-search-dialog" : "medical-institution-search-dialog"}
                  aria-expanded={clinicianSearch ? clinicianSearchOpen : facilitySearchOpen}
                  onPointerDown={(event) => event.stopPropagation()}
                  onClick={clinicianSearch ? openClinicianSearch : openFacilitySearch}
                  disabled={clinicianSearch ? !clinicianSearchAvailable : !facilitySearchAvailable}
                >
                  <InstitutionIcon src={ICONS.search} fallback={Search} size={24} />
                  <span>{clinicianSearch ? t("institutionClinicians.search", "Search clinicians") : institutionShellLabel("searchMedicalInstitutions", "Search medical institutions")}</span>
                </button>
              ) : null}
              <button type="button" className="institutions-header-icon" aria-label={institutionShellLabel("notifications.title")} onClick={() => navigate(institutionRoute("/institutions/notifications"))}>
                <InstitutionIcon src={ICONS.bell} size={24} />
              </button>
              <button type="button" className="institutions-header-icon" aria-label={institutionShellLabel("navigation.documentation")} onClick={() => navigate(institutionRoute("/institutions/docs"))}>
                <InstitutionIcon src={ICONS.help} size={24} />
              </button>
              <div className="institutions-me" ref={meMenuRef}>
                <button
                  type="button"
                  className="institutions-me-button" data-testid="institution-header-account-control"
                  aria-haspopup="menu"
                  aria-expanded={menuOpen}
                  onClick={() => setMenuOpen((open) => !open)}
                >
                  {renderAccountAvatar()}
                  <span>{institutionShellLabel("account.me")}</span>
                  <InstitutionIcon src={ICONS.expand} fallback={ChevronDown} size={18} useMask />
                </button>
                {menuOpen ? (
                  <div className="institutions-me-menu institutions-menu-surface" role="menu" data-institutions-me-menu>
                    <button type="button" role="menuitem" onClick={() => goToMenuItem("/institutions/account")}>
                      <InstitutionIcon src={ICONS.accountCircleFilled} size={18} useMask />
                      <span>{institutionShellLabel("account.myAccount")}</span>
                    </button>
                    {canManageInstitutions ? <button type="button" role="menuitem" onClick={() => goToMenuItem("/institutions/medical-institutions")}><InstitutionIcon src={ICONS.menuMedicalInstitutions} fallback={Building2} size={18} /><span>{institutionShellLabel("navigation.medicalInstitutions", "Medical Institutions")}</span></button> : null}
                    {visibleMenuItems.map((item) => {
                      const MenuIcon = item.icon;
                      return (
                        <button
                          key={item.to}
                          type="button"
                          role="menuitem"
                          onClick={() => goToMenuItem(item.to)}
                        >
                          <InstitutionIcon src={item.iconSrc} fallback={MenuIcon} size={18} useMask />
                          <span>{localizedInstitutionLabel(item)}</span>
                        </button>
                      );
                    })}
                    <button type="button" role="menuitem" onClick={() => goToMenuItem("/institutions/docs")}>
                      <InstitutionIcon src={ICONS.help} size={18} useMask />
                      <span>{institutionShellLabel("navigation.documentation")}</span>
                    </button>
                    <button type="button" role="menuitem" onClick={goToInstitutionFeedback}>
                      <MessageSquareWarning size={18} aria-hidden="true" />
                      <span>{institutionShellLabel("actions.makeSuggestion")}</span>
                    </button>
                    <div className="institutions-me-theme" role="group" aria-label={institutionShellLabel("theme.title")}>
                      <p>{institutionShellLabel("theme.title")}</p>
                      {INSTITUTION_THEME_OPTIONS.map((option) => (
                        <InstitutionThemeOption
                          key={option.value}
                          active={theme === option.value}
                          label={localizeThemeOption(option)}
                          iconSrc={option.iconSrc}
                          onClick={() => handleInstitutionThemeSelect(option.value)}
                        />
                      ))}
                    </div>
                    <div className="institutions-me-theme" role="group" aria-label={institutionShellLabel("language.title")}>
                      <p>{institutionShellLabel("language.title")}</p>
                      <InstitutionLanguageOption active={!String(languageCode || "").toLowerCase().startsWith("fr")} label={institutionShellLabel("language.english")} onClick={() => setLanguage("en-GB")} />
                      <InstitutionLanguageOption active={String(languageCode || "").toLowerCase().startsWith("fr")} label={institutionShellLabel("language.french")} onClick={() => setLanguage("fr")} />
                    </div>
                    <button type="button" role="menuitem" className="institutions-me-menu-critical" onClick={handleSignOut}>
                      <InstitutionIcon src={ICONS.signOut} size={18} />
                      <span>{institutionShellLabel("actions.signOut")}</span>
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
          {showContextSearch ? (
            <div className="institutions-mobile-search-rail afrimed-rail afrimed-rail--comfortable">
              <AfrimedSearchBar
                value={mobileSearch}
                onChange={(event) => setMobileSearch(event.target.value)}
                onClear={() => setMobileSearch("")}
                placeholder={clinicianSearch ? t("institutionClinicians.search", "Search clinicians") : institutionShellLabel("searchMedicalInstitutions", "Search medical institutions")}
                ariaLabel={clinicianSearch ? t("institutionClinicians.search", "Search clinicians") : institutionShellLabel("searchMedicalInstitutions", "Search medical institutions")}
                showTrailing={false}
              />
            </div>
          ) : null}
        </header>
        {mobileSearch.trim() && clinicianSearch ? (
          <div className="institutions-mobile-facility-search afrimed-rail afrimed-rail--comfortable">
            <InstitutionClinicianSearchResults
              query={mobileSearch}
              clinicians={clinicianSearchRoster}
              onSelect={openClinicianSearchResult}
            />
          </div>
        ) : mobileSearch.trim() && facilitySearchAvailable ? (
          <div className="institutions-mobile-facility-search afrimed-rail afrimed-rail--comfortable">
            <MedicalInstitutionSearchResults
              query={mobileSearch}
              facilities={facilitySearchFacilities}
              members={facilitySearchMembers}
              loading={!facilitySearchNetwork && !facilitySearchError}
              error={facilitySearchErrorLabel}
              onSelect={openFacilitySearchResult}
              onSelectMember={openFacilityMemberSearchResult}
            />
          </div>
        ) : null}
        {mobileDrawerOpen ? createPortal(
          <div className="institutions-mobile-drawer-backdrop" onMouseDown={closeMobileDrawer}>
            <aside className="institutions-mobile-drawer" aria-label={institutionShellLabel("accessibility.navigation")} onMouseDown={(event) => event.stopPropagation()}>
              <header><button type="button" className="institutions-drawer-home-brand" aria-label={t("institutionHome.title", "Home")} onClick={goHome}><img src={ICONS.canonicalBrand} alt="AfriMed" /></button><button type="button" className="topbar-back" aria-label={institutionShellLabel("accessibility.closeNavigation")} onClick={closeMobileDrawer}><InstitutionIcon src={ICONS.close} size={20} useMask /></button></header>
              {showContextSearch ? (
                <AfrimedSearchBar
                  value={mobileSearch}
                  onChange={(event) => setMobileSearch(event.target.value)}
                  onClear={() => setMobileSearch("")}
                  placeholder={clinicianSearch ? t("institutionClinicians.search", "Search clinicians") : institutionShellLabel("searchMedicalInstitutions", "Search medical institutions")}
                  ariaLabel={clinicianSearch ? t("institutionClinicians.search", "Search clinicians") : institutionShellLabel("searchMedicalInstitutions", "Search medical institutions")}
                  showTrailing={false}
                  className="institutions-mobile-drawer-search"
                />
              ) : null}
              <button type="button" className="institutions-mobile-notifications" onClick={() => goToMenuItem("/institutions/notifications")}><span><strong className="text-title-small">{institutionShellLabel("notifications.title")}</strong><span className="text-body-small">{institutionShellLabel("notifications.support")}</span></span><span className="text-body-medium">{institutionShellLabel("notifications.empty")}</span></button>
              <nav>{canManageInstitutions ? <button type="button" className={location.pathname.startsWith("/institutions/medical-institutions") ? "is-active" : ""} onClick={() => goToMenuItem("/institutions/medical-institutions")}><InstitutionIcon src={ICONS.menuMedicalInstitutions} fallback={Building2} size={22} useMask /><span>{institutionShellLabel("navigation.medicalInstitutions", "Medical Institutions")}</span></button> : null}{visibleMenuItems.map((item) => <button key={item.to} type="button" className={location.pathname === item.to ? "is-active" : ""} onClick={() => goToMenuItem(item.to)}><InstitutionIcon src={item.iconSrc} fallback={item.icon} size={22} useMask={Boolean(item.iconSrc)} /><span>{localizedInstitutionLabel(item)}</span></button>)}<button type="button" onClick={() => goToMenuItem("/institutions/docs")}><InstitutionIcon src={ICONS.help} size={22} useMask /><span>{institutionShellLabel("navigation.documentation")}</span></button></nav>
              <footer><div className="institutions-mobile-member">{renderAccountAvatar()}<span><strong>{institutionShellLabel("account.me")}</strong><small>{activeInstitutionName}</small></span></div><button type="button" onClick={() => goToMenuItem("/institutions/account")}><InstitutionIcon src={ICONS.accountCircleFilled} size={22} useMask /><span>{institutionShellLabel("account.myAccount")}</span></button><div className="institutions-mobile-theme" role="group" aria-label={institutionShellLabel("theme.title")}><p>{institutionShellLabel("theme.title")}</p>{INSTITUTION_THEME_OPTIONS.map((option) => <InstitutionThemeOption key={option.value} active={theme === option.value} label={localizeThemeOption(option)} iconSrc={option.iconSrc} onClick={() => handleInstitutionThemeSelect(option.value)} />)}</div><div className="institutions-mobile-language" role="group" aria-label={institutionShellLabel("language.title")}><p>{institutionShellLabel("language.title")}</p><InstitutionLanguageOption active={!String(languageCode || "").toLowerCase().startsWith("fr")} label={institutionShellLabel("language.english")} onClick={() => setLanguage("en-GB")} /><InstitutionLanguageOption active={String(languageCode || "").toLowerCase().startsWith("fr")} label={institutionShellLabel("language.french")} onClick={() => setLanguage("fr")} /></div><button type="button" onClick={goToInstitutionFeedback}><MessageSquareWarning size={22} /><span>{institutionShellLabel("actions.makeSuggestion")}</span></button><button type="button" className="is-critical" onClick={handleSignOut}><LogOut size={22} /><span>{institutionShellLabel("actions.signOut")}</span></button></footer>
            </aside>
          </div>, document.body) : null}
        <MedicalInstitutionSearchSurface
          open={facilitySearchOpen}
          facilities={facilitySearchFacilities}
          members={facilitySearchMembers}
          loading={!facilitySearchNetwork && !facilitySearchError}
          error={facilitySearchErrorLabel}
          onClose={closeFacilitySearch}
          onSelect={openFacilitySearchResult}
          onSelectMember={openFacilityMemberSearchResult}
        />
        <InstitutionClinicianSearchSurface
          open={clinicianSearchOpen}
          clinicians={clinicianSearchRoster}
          onClose={closeClinicianSearch}
          onSelect={openClinicianSearchResult}
        />
        <div className={`institutions-content ${contentClassName}`} data-testid="institution-body-rail">
          <InstitutionPageShell
            title={pageTopBarTitle}
            subtitle={pageTopBarSubtitle}
            navigation={pageTopBarNavigation}
            centered={pageTopBarCentered ?? (Boolean(pageTopBarTitle) && !dashboardHeader && !profileDetail)}
            onBack={pageTopBarNavigation === INSTITUTION_TOP_BAR_NAVIGATION.BACK ? () => navigate(pageTopBarBackTo ? institutionRoute(pageTopBarBackTo) : -1) : undefined}
            backLabel={pageTopBarBackLabel || institutionShellLabel("actions.back", "Back")}
            headerActions={pageTopBarActions}
            flushTop={profileDetail}
          >
            {children}
          </InstitutionPageShell>
        </div>
      </main>
      {logoutOpen &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className="fixed inset-0 institutions-logout-dialog bg-[rgba(15,23,42,0.35)] backdrop-blur-sm flex items-center justify-center"
            role="dialog"
            aria-modal="true"
            onClick={() => (logoutPending ? null : setLogoutOpen(false))}
          >
            <div
              className="w-[420px] rounded-2xl bg-[rgb(var(--clr-surface-cont-highest))] shadow-[0_18px_45px_rgba(15,23,42,0.18)] overflow-hidden"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="p-6">
                <div className="mx-auto w-full max-w-[320px] flex items-start justify-center gap-3">
                  <img src={ICONS.signOut} alt="" className="h-6 w-6 mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <div className="text-title-medium text-on-surface">
                      {institutionShellLabel("logout.title")}
                    </div>
                    <div className="mt-1 text-body-medium text-on-surface-var">
                      {institutionShellLabel("logout.body")}
                    </div>
                  </div>
                </div>

                <div className="mt-5 flex items-center justify-center gap-3">
                  <button
                    type="button"
                    className="flex-1 rounded-xl px-4 py-3 text-label-large disabled:opacity-60"
                    style={{
                      background: "var(--State-Layers-Primary-Opacity-08,rgba(29,79,215,0.08))",
                      color: "var(--Schemes-Primary,#1D4FD7)",
                    }}
                    disabled={logoutPending}
                    onClick={() => setLogoutOpen(false)}
                  >
                    {institutionShellLabel("logout.cancel")}
                  </button>

                  <button
                    type="button"
                    className="flex-1 rounded-xl px-4 py-3 text-label-large disabled:opacity-60"
                    style={{
                      background: "var(--State-Layers-Critical-Opacity-08,rgba(248,83,58,0.08))",
                      color: "var(--Schemes-Critical,#F8533A)",
                    }}
                    disabled={logoutPending}
                    onClick={confirmSignOut}
                  >
                    {logoutPending
                      ? institutionShellLabel("logout.loggingOut")
                      : institutionShellLabel("logout.confirm")}
                  </button>
                </div>
              </div>

              <div className="h-px bg-[rgba(15,23,42,0.06)]" />
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}

function Dashboard({ workspace, onInstitutionSelect }) {
  const { t, formatDate } = useLanguage();
  const counts = workspace.counts || {};
  const [activeRange, setActiveRange] = useState("Today");
  const [appointmentSnapshot, setAppointmentSnapshot] = useState(null);
  const [appointmentLoading, setAppointmentLoading] = useState(false);
  const [appointmentError, setAppointmentError] = useState("");
  const [calendarSnapshot, setCalendarSnapshot] = useState(null);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const dashboard = (key, fallback) => t(`institutionDashboard.${key}`, fallback);
  const hasAppointmentLinkage = appointmentSnapshot?.available === true;
  const appointmentCount = hasAppointmentLinkage ? appointmentSnapshot.count : null;
  const appointmentRows = hasAppointmentLinkage && Array.isArray(appointmentSnapshot.rows) ? appointmentSnapshot.rows : [];
  // Only the guarded, explicitly linked Institution projection can populate
  // this count. A missing contract or denied authority remains unknown.
  const kpiValues = {
    patients: workspace.patientContractAvailable ? String(Number(counts.patients || 0)) : dashboard("unavailable", "Unavailable"),
    rating: counts.rating == null || !Number.isFinite(Number(counts.rating)) ? dashboard("unavailable", "Unavailable") : Number(counts.rating).toFixed(1),
    appointments: appointmentCount === null ? dashboard("unavailable", "Unavailable") : String(appointmentCount),
    visitors: dashboard("unavailable", "Unavailable"),
  };

  useEffect(() => {
    let alive = true;
    const institutionId = workspace.activeInstitution?.id;
    if (!institutionId) { setAppointmentSnapshot(null); return undefined; }
    setAppointmentLoading(true); setAppointmentSnapshot(null); setAppointmentError("");
    supabase.rpc("institution_appointment_period_snapshot", { target_institution: institutionId, period_input: activeRange.toLowerCase() })
      .then(({ data, error }) => {
        if (!alive) return;
        if (error) { setAppointmentError(error.message || "Appointment reporting is unavailable."); setAppointmentSnapshot(null); }
        else setAppointmentSnapshot(data || { available: false });
      })
      .catch((error) => { if (alive) { setAppointmentError(error?.message || "Appointment reporting is unavailable."); setAppointmentSnapshot(null); } })
      .finally(() => { if (alive) setAppointmentLoading(false); });
    return () => { alive = false; };
  }, [workspace.activeInstitution?.id, activeRange]);

  useEffect(() => {
    let alive = true;
    const institutionId = workspace.activeInstitution?.id;
    if (!institutionId) { setCalendarSnapshot(null); return undefined; }
    setCalendarLoading(true); setCalendarSnapshot(null);
    // The middle of the selected month is safe across all Institution timezones.
    const anchor = new Date(Date.UTC(calendarMonth.getFullYear(), calendarMonth.getMonth(), 15, 12)).toISOString();
    supabase.rpc("institution_appointment_period_snapshot", { target_institution: institutionId, period_input: "month", anchor_input: anchor })
      .then(({ data, error }) => { if (alive) setCalendarSnapshot(error ? null : data || null); })
      .catch(() => { if (alive) setCalendarSnapshot(null); })
      .finally(() => { if (alive) setCalendarLoading(false); });
    return () => { alive = false; };
  }, [workspace.activeInstitution?.id, calendarMonth]);

  return (
    <PageShell workspace={workspace} title={dashboard("title", "Dashboard")} icon={LayoutDashboard} dashboardHeader onInstitutionSelect={onInstitutionSelect}>
      <div className="institutions-dashboard-layout">
        <section className="institutions-dashboard-kpis institutions-dashboard-kpis--three" aria-label="Institution dashboard metrics">
          <DashboardMetricCard
            iconSrc={ICONS.dashboardPatients}
            iconMask
            label={dashboard("patients", "Patients")}
            value={kpiValues.patients}
          />
          <DashboardMetricCard
            iconSrc={ICONS.rating}
            label={dashboard("overallRating", "Overall rating")}
            value={kpiValues.rating}
          />
          <DashboardMetricCard
            iconSrc={ICONS.appointments}
            label={dashboard("appointments", "Appointments")}
            value={kpiValues.appointments}
            footerLabel={hasAppointmentLinkage ? dashboard(`ranges.${activeRange.toLowerCase()}`, activeRange) : ""}
          />
        </section>
        <section className="institutions-dashboard-main-grid">
          <article className="institutions-dashboard-chart">
          <div className="institutions-dashboard-chart-head">
            <div>
              <div className="institutions-dashboard-card-title">{dashboard("clinicVisitors", "Clinic visitors")}</div>
              <strong>{kpiValues.visitors}</strong>
            </div>
          </div>
          <div className="institutions-dashboard-chart-unavailable" role="status">{dashboard("visitorSourceUnavailable", "Visitor chart unavailable: no governed Institution check-in source is connected. Appointments are not visitor counts.")}</div>
          </article>
          <DashboardCalendar
            month={calendarMonth}
            snapshot={calendarSnapshot}
            loading={calendarLoading}
            onMonthChange={setCalendarMonth}
            onPrev={() => setCalendarMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))}
            onNext={() => setCalendarMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))}
            t={t}
            formatDate={formatDate}
          />
        </section>
        <section className="institutions-dashboard-table-card">
          <div className="institutions-dashboard-table-head">
            <div>
              <h2 className="text-title-large">{dashboard("patientAppointments", "Patients Appointments")}</h2>
              <p>{dashboard("patientAppointmentsIntro", "An overview of the patient's journey and the assistance provided at the clinic.")}</p>
              {hasAppointmentLinkage ? <p className="text-body-small text-on-surface-variant">{dashboard("appointmentCoverage", "Explicitly linked Institution appointments only")} · {appointmentSnapshot.timezone} · {dashboard("appointmentIncludedStatuses", "Scheduled, in progress and completed; cancelled excluded")}</p> : null}
            </div>
            <DashboardSegmented active={activeRange} onChange={setActiveRange} t={t} />
          </div>
          {appointmentError ? <p className="px-4 text-body-small text-on-surface-variant" role="alert">{dashboard("appointmentReportingUnavailable", "Appointment reporting requires an authorised Institution data link.")}</p> : null}
          <div className="institutions-dashboard-table">
            <div className="institutions-dashboard-table-row institutions-dashboard-table-row--head">
              <span>{dashboard("columns.patientName", "Patient name")}</span>
              <span>{dashboard("columns.primaryCondition", "Primary condition")}</span>
              <span>{dashboard("columns.riskLevel", "Risk level")}</span>
              <span>{dashboard("columns.insurance", "Insurance")}</span>
              <span>{dashboard("columns.lastVisit", "Last visit")}</span>
              <span>{dashboard("columns.doctors", "Doctors")}</span>
              <span />
            </div>
            {appointmentLoading ? <div className="institutions-dashboard-table-empty" role="status">{dashboard("appointmentLoading", "Loading scoped appointments…")}</div> : appointmentRows.length ? (
              appointmentRows.map((row, index) => <AppointmentTableRow key={row?.id || index} row={row} timezone={appointmentSnapshot?.timezone} />)
            ) : (
              <div className="institutions-dashboard-table-empty">
                {hasAppointmentLinkage
                  ? dashboard("noAppointments", "No linked appointments in this range.")
                  : dashboard(
                    "appointmentReportingUnavailable",
                    "Appointment reporting requires an authorised Institution data link.",
                  )}
              </div>
            )}
          </div>
        </section>
      </div>
    </PageShell>
  );
}

function SettingsView({ workspace }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const institutionId = workspace?.activeInstitution?.id || "";
  const facilityId = useMemo(() => new URLSearchParams(location.search).get("facility") || "", [location.search]);
  const [network, setNetwork] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (facilityId) return undefined;
    let active = true;
    loadMedicalInstitutionNetwork({ institutionId }).then((value) => { if (active) setNetwork(value); }).catch(() => { if (active) setError("Medical Institutions could not be loaded. Try again."); });
    return () => { active = false; };
  }, [facilityId, institutionId]);
  if (facilityId) return <Navigate to={institutionPath(institutionId, `/${facilityId}`)} replace />;
  return <PageShell
    workspace={workspace}
    title={t("institutionSettings.title", "Settings")}
    icon={Settings}
    iconSrc={ICONS.settings}
    pageTopBarTitle={t("institutionSettings.title", "Settings")}
    pageTopBarSubtitle={t("institutionSettings.scope", "Organisation settings and integrations")}
    pageTopBarNavigation="back"
  >
    <section className="medical-institution-settings-picker" aria-label="Institution settings">
      <Button variant="tonal" onClick={() => navigate(`/institutions/settings/integrations-interoperability${location.search}`)}>{t("settings.preferences.integrations.title", "Integrations • Interoperability")}</Button>
      {error ? <div className="medical-institution-error" role="alert">{error}</div> : null}
      {!network && !error ? <NetworkLoadingState /> : null}
      {network?.facilities?.length > 0 ? <div className="medical-institution-settings-picker-list">{network.facilities.map((facility) => <button key={facility.id} type="button" onClick={() => navigate(facilitySettingsPath(institutionId, facility.id))}><FacilityLogo facility={facility} /><span className="medical-institution-settings-picker-copy"><strong>{getFacilityName(facility)}</strong><small>ID: {facility.reference || truncateId(facility.id)}</small></span><ChevronRight size={20} aria-hidden="true" /></button>)}</div> : null}

      {network && !network.facilities?.length ? <p className="text-body-medium">{t("institutionSettings.empty", "No Medical Institutions are available in this parent organisation.")}</p> : null}
    </section>
  </PageShell>;
}

function InstitutionAccountView({ workspace, onInstitutionSelect }) {
  const auth = useAuth() || {};
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [recoverySending, setRecoverySending] = useState(false);
  const [accountNotice, setAccountNotice] = useState("");
  const [savingName, setSavingName] = useState(false);
  const profile = auth.profile || {};
  const institution = workspace.activeInstitution || {};
  const institutionName = institutionDisplayName(institution);
  const displayName = profile.full_name || profile.display_name || auth.user?.user_metadata?.full_name || auth.user?.email || "Institution member";
  const [fullName, setFullName] = useState(displayName);
  const profileImage = profile.avatar_url || profile.profile_image_url || auth.user?.user_metadata?.avatar_url || "";
  const role = institutionControlledLabel(t, (workspace.userRoles || [])[0] || "member", t("institutionAccount.member", "Member"));
  const email = auth.user?.email || "";

  useEffect(() => {
    if (!savingName) setFullName(displayName);
  }, [displayName, savingName]);

  const handleSaveName = async (event) => {
    event?.preventDefault?.();
    if (savingName) return;
    setSavingName(true);
    setAccountNotice("");
    try {
      const result = await updateOwnAccountDisplayName(fullName);
      setFullName(result.display_name || fullName);
      await auth?.refreshProfile?.();
      setAccountNotice(t("institutionAccount.detailsSaved", "Account details saved."));
    } catch (error) {
      setAccountNotice(String(error?.message || t("institutionAccount.detailsSaveFailed", "We couldn’t save your account details.")));
    } finally {
      setSavingName(false);
    }
  };

  const handleRecoveryLink = async () => {
    if (!email || recoverySending) return;
    setRecoverySending(true);
    setAccountNotice("");
    try {
      await requestAccountPasswordReset(email);
      setAccountNotice(t("institutionAccount.recoverySent", "Secure recovery link sent."));
    } catch {
      setAccountNotice(t("institutionAccount.recoveryFailed", "We couldn’t send a recovery link right now. Please try again."));
    } finally {
      setRecoverySending(false);
    }
  };

  return (
    <PageShell
      workspace={workspace}
      dashboardHeader
      dashboardHeaderTitle={institutionName}
      onInstitutionSelect={onInstitutionSelect}
      contentClassName="w-full"
    >
      <SettingsScreenShell className="[--rail-pad:12px] lg:[--rail-pad:var(--afrimed-rail-gutter-desktop)]">
        <WorkspaceAccountContract title={t("institutionAccount.title", "My account")} subtitle={t("institutionAccount.subtitle", "Your AfriMed™ Institution identity, image, and role settings.")} onBack={() => navigate(-1)}>
        <div className={WORKSPACE_ACCOUNT_GRID_CLASS}>
          <section className="order-1 lg:col-start-1 lg:row-start-1 lg:self-start">
            <form className="flex flex-col gap-6" onSubmit={handleSaveName}>
            <div className="px-1"><h2 className="text-title-large text-on-surface">{t("institutionAccount.memberProfile", "Member profile")}</h2><p className="mt-1 text-body-medium text-on-surface-var">{t("institutionAccount.memberProfileCopy", "Keep your Institution identity clear and role-safe across AfriMed™.")}</p></div>
            <TextField56 label={t("institutionAccount.fullName", "Full name")} value={fullName} onChange={setFullName} autoComplete="name" disabled={savingName} tone="surface" />
            <TextField56 label={t("institutionAccount.workEmail", "Work email")} value={email} readOnly tone="surface" />
            <AccountEmailChangeAction lane="institution" currentEmail={email} disabled={savingName} />
            <div className="grid gap-3 md:grid-cols-2"><div className="rounded-[12px] bg-[rgb(var(--clr-surface-cont-highest))] px-4 py-4"><div className="text-label-medium text-on-surface-var">{t("institutionAccount.role", "Institution role")}</div><div className="mt-1 text-title-medium text-on-surface">{role}</div></div><div className="rounded-[12px] bg-[rgb(var(--clr-surface-cont-highest))] px-4 py-4"><div className="text-label-medium text-on-surface-var">{t("institutionAccount.institution", "Institution")}</div><div className="mt-1 text-title-medium text-on-surface">{institutionName}</div></div></div>
            <div className="px-1"><div className="text-title-medium text-on-surface">{t("institutionAccount.privilegeHandoff", "Privilege handoff")}</div><p className="mt-2 text-body-medium text-on-surface-var">{t("institutionAccount.privilegeHandoffCopy", "Can manage Institution operations and approved member workflows within assigned permissions. Surveillance authority, facility authority and programme authority remain separately controlled and audited.")}</p></div>
            {accountNotice ? <div className="rounded-[12px] bg-[var(--state-primary-12)] px-4 py-3 text-body-medium text-on-surface">{accountNotice}</div> : null}
            <p className="px-1 text-body-small text-on-surface-var">{t("institutionAccount.emailLifecycle", "Email changes use the existing verified sign-in lifecycle and are not managed through Institution governance.")}</p>
            <div className="flex flex-wrap items-center gap-3 px-1"><Button type="submit" disabled={savingName || !String(fullName || "").trim()}>{savingName ? t("institutionAccount.saving", "Saving…") : t("institutionAccount.save", "Save changes")}</Button></div>
            </form>
          </section>
          <section className="order-2 lg:col-start-2 lg:row-span-2 lg:row-start-1 lg:self-start"><div className="flex flex-col gap-5"><div><h2 className="text-title-large text-on-surface">{t("institutionAccount.profileImage", "Profile image")}</h2><p className="mt-1 text-body-medium text-on-surface-var">{t("institutionAccount.profileImageCopy", "Stored in the established shared AfriMed profile image area and preferred by the header first.")}</p></div><ProfilePictureUpload accountLayout currentImageUrl={profileImage} displayName={displayName} userType="user" messageNamespace="institutionAccount.imageActions" onUploadSuccess={() => setAccountNotice(t("institutionAccount.imageSaved", "Profile image saved."))} onUploadError={() => setAccountNotice(t("institutionAccount.imageSaveFailed", "Profile image could not be saved."))} /><p className="text-center text-body-small text-on-surface-var">{t("institutionAccount.imageSupport", "Supported: JPEG, PNG, WebP up to 5MB.")}</p></div></section>
          <section className="order-3 lg:col-start-1 lg:row-start-2"><AccountPasswordLifecyclePanel context="settings" intent="create" hasPasswordOverride={false} userEmailOverride={email} titleOverride={t("institutionAccount.passwordTitle", "Password and sign-in")} copyOverride={t("institutionAccount.passwordCopy", "Use your password for normal Institution sign-in. Secure links remain available for first-time setup and recovery.")} createSignOutNoticeOverride={null} submitLabelOverride={t("institutionAccount.passwordSubmit", "Set or change password")} enableResetHandoff={false} resetBasePath="/institutions/account" loginSecurityPath="/institutions/account" clearPasswordMigrationOnSuccess submitLeadingIcon={<span aria-hidden className="h-4 w-4 shrink-0 bg-[rgb(var(--afrimed-on-primary))]" style={{ WebkitMask: "url('/icons/key.svg') center / contain no-repeat", mask: "url('/icons/key.svg') center / contain no-repeat" }} />} submitRowClassName="flex w-full flex-col gap-3 sm:flex-row sm:items-center" secondarySubmitAction={<Button type="button" variant="tonal" onClick={handleRecoveryLink} disabled={recoverySending || !email} loading={recoverySending} className="min-h-10 gap-2 sm:flex-1" fullWidth>{t("institutionAccount.recovery", "Send recovery link")}</Button>} /></section>
        </div>
        </WorkspaceAccountContract>
      </SettingsScreenShell>
    </PageShell>
  );
}

function InstitutionFeedbackView({ workspace, onInstitutionSelect }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [submitted, setSubmitted] = useState(false);
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const returnTo = params.get("returnTo") || `/institutions/regional-health?institution=${workspace?.activeInstitution?.id || ""}`;

  return (
    <PageShell
      workspace={workspace}
      dashboardHeader
      dashboardHeaderTitle={institutionDisplayName(workspace?.activeInstitution)}
      onInstitutionSelect={onInstitutionSelect}
      pageTopBarTitle={t("institutionFeedback.title", "Staging Feedback")}
      pageTopBarSubtitle={t("institutionFeedback.subtitle", "Share what worked, what felt unclear, or what needs attention in preparation for production.")}
      pageTopBarNavigation="back"
      contentClassName="afrimed-rail afrimed-rail--comfortable pb-24"
    >
      <section className="mx-auto w-full max-w-[1008px]" aria-label="Institution feedback">
        {submitted ? (
          <div className="afrimed-card max-w-[520px] p-5">
            <h2 className="text-title-large text-on-surface">Thank you</h2>
            <p className="mt-2 text-body-medium text-on-surface-var">Your feedback has been saved for the AfriMed™ team.</p>
            <Button className="mt-5" onClick={() => navigate(returnTo)}>Return to Regional Health</Button>
          </div>
        ) : (
          <FeedbackForm shell="bare" onSubmitted={() => setSubmitted(true)} />
        )}
      </section>
    </PageShell>
  );
}

function SettingsNavItem({ icon: Icon, iconSrc, label, active = false }) {
  return (
    <div className={`institutions-settings-nav-item ${active ? "is-active" : ""}`}>
      <InstitutionIcon src={iconSrc} fallback={Icon} size={24} useMask={Boolean(iconSrc)} />
      <span>{label}</span>
    </div>
  );
}

function GroupedHeader({ title, description = "", compact = false, nodeId }) {
  return (
    <div className={`institutions-settings-grouped-header ${compact ? "is-compact" : ""}`} data-node-id={nodeId}>
      <h2>{title}</h2>
      {description ? <p>{description}</p> : null}
    </div>
  );
}

function SettingsFormSection({ title, children, nodeId }) {
  return (
    <section className="institutions-settings-form-section" data-node-id={nodeId}>
      <GroupedHeader title={title} />
      <div className="institutions-settings-form-fields">
        {children}
      </div>
    </section>
  );
}

function SettingsTextField({ label, value, nodeId }) {
  return (
    <div className="institutions-settings-text-field" data-node-id={nodeId}>
      <span>{label}</span>
      <strong>{value || "Setup pending"}</strong>
    </div>
  );
}

const MEDICAL_INSTITUTION_TYPE_OPTIONS = [
  { value: "Hospital", label: "Hospital", icon: `${MI_ICON_ROOT}/hospital_Fill.svg` },
  { value: "Clinic", label: "Clinic", icon: `${MI_ICON_ROOT}/Clinic_fill.svg` },
  { value: "Pharmacy", label: "Pharmacy", icon: `${MI_ICON_ROOT}/local_pharmacy.svg` },
  { value: "Laboratory", label: "Laboratory", icon: `${MI_ICON_ROOT}/labs.svg` },
  { value: "Imaging centre", label: "Imaging centre", icon: `${MI_ICON_ROOT}/add_photo_alternate.svg` },
  { value: "Medical centre", label: "Medical centre", icon: `${MI_ICON_ROOT}/home-health.svg` },
  { value: "Specialist centre", label: "Specialist centre", icon: `${MI_ICON_ROOT}/personal_injury_Filled.svg` },
  { value: "Other authorised healthcare facility", label: "Other authorised healthcare facility", icon: `${MI_ICON_ROOT}/hospital_avatar.svg` },
];

const MEDICAL_INSTITUTION_TYPE_MESSAGE_KEYS = {
  Hospital: "hospital",
  Clinic: "clinic",
  Pharmacy: "pharmacy",
  Laboratory: "laboratory",
  "Imaging centre": "imagingCentre",
  "Medical centre": "medicalCentre",
  "Specialist centre": "specialistCentre",
  "Other authorised healthcare facility": "otherAuthorisedHealthcareFacility",
};

const AVAILABILITY_DAYS = [
  { key: "sunday", label: "Sunday", short: "S" },
  { key: "monday", label: "Monday", short: "M" },
  { key: "tuesday", label: "Tuesday", short: "T" },
  { key: "wednesday", label: "Wednesday", short: "W" },
  { key: "thursday", label: "Thursday", short: "T" },
  { key: "friday", label: "Friday", short: "F" },
  { key: "saturday", label: "Saturday", short: "S" },
];
const FACILITY_DESCRIPTION_MAX_LENGTH = 500;

function institutionPath(institutionId, suffix = "") {
  const encodedInstitution = encodeURIComponent(String(institutionId || ""));
  return `/institutions/medical-institutions${suffix}?institution=${encodedInstitution}`;
}

function clinicianPath(institutionId, rosterId = "") {
  const encodedInstitution = encodeURIComponent(String(institutionId || ""));
  const suffix = rosterId ? `/${encodeURIComponent(String(rosterId))}` : "";
  return `/institutions/clinicians${suffix}?institution=${encodedInstitution}`;
}

function facilitySettingsPath(institutionId, facilityId) {
  const params = new URLSearchParams({ institution: String(institutionId || ""), facility: String(facilityId || "") });
  return `/institutions/settings?${params.toString()}`;
}

function NetworkLoadingState() {
  const { t } = useLanguage();
  return <div className="medical-institution-network-loading"><RouteFallback label={t("institutionWorkspace.loading", "Loading Medical Institutions…")} /></div>;
}

function MedicalInstitutionLoadError({ onRetry, compact = false }) {
  const { t } = useLanguage();
  return <div className={`medical-institution-error${compact ? " medical-institution-error--compact" : ""}`} role="alert">
    <p className="text-body-medium">{t("institutionWorkspace.loadError", "Medical Institutions could not be loaded. Try again.")}</p>
    {onRetry ? <Button type="button" variant="text" size="sm" onClick={onRetry}>{t("institutionWorkspace.retry", "Retry")}</Button> : null}
  </div>;
}

function medicalInstitutionCreator(workspace) {
  const user = workspace?.user || {};
  const member = (workspace?.members || []).find((item) => item?.user_id === user?.id)
    || (workspace?.members || []).find((item) => ["owner", "admin", "moh_admin", "institution_admin", "facility_admin"].includes(String(item?.role || "").toLowerCase()))
    || null;
  const metadata = user?.user_metadata || {};
  return {
    name: member ? getMemberName(member) : metadata.full_name || metadata.name || user.email || "Institution administrator",
    email: member ? getMemberEmail(member) : user.email || "",
    avatar: member ? getMemberAvatar(member) : metadata.avatar_url || "",
  };
}

function normaliseAvailabilityTime(value) {
  const match = String(value || "").trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return null;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (!Number.isInteger(hour) || !Number.isInteger(minute) || hour > 23 || minute > 59) return null;
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}

function normaliseOpeningHours(openingHours = {}) {
  const legacyDays = Array.isArray(openingHours?.days) ? openingHours.days.map((day) => String(day).toLowerCase()) : [];
  const legacyOpen = normaliseAvailabilityTime(openingHours?.opensAt);
  const legacyClose = normaliseAvailabilityTime(openingHours?.closesAt);
  const days = Object.fromEntries(AVAILABILITY_DAYS.map(({ key, label }) => {
    const current = openingHours?.[key] || openingHours?.[label] || {};
    const enabled = typeof current?.enabled === "boolean" ? current.enabled : legacyDays.includes(key) || legacyDays.includes(label.toLowerCase());
    const opens = normaliseAvailabilityTime(current?.opens) || legacyOpen;
    const closes = normaliseAvailabilityTime(current?.closes) || legacyClose;
    return [key, { enabled, opens: opens || null, closes: closes || null }];
  }));
  return { isOpen24Hours: openingHours?.isOpen24Hours === true, ...days };
}

function formatAvailabilityTime(value, locale = "") {
  const normalized = normaliseAvailabilityTime(value);
  if (!normalized) return "";
  const [hourValue, minute] = normalized.split(":").map(Number);
  if (locale) {
    return new Intl.DateTimeFormat(locale, { hour: "numeric", minute: "2-digit" }).format(new Date(2000, 0, 1, hourValue, minute));
  }
  const suffix = hourValue >= 12 ? "PM" : "AM";
  const hour = hourValue % 12 || 12;
  return `${hour}:${String(minute).padStart(2, "0")} ${suffix}`;
}

function formatOpeningHoursSummary(openingHours = {}, labels = {}) {
  const schedule = normaliseOpeningHours(openingHours);
  if (schedule.isOpen24Hours) return { days: labels.openEveryDay || "Open 24 hours · Every day", time: "" };
  const enabled = AVAILABILITY_DAYS.filter(({ key }) => schedule[key]?.enabled);
  if (!enabled.length) return { days: labels.setupPending || "Setup pending", time: "" };
  const times = enabled.map(({ key }) => `${schedule[key]?.opens || ""}|${schedule[key]?.closes || ""}`);
  const sameTimes = times.every((value) => value === times[0]);
  const isWeekdays = enabled.length === 5 && enabled.every(({ key }, index) => key === ["monday", "tuesday", "wednesday", "thursday", "friday"][index]);
  const dayLabel = enabled.length === AVAILABILITY_DAYS.length
    ? (labels.allDays || "S,M,T,W,T,F,S")
    : isWeekdays
      ? (labels.weekdays || "Mon – Fri")
      : sameTimes
        ? enabled.map(({ key, short }) => labels.dayShorts?.[key] || short).join(",")
        : (labels.daysConfigured?.(enabled.length) || `${enabled.length} days configured`);
  const [opens, closes] = times[0].split("|");
  const time = sameTimes && opens && closes
    ? (opens === "00:00" && closes === "23:59" ? "24h" : `${formatAvailabilityTime(opens, labels.timeLocale)} – ${formatAvailabilityTime(closes, labels.timeLocale)}`)
    : "";
  return { days: dayLabel, time };
}

function MedicalInstitutionTypePicker({ value, onChange, disabled = false, label = "Institution type *", placeholder = "Select institution type", optionLabel = (option) => option.label }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const selected = MEDICAL_INSTITUTION_TYPE_OPTIONS.find((option) => option.value === value);
  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event) => { if (!rootRef.current?.contains(event.target)) setOpen(false); };
    const onKey = (event) => { if (event.key === "Escape") setOpen(false); };
    document.addEventListener("pointerdown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("pointerdown", onPointer); document.removeEventListener("keydown", onKey); };
  }, [open]);
  return (
    <div className="medical-institution-type-picker" ref={rootRef}>
      <TextField56
        as="button"
        label={label}
        value={selected ? optionLabel(selected) : ""}
        placeholder={placeholder}
        tone="surface"
        leadingIcon={selected?.icon || ICONS.facilityHospital}
        leadingIconMask
        trailingSlot={<ChevronDown size={18} />}
        triggerAriaLabel={label}
        triggerAriaHaspopup="listbox"
        triggerAriaExpanded={open}
        disabled={disabled}
        onTriggerClick={() => setOpen((current) => !current)}
      />
      {open ? <div className="institutions-menu-surface medical-institution-type-menu" role="listbox" aria-label={label}>
        {MEDICAL_INSTITUTION_TYPE_OPTIONS.map((option) => <button key={option.value} type="button" role="option" aria-selected={option.value === value} className={option.value === value ? "is-selected" : ""} onClick={() => { onChange?.(option.value); setOpen(false); }}>
          <InstitutionIcon src={option.icon} size={18} useMask />
          <span>{optionLabel(option)}</span>
        </button>)}
      </div> : null}
    </div>
  );
}

const MEDICAL_INSTITUTION_ACCESSIBILITY_OPTIONS = [
  { value: "private", label: "Private" },
  { value: "public", label: "Public" },
];

function MedicalInstitutionAccessibilityPicker({ value = "private", onChange, disabled = false, label = "Facility ownership", optionLabel = (option) => option.label }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const selected = MEDICAL_INSTITUTION_ACCESSIBILITY_OPTIONS.find((option) => option.value === value) || MEDICAL_INSTITUTION_ACCESSIBILITY_OPTIONS[0];
  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event) => { if (!rootRef.current?.contains(event.target)) setOpen(false); };
    const onKey = (event) => { if (event.key === "Escape") setOpen(false); };
    document.addEventListener("pointerdown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("pointerdown", onPointer); document.removeEventListener("keydown", onKey); };
  }, [open]);
  return <div className="medical-institution-type-picker" ref={rootRef}>
    <TextField56 as="button" label={label} value={optionLabel(selected)} tone="surface" leadingIcon={ICONS.patients} leadingIconMask trailingSlot={<ChevronDown size={18} />} triggerAriaLabel={label} triggerAriaHaspopup="listbox" triggerAriaExpanded={open} disabled={disabled} onTriggerClick={() => setOpen((current) => !current)} />
    {open ? <div className="institutions-menu-surface medical-institution-type-menu" role="listbox" aria-label={label}>
      {MEDICAL_INSTITUTION_ACCESSIBILITY_OPTIONS.map((option) => <button key={option.value} type="button" role="option" aria-selected={option.value === value} className={option.value === value ? "is-selected" : ""} onClick={() => { onChange?.(option.value); setOpen(false); }}><InstitutionIcon src={ICONS.patients} size={18} useMask /><span>{optionLabel(option)}</span></button>)}
    </div> : null}
  </div>;
}

function MedicalInstitutionDescriptionField({ value = "", onChange, disabled = false, label = "Facility description", help = "Optional concise description" }) {
  const count = String(value || "").length;
  return <div className="medical-institution-description-field">
    <label htmlFor="medical-institution-facility-description" className="text-title-medium text-on-surface-variant">{label}</label>
    <textarea
      id="medical-institution-facility-description"
      value={value}
      disabled={disabled}
      maxLength={FACILITY_DESCRIPTION_MAX_LENGTH}
      rows={4}
      aria-describedby="medical-institution-facility-description-help"
      onChange={(event) => onChange?.(event.target.value)}
    />
    <div id="medical-institution-facility-description-help" className="medical-institution-description-field-help">
      <span>{help}</span><span>{count}/{FACILITY_DESCRIPTION_MAX_LENGTH}</span>
    </div>
  </div>;
}

function MedicalInstitutionAvatarControl({ preview, onSelect, disabled = false, ariaLabel = "Edit Medical Institution logo" }) {
  const inputRef = useRef(null);
  const isDefaultAvatar = !preview;
  return (
    <div className="medical-institution-avatar-row" data-node-id="61169:85970">
      <div className="medical-institution-avatar-control">
        <button type="button" className={`medical-institution-profile-avatar${isDefaultAvatar ? " is-default" : ""}`} onClick={() => inputRef.current?.click?.()} disabled={disabled} aria-label={ariaLabel}>
          <img className={`medical-institution-profile-avatar-image ${isDefaultAvatar ? "is-default" : "is-preview"}`} src={preview || ICONS.facilityAvatarPlaceholder} alt="" />
        </button>
        <span className="medical-institution-profile-avatar-edit" aria-hidden="true"><img src={ICONS.facilityEdit} alt="" /></span>
      </div>
      <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={onSelect} hidden />
    </div>
  );
}

const FACILITY_MEMBER_ROLE_LABELS = [
  { value: "owner", label: "Owner" }, { value: "super_admin", label: "Super admin" },
  { value: "moh_admin", label: "MoH admin" }, { value: "institution_admin", label: "Institution admin" },
  { value: "facility_admin", label: "Facility admin" }, { value: "admin", label: "Admin" },
  { value: "pharmacist", label: "Pharmacist" }, { value: "nurse", label: "Nurse" },
  { value: "laboratory_professional", label: "Laboratory professional" },
  { value: "imaging_professional", label: "Imaging professional" }, { value: "receptionist", label: "Receptionist" },
  { value: "operations_staff", label: "Operations staff" },
];
const FACILITY_MEMBER_ROLE_OPTIONS = FACILITY_MEMBER_ROLE_LABELS.filter((option) => [
  "admin",
  "pharmacist",
  "nurse",
  "laboratory_professional",
  "imaging_professional",
  "receptionist",
  "operations_staff",
].includes(option.value));
const CLINICIAN_INTAKE_ROLE_OPTION = { value: "clinician", label: "Clinician" };
const facilityMemberRoleLabel = (role, customRoleLabel = "") => role === "custom_staff"
  ? (customRoleLabel || "Custom staff")
  : (FACILITY_MEMBER_ROLE_LABELS.find((item) => item.value === role)?.label || (role === CLINICIAN_INTAKE_ROLE_OPTION.value ? CLINICIAN_INTAKE_ROLE_OPTION.label : "Member"));

function getMedicalInstitutionFacilityMemberErrorMessage(error, role = "admin") {
  const code = String(error?.code || "");
  const message = String(error?.message || "").toLowerCase();
  if (role === "clinician" || message.includes("clinicians are linked through the governed institution clinicians workflow")) {
    return "Clinicians are linked through the governed Institution Clinicians workflow.";
  }
  if (code === "42501" || error?.status === 403 || /permission denied|not authorized|authority required/.test(message)) {
    return "You do not have permission to manage members for this Medical Institution.";
  }
  if (code === "23505" || /already (linked|assigned|exists)|duplicate|conflict/.test(message)) {
    return "This member is already linked to this Medical Institution. No duplicate relationship was created.";
  }
  return role === "admin" || role === "facility_admin"
    ? "Facility administrator could not be saved. Try again."
    : "Facility member could not be saved. Try again.";
}

function getInstitutionClinicianIntakeErrorMessage(error) {
  const code = String(error?.code || "");
  const message = String(error?.message || "").toLowerCase();
  if (code === "42501" || error?.status === 403 || /permission denied|not authorized|authority required/.test(message)) {
    return "You do not have permission to manage clinician relationships for this Institution.";
  }
  if (code === "23505" || /already exists|duplicate|conflict|already has an institution roster relationship/.test(message)) {
    return "This clinician already has an Institution roster relationship. No duplicate relationship was created.";
  }
  if (/at least one active medical institution|facility assignments/.test(message)) {
    return "Select at least one active Medical Institution before saving the clinician relationship.";
  }
  return "The clinician relationship could not be saved. Review the required details and try again.";
}

function MedicalInstitutionMemberRow({ id, workspace, admins = [], onManageAdmins, editable = true, actionRef, labels = {} }) {
  const creator = medicalInstitutionCreator(workspace);
  const creatorEmail = String(creator.email || "").trim().toLowerCase();
  const additionalAdmins = (Array.isArray(admins) ? admins : []).filter((admin) => String(admin?.email || "").trim().toLowerCase() !== creatorEmail);
  return (
    <section id={id} className="medical-institution-list-section medical-institution-section--divider" data-node-id="61169:104908">
      <h2 className="text-title-medium text-on-surface-variant">{labels.member || "Member"}</h2>
      <div className="medical-institution-member-row">
        <InstitutionIcon src={ICONS.facilityMember} size={24} useMask />
        <div><strong className="text-body-large">{creator.name}</strong>{creator.email ? <small className="text-label-medium">{creator.email}</small> : null}<StatusPill tone="success">{labels.accountableCreator || "Accountable creator"}</StatusPill></div>
        {editable ? <Button ref={actionRef} type="button" variant="ghost" size="sm" onClick={onManageAdmins}>{additionalAdmins.length ? (labels.edit || "Edit") : (labels.addMember || "+ Add member")}</Button> : null}
      </div>
      {additionalAdmins.map((admin) => <div className="medical-institution-member-row medical-institution-member-row--additional" key={admin.id || admin.userId || admin.email}><InstitutionIcon src={ICONS.facilityMember} size={24} useMask /><div><strong className="text-body-large">{admin.name}</strong>{admin.email ? <small className="text-body-medium">{admin.email}</small> : null}<StatusPill tone={admin.status === "pending" ? "warning" : "success"}>{admin.status === "pending" ? `Invitation pending · ${facilityMemberRoleLabel(admin.role, admin.customRoleLabel)}` : facilityMemberRoleLabel(admin.role, admin.customRoleLabel)}</StatusPill></div></div>)}
    </section>
  );
}

function MedicalInstitutionSpecialties({ specialties = [], selectedIds = [], customSpecialties = [], onChange, onCustomChange, editable = true, loading = false, error = false, onRetry, heading = "Specialties", className = "", menuId = "medical-institution-specialty-menu", searchPlaceholder = "Search or add", addLabel = "Add", addIconSrc = "", clearSearch = false, clearSearchLabel = "Clear search", initialOpen = false, mobileSheetTitle = "", helperText = "" }) {
  const [open, setOpen] = useState(initialOpen);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const rootRef = useRef(null);
  const addButtonRef = useRef(null);
  const searchInputRef = useRef(null);
  const mobileSheetPresentation = useMobileSheetPresentation();
  const { catalogue, selected, selectedIds: validSelectedIds, results, allAvailableSelected, canAddCustom, customLabel } = useMemo(
    () => getMedicalInstitutionSpecialtyState({ specialties, selectedIds, customSpecialties, query }),
    [specialties, selectedIds, customSpecialties, query],
  );
  const closeMenu = (restoreFocus = false) => {
    setOpen(false);
    setQuery("");
    setActiveIndex(0);
    if (restoreFocus) window.requestAnimationFrame(() => addButtonRef.current?.focus?.());
  };
  useEffect(() => {
    if (!open) return undefined;
    const focusFrame = window.requestAnimationFrame(() => searchInputRef.current?.focus?.());
    const onPointer = (event) => { if (!mobileSheetPresentation && !rootRef.current?.contains(event.target)) closeMenu(true); };
    const onKey = (event) => { if (event.key === "Escape") { event.preventDefault(); closeMenu(true); } };
    document.addEventListener("pointerdown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => { window.cancelAnimationFrame(focusFrame); document.removeEventListener("pointerdown", onPointer); document.removeEventListener("keydown", onKey); };
  }, [open]);
  useEffect(() => { if (activeIndex >= results.length) setActiveIndex(0); }, [activeIndex, results.length]);
  const remove = (specialty) => {
    if (specialty.source === "user_added") onCustomChange?.(removeMedicalInstitutionCustomSpecialty(customSpecialties, specialty.id, catalogue));
    else onChange?.(removeMedicalInstitutionSpecialtyId(validSelectedIds, specialty.id, catalogue));
  };
  const add = (id) => {
    onChange?.(addMedicalInstitutionSpecialtyId(validSelectedIds, id, catalogue));
    setQuery("");
    setActiveIndex(0);
    window.requestAnimationFrame(() => searchInputRef.current?.focus?.());
  };
  const addCustom = () => {
    if (!canAddCustom) return;
    onCustomChange?.(addMedicalInstitutionCustomSpecialty(customSpecialties, customLabel, catalogue));
    setQuery(""); setActiveIndex(0);
    window.requestAnimationFrame(() => searchInputRef.current?.focus?.());
  };
  const handleSearchKeyDown = (event) => {
    if (event.key === "ArrowDown") { event.preventDefault(); setActiveIndex((current) => results.length ? (current + 1) % results.length : 0); }
    if (event.key === "ArrowUp") { event.preventDefault(); setActiveIndex((current) => results.length ? (current - 1 + results.length) % results.length : 0); }
    if (event.key === "Enter") { event.preventDefault(); if (results[activeIndex]) add(results[activeIndex].id); else if (canAddCustom) addCustom(); }
    if (event.key === "Escape") { event.preventDefault(); closeMenu(true); }
  };
  const menuContents = <div id={menuId} className="institutions-menu-surface medical-institution-specialty-menu" role="dialog" aria-label={searchPlaceholder}>
    {helperText ? <p className="medical-institution-specialty-helper">{helperText}</p> : null}
    <label><Search size={20} aria-hidden="true" /><input ref={searchInputRef} value={query} onChange={(event) => { setQuery(event.target.value); setActiveIndex(0); }} onKeyDown={handleSearchKeyDown} placeholder={searchPlaceholder} aria-controls={`${menuId}-results`} aria-activedescendant={results[activeIndex] ? `${menuId}-${results[activeIndex].id}` : undefined} />{clearSearch && query ? <button type="button" className="medical-institution-specialty-search-clear" aria-label={clearSearchLabel} onMouseDown={(event) => event.preventDefault()} onClick={() => { setQuery(""); setActiveIndex(0); window.requestAnimationFrame(() => searchInputRef.current?.focus?.()); }}><img src="/icons/doctor/cancel.svg" alt="" /></button> : null}</label>
    <div id={`${menuId}-results`} className="medical-institution-specialty-results" role="listbox" aria-label="Search results">
      {loading ? <p role="status">Loading specialties…</p> : error ? <p>Specialties could not be loaded. {onRetry ? <button type="button" onClick={onRetry}>Try again.</button> : null}</p> : <>{results.map((specialty, index) => <button id={`${menuId}-${specialty.id}`} key={specialty.id} type="button" role="option" aria-selected={index === activeIndex} className={index === activeIndex ? "is-selected" : ""} onMouseEnter={() => setActiveIndex(index)} onClick={() => add(specialty.id)}>{specialty.name}</button>)}{canAddCustom ? <button type="button" className="medical-institution-specialty-custom-add" onClick={addCustom}><Plus size={18} aria-hidden="true" />{addLabel} &quot;{customLabel}&quot;</button> : null}{query.trim() && !results.length && !canAddCustom ? <p>{allAvailableSelected ? "No more specialties available." : "No matching specialties."}</p> : null}</>}
    </div>
  </div>;
  return (
    <section className={`medical-institution-specialties ${className}`.trim()} data-node-id="61169:104683" ref={rootRef}>
      {heading ? <h2 className="text-title-medium text-on-surface-variant">{heading}</h2> : null}
      <div className="medical-institution-chip-row">
        {selected.map((specialty) => <FocusChip key={specialty.id} label={specialty.name} removable={editable} onRemove={() => remove(specialty)} />)}
        {editable ? <button ref={addButtonRef} type="button" className="medical-institution-add-chip" aria-expanded={open} aria-controls={menuId} onClick={() => (open ? closeMenu(true) : setOpen(true))}>{addIconSrc ? <InstitutionIcon src={addIconSrc} size={18} /> : <Plus size={18} aria-hidden="true" />}{addLabel}</button> : null}
      </div>
      {open ? mobileSheetPresentation && mobileSheetTitle ? <InstitutionSideSheet open title={mobileSheetTitle} onClose={() => closeMenu(true)} labelledBy={`${menuId}-title`} returnFocusRef={addButtonRef}><div className="institution-mobile-specialty-picker">{menuContents}</div></InstitutionSideSheet> : menuContents : null}
    </section>
  );
}

function MedicalInstitutionAvailabilityRow({ openingHours, onEdit, editable = true, labels = {} }) {
  const summary = formatOpeningHoursSummary(openingHours, labels);
  return (
    <section className="medical-institution-list-section medical-institution-section--divider" data-node-id="61169:104295">
      <h2 className="text-title-medium text-on-surface-variant">{labels.availability || "Availability"}</h2>
      <div className="medical-institution-availability-row">
        <InstitutionIcon src={ICONS.facilityAvailability} size={24} useMask />
        <div><strong className="text-body-large">{labels.openingHours || "Opening hours"}</strong><small className="text-body-medium">{summary.days}</small><small className="text-body-medium">{summary.time}</small></div>
        {editable ? <Button type="button" variant="ghost" size="sm" onClick={onEdit}>{labels.edit || "Edit"}</Button> : null}
      </div>
    </section>
  );
}

function MedicalInstitutionMemberRolePicker({ value, customRoleLabel = "", onChange, onCustomRole, labels = {} }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => { if (!rootRef.current?.contains(event.target)) setOpen(false); };
    const escape = (event) => { if (event.key === "Escape") setOpen(false); };
    document.addEventListener("pointerdown", close); document.addEventListener("keydown", escape);
    return () => { document.removeEventListener("pointerdown", close); document.removeEventListener("keydown", escape); };
  }, [open]);
  const roleLabel = (role, custom = "") => role === "custom_staff" ? (custom || labels.customStaff || "Custom staff") : (labels.roles?.[role] || facilityMemberRoleLabel(role));
  const label = roleLabel(value, customRoleLabel);
  return <div ref={rootRef} className="medical-institution-type-picker"><TextField56 as="button" label={labels.role || "Role"} value={label} tone="surface" leadingSlot={<InstitutionIcon src={ICONS.facilityRole} size={18} useMask />} trailingSlot={<ChevronDown size={18} />} triggerAriaHaspopup="listbox" triggerAriaExpanded={open} onTriggerClick={() => setOpen((current) => !current)} />{open ? <div className="institutions-menu-surface medical-institution-type-menu" role="listbox" aria-label={labels.facilityMemberRole || "Facility member role"}>{FACILITY_MEMBER_ROLE_OPTIONS.map((option) => <button type="button" key={option.value} role="option" aria-selected={value === option.value} className={value === option.value ? "is-selected" : ""} onClick={() => { onChange(option.value); setOpen(false); }}><InstitutionIcon src={ICONS.facilityRole} size={18} useMask />{roleLabel(option.value)}</button>)}<button type="button" key={CLINICIAN_INTAKE_ROLE_OPTION.value} role="option" aria-selected={value === CLINICIAN_INTAKE_ROLE_OPTION.value} className={value === CLINICIAN_INTAKE_ROLE_OPTION.value ? "is-selected" : ""} onClick={() => { onChange(CLINICIAN_INTAKE_ROLE_OPTION.value); setOpen(false); }}><InstitutionIcon src={ICONS.clinicians} fallback={Stethoscope} size={18} useMask />{roleLabel(CLINICIAN_INTAKE_ROLE_OPTION.value)}</button><button type="button" className="medical-institution-role-add" onClick={() => { setOpen(false); onCustomRole?.(); }}><Plus size={18} aria-hidden="true" />{labels.addRole || "Add role"}</button></div> : null}</div>;
}

function MedicalInstitutionAdminSheet({ open, workspace, institutionId, stagedAdmins = [], onSave, onClose, returnFocusRef, pending: externallyPending = false }) {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const c = useCallback((key, fallback, values) => t(`institutionMedicalInstitutionCreate.memberSheet.${key}`, fallback, values), [t]);
  const creator = medicalInstitutionCreator(workspace);
  const [mode, setMode] = useState("search");
  const [query, setQuery] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const [newUser, setNewUser] = useState({ fullName: "", email: "", phone: "", phoneCc: "+27", role: "admin", customRoleLabel: "" });
  useEffect(() => {
    if (!open) return undefined;
    let alive = true; const timer = window.setTimeout(() => {
      setPending(true); setError("");
      searchMedicalInstitutionAdminCandidates({ institutionId, query }).then((items) => { if (alive) setCandidates(items); }).catch(() => { if (alive) setError(c("errors.candidates", "Member candidates could not be loaded. Try again.")); }).finally(() => { if (alive) setPending(false); });
    }, query ? 180 : 0);
    return () => { alive = false; window.clearTimeout(timer); };
  }, [open, institutionId, query]);
  useEffect(() => { if (open) { setMode("search"); setQuery(""); setSelected(stagedAdmins.find((admin) => admin?.userId) || null); setError(""); } }, [open, stagedAdmins]);
  const close = () => { if (externallyPending) return; setMode("search"); setQuery(""); setSelected(null); setError(""); onClose?.(); };
  const clinicianHandoff = () => {
    if (externallyPending) return;
    close();
    navigate(clinicianPath(institutionId, "new"));
  };
  const submit = async () => {
    try {
      if (mode === "new") {
        const fullName = newUser.fullName.trim(); const email = newUser.email.trim().toLowerCase();
        if (newUser.role === "clinician") { clinicianHandoff(); return; }
        if (!fullName || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { setError(c("errors.nameAndEmail", "Name and a valid email are required.")); return; }
        if (newUser.role === "custom_staff" && !newUser.customRoleLabel.trim()) { setError(c("errors.customRole", "Enter a custom role name.")); return; }
        await Promise.resolve(onSave?.({ kind: "invitation", invitation: { fullName, email, phone: `${newUser.phoneCc}${newUser.phone}`.trim(), role: newUser.role, customRoleLabel: newUser.customRoleLabel.trim() } })); close(); return;
      }
      if (!selected) { setError(c("errors.selectMember", "Select an eligible member or add a new member.")); return; }
      await Promise.resolve(onSave?.({ kind: "existing", candidate: selected, role: selected.role || "admin", customRoleLabel: selected.customRoleLabel || "" })); close();
    } catch (err) {
      console.error("Medical Institution facility member save failed", err);
      setError(c("errors.save", "The member could not be saved. Please try again."));
    }
  };
  const clinicianSelected = mode === "new" && newUser.role === "clinician";
  const roleLabels = { role: c("role", "Role"), facilityMemberRole: c("facilityMemberRole", "Facility member role"), addRole: c("addRole", "Add role"), customStaff: c("roles.customStaff", "Custom staff"), roles: Object.fromEntries([...FACILITY_MEMBER_ROLE_OPTIONS, CLINICIAN_INTAKE_ROLE_OPTION].map((option) => [option.value, c(`roles.${option.value}`, option.label)])) };
  const footer = mode === "new" ? <><Button type="button" variant="ghost" onClick={close} disabled={pending || externallyPending}>{c("cancel", "Cancel")}</Button><Button type="button" variant="default" onClick={clinicianSelected ? clinicianHandoff : submit} disabled={pending || externallyPending}>{clinicianSelected ? c("addThroughClinicians", "Add through Clinicians") : c("addMember", "Add member")}</Button></> : <><Button type="button" variant="ghost" onClick={close} disabled={pending || externallyPending}>{c("cancel", "Cancel")}</Button><Button type="button" variant="default" onClick={submit} disabled={pending || externallyPending}>{c("save", "Save")}</Button></>;
  return <InstitutionSideSheet open={open} title={mode === "new" ? c("addMember", "Add member") : c("members", "Members")} iconSrc={ICONS.facilityMember} onClose={close} pending={externallyPending} labelledBy="medical-institution-admin-sheet" titleRow={mode === "new" ? "compact" : "member"} returnFocusRef={returnFocusRef} footer={footer}>
    {mode === "search" ? <div className="medical-institution-admin-sheet">
      <div className="medical-institution-sheet-member"><InstitutionIcon src={ICONS.facilityMember} size={24} useMask /><div><strong className="text-body-large">{creator.name}</strong>{creator.email ? <span>{creator.email}</span> : null}<StatusPill tone="success">{c("accountableCreator", "Accountable creator")}</StatusPill></div></div>
      <TextField56 label={c("member", "Member")} value={query} onChange={setQuery} tone="surface" leadingSlot={<Search size={18} />} />
      <div className="medical-institution-admin-candidates" role="listbox" aria-label={c("eligibleMembers", "Eligible members")}><button type="button" className="medical-institution-admin-add-member" onClick={() => { setMode("new"); setError(""); }}><Plus size={20} aria-hidden="true" /><span>{c("addMember", "Add Member")}</span></button>{pending ? <p>{c("loadingCandidates", "Loading eligible members…")}</p> : candidates.filter((candidate) => String(candidate?.email || "").trim().toLowerCase() !== String(creator.email || "").trim().toLowerCase()).map((candidate) => <button type="button" key={candidate.userId} role="option" aria-selected={selected?.userId === candidate.userId} className={selected?.userId === candidate.userId ? "is-selected" : ""} onClick={() => setSelected(candidate)}><InstitutionIcon src={ICONS.facilityMember} size={20} useMask /><span><strong className="text-body-large">{candidate.name}</strong><small>{[candidate.phone, candidate.email].filter(Boolean).join(" · ")}</small></span></button>)}{!pending && !candidates.filter((candidate) => String(candidate?.email || "").trim().toLowerCase() !== String(creator.email || "").trim().toLowerCase()).length ? <p>{c("noCandidates", "No eligible members match this search.")}</p> : null}</div>
      {error ? <p className="medical-institution-sheet-error" role="alert">{error}</p> : null}
    </div> : <div className="medical-institution-admin-sheet"><button type="button" className="medical-institution-sheet-back" onClick={() => { setMode("search"); setError(""); }} aria-label={c("backToMembers", "Back to Members")}><InstitutionIcon src={ICONS.facilityChevronBack} size={18} useMask />{c("members", "Members")}</button><TextField56 label={c("fullName", "Full name")} value={newUser.fullName} onChange={(fullName) => setNewUser((value) => ({ ...value, fullName }))} tone="surface" leadingSlot={<InstitutionIcon src={ICONS.facilityPerson} size={18} useMask />} /><TextField56 label={c("email", "Email")} value={newUser.email} onChange={(email) => setNewUser((value) => ({ ...value, email }))} tone="surface" leadingSlot={<InstitutionIcon src={ICONS.facilityMail} size={18} useMask />} /><InputPhoneSADC value={newUser.phone} onChange={(phone) => setNewUser((value) => ({ ...value, phone }))} cc={newUser.phoneCc} onChangeCc={(phoneCc) => setNewUser((value) => ({ ...value, phoneCc }))} tone="surface" countryCodeLabel={c("phone", "Phone")} numberLabel={c("phoneNumber", "Phone number")} leadingIcon={ICONS.facilityCall} /><MedicalInstitutionMemberRolePicker value={newUser.role} customRoleLabel={newUser.customRoleLabel} labels={roleLabels} onChange={(role) => setNewUser((value) => ({ ...value, role, customRoleLabel: role === "custom_staff" ? value.customRoleLabel : "" }))} onCustomRole={() => setNewUser((value) => ({ ...value, role: "custom_staff" }))} />{clinicianSelected ? <div className="medical-institution-clinician-handoff" role="status"><p className="text-body-medium text-on-surface">{c("clinicianHandoff", "Clinicians are linked through the governed Institution Clinicians workflow.")}</p><p className="text-body-medium text-on-surface-variant">{c("clinicianHandoffSupport", "Continue there to create or link the clinician and assign an authorised Medical Institution facility.")}</p></div> : null}{newUser.role === "custom_staff" ? <TextField56 label={c("roleName", "Role name")} value={newUser.customRoleLabel} onChange={(customRoleLabel) => setNewUser((value) => ({ ...value, customRoleLabel }))} tone="surface" leadingSlot={<InstitutionIcon src={ICONS.facilityRole} size={18} useMask />} /> : null}{error ? <p className="medical-institution-sheet-error" role="alert">{error}</p> : null}</div>}
  </InstitutionSideSheet>;
}

function MedicalInstitutionAvailabilitySheet({ open, value, onCancel, onSave, returnFocusRef, labels = {} }) {
  const [draft, setDraft] = useState(() => normaliseOpeningHours(value));
  const [error, setError] = useState("");
  const [activeTimeField, setActiveTimeField] = useState(null);
  const timeTriggerRefs = useRef({});
  useEffect(() => { if (open) { setDraft(normaliseOpeningHours(value)); setError(""); setActiveTimeField(null); } }, [open, value]);
  const updateDay = (dayKey, patch) => setDraft((current) => ({
    ...current,
    [dayKey]: { ...current[dayKey], ...patch },
  }));
  const activeDay = activeTimeField ? draft[activeTimeField.dayKey] : null;
  const dayLabel = (day) => labels.days?.[day.key] || day.label;
  const activeDayLabel = activeTimeField ? dayLabel(AVAILABILITY_DAYS.find((day) => day.key === activeTimeField.dayKey) || {}) : "";
  const closePicker = () => setActiveTimeField(null);
  const save = () => {
    const normalized = normaliseOpeningHours(draft);
    if (!normalized.isOpen24Hours) {
      for (const dayDefinition of AVAILABILITY_DAYS) {
        const { key } = dayDefinition;
        const label = dayLabel(dayDefinition);
        const day = normalized[key];
        if (!day.enabled) continue;
        if (!day.opens || !day.closes) { setError((labels.needsOpeningAndClosingTime || "{day} needs an opening and closing time.").replace("{day}", label)); return; }
        if (day.opens >= day.closes) { setError(labels.overnightUnsupported || "Overnight opening hours are not supported in this phase."); return; }
      }
    }
    setError("");
    onSave?.(normalized);
  };
  return <InstitutionSideSheet open={open} title={labels.title || "Availability"} iconSrc={ICONS.facilityAvailability} onClose={onCancel} labelledBy="medical-institution-availability-sheet" variant="availability" returnFocusRef={returnFocusRef} footer={<><Button type="button" variant="default" onClick={save}>{labels.save || "Save"}</Button><Button type="button" variant="ghost" onClick={onCancel}>{labels.cancel || "Cancel"}</Button></>}>
    <div className="medical-institution-availability-sheet">
      <div className="medical-institution-open-24-hours">
        <div><strong className="text-body-large">{labels.open24Hours || "Open 24 hours"}</strong><span className="text-body-small">{labels.openContinuously || "Open continuously every day"}</span></div>
        <AfriSwitch variant="toggle" value={draft.isOpen24Hours ? "on" : "off"} onChange={(next) => { setDraft((current) => ({ ...current, isOpen24Hours: next === "on" })); closePicker(); }} ariaLabel={draft.isOpen24Hours ? (labels.disableOpen24Hours || "Disable open 24 hours") : (labels.enableOpen24Hours || "Enable open 24 hours")} />
      </div>
      {draft.isOpen24Hours ? <p className="medical-institution-open-24-hours-help">{labels.open24HoursHelp || "Individual opening hours are not used while the facility is open 24 hours."}</p> : null}
      <div className="medical-institution-availability-days" role="group" aria-label={labels.weekdays || "Weekdays"}>
        {AVAILABILITY_DAYS.map((dayDefinition) => {
          const { key } = dayDefinition;
          const label = dayLabel(dayDefinition);
          const day = draft[key] || { enabled: false, opens: null, closes: null };
          return <div className={`medical-institution-availability-day-row${draft.isOpen24Hours ? " is-subordinate" : ""}`} key={key}>
            <AfriSwitch variant="toggle" value={day.enabled ? "on" : "off"} disabled={draft.isOpen24Hours} onChange={(next) => { const enabled = next === "on"; updateDay(key, { enabled }); if (!enabled && activeTimeField?.dayKey === key) closePicker(); }} ariaLabel={(day.enabled ? (labels.closeDay || "Close {day}") : (labels.openDay || "Open {day}")).replace("{day}", label)} />
            <span className="text-body-large">{label}</span>
            {day.enabled ? <>
              <button ref={(node) => { timeTriggerRefs.current[`${key}-opens`] = node; }} type="button" disabled={draft.isOpen24Hours} className="afri-time-picker__trigger" onClick={() => setActiveTimeField({ dayKey: key, field: "opens" })} aria-label={(labels.chooseOpeningTime || "Choose {day} opening time").replace("{day}", label.toLowerCase())} aria-haspopup="dialog" aria-expanded={activeTimeField?.dayKey === key && activeTimeField?.field === "opens"}><span>{formatAfriTime(day.opens)}</span></button>
              <span className="medical-institution-availability-separator" aria-hidden="true">–</span>
              <button ref={(node) => { timeTriggerRefs.current[`${key}-closes`] = node; }} type="button" disabled={draft.isOpen24Hours} className="afri-time-picker__trigger" onClick={() => setActiveTimeField({ dayKey: key, field: "closes" })} aria-label={(labels.chooseClosingTime || "Choose {day} closing time").replace("{day}", label.toLowerCase())} aria-haspopup="dialog" aria-expanded={activeTimeField?.dayKey === key && activeTimeField?.field === "closes"}><span>{formatAfriTime(day.closes)}</span></button>
            </> : null}
          </div>;
        })}
      </div>
      {activeTimeField && activeDay ? <AfriTimePickerPanel key={`${activeTimeField.dayKey}-${activeTimeField.field}`} inline label={(activeTimeField.field === "opens" ? (labels.openingTime || "{day} opening time") : (labels.closingTime || "{day} closing time")).replace("{day}", activeDayLabel)} headline={(activeTimeField.field === "opens" ? (labels.opensAt || "On {day}, opens at") : (labels.closesAt || "On {day}, closes at")).replace("{day}", activeDayLabel)} value={activeDay[activeTimeField.field] || ""} onChange={(time) => updateDay(activeTimeField.dayKey, { [activeTimeField.field]: time })} onClose={closePicker} returnFocusRef={{ current: timeTriggerRefs.current[`${activeTimeField.dayKey}-${activeTimeField.field}`] }} /> : null}
      {error ? <p className="medical-institution-sheet-error" role="alert">{error}</p> : null}
    </div>
  </InstitutionSideSheet>;
}

function MedicalInstitutionsRegistryView({ workspace }) {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const institutionId = workspace?.activeInstitution?.id || "";
  const [network, setNetwork] = useState(null);
  const [error, setError] = useState("");
  const [retryKey, setRetryKey] = useState(0);
  const [typeFilter, setTypeFilter] = useState("all");
  const [typeFilterOpen, setTypeFilterOpen] = useState(false);
  const [csvOpen, setCsvOpen] = useState(false);
  const typeFilterRef = useRef(null);

  useEffect(() => {
    let active = true;
    setNetwork(null); setError("");
    loadMedicalInstitutionNetwork({ institutionId })
      .then((value) => { if (active) setNetwork(value); })
      .catch((err) => { console.error("Medical Institution registry load failed", err); if (active) setError("load_failed"); });
    return () => { active = false; };
  }, [institutionId, retryKey]);

  const facilities = network?.facilities || [];
  const canonicalFacilityTypes = MEDICAL_INSTITUTION_TYPE_OPTIONS.map((option) => option.value);
  const facilityTypes = [...canonicalFacilityTypes, ...facilities.map((facility) => getFacilityType(facility)).filter((type) => type && !canonicalFacilityTypes.includes(type))];
  const filteredFacilities = typeFilter === "all" ? facilities : facilities.filter((facility) => getFacilityType(facility) === typeFilter);
  const typeLabel = (value) => {
    const option = MEDICAL_INSTITUTION_TYPE_OPTIONS.find((item) => item.value === value);
    if (!option) return value;
    return t(`institutionMedicalInstitutionDetail.types.${MEDICAL_INSTITUTION_TYPE_MESSAGE_KEYS[option.value] || option.value}`, option.label);
  };
  const selectedTypeLabel = typeFilter === "all" ? t("institutionWorkspace.filterAll", "All") : typeLabel(typeFilter);
  useEffect(() => {
    if (!typeFilterOpen) return undefined;
    const closeOnOutsidePointer = (event) => {
      if (!typeFilterRef.current?.contains(event.target)) setTypeFilterOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key !== "Escape") return;
      setTypeFilterOpen(false);
      window.requestAnimationFrame(() => typeFilterRef.current?.querySelector("button")?.focus());
    };
    document.addEventListener("mousedown", closeOnOutsidePointer);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsidePointer);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [typeFilterOpen]);
  useEffect(() => {
    if (typeFilter !== "all" && !facilityTypes.includes(typeFilter)) setTypeFilter("all");
  }, [facilityTypes, typeFilter]);
  const selectTypeFilter = (nextType) => {
    setTypeFilter(nextType);
    setTypeFilterOpen(false);
    window.requestAnimationFrame(() => typeFilterRef.current?.querySelector("button")?.focus());
  };
  const openFacility = (facilityId) => {
    if (!facilityId || !institutionId) return;
    recordInstitutionHomeFacilityView(workspace?.user?.id, { parentInstitutionId: institutionId, facilityId });
    navigate(institutionPath(institutionId, `/${facilityId}`));
  };
  return <PageShell workspace={workspace} title={t("institutionShell.navigation.medicalInstitutions", "Medical Institutions")} icon={Building2} iconSrc={ICONS.pageMedicalInstitutions} pageTopBarTitle={t("institutionShell.navigation.medicalInstitutions", "Medical Institutions")} contentClassName="afrimed-rail afrimed-rail--comfortable pb-24">
    <section className="medical-institution-network" aria-label={t("institutionShell.navigation.medicalInstitutions", "Medical Institutions")} data-node-id="61317:34889">
      <div className="medical-institution-toolbar">
        <div className="medical-institution-toolbar-controls">
          {facilities.length ? (
            <div className="medical-institution-type-filter" ref={typeFilterRef}>
              <button type="button" className="institutions-filter-chip text-label-large" aria-label={t("institutionWorkspace.filterFacilityType", "Facility type filter")} aria-haspopup="listbox" aria-expanded={typeFilterOpen} onClick={() => setTypeFilterOpen((open) => !open)}>{selectedTypeLabel} <InstitutionIcon src={ICONS.expand} fallback={ChevronDown} size={18} /></button>
              {typeFilterOpen ? <div className="institutions-menu-surface medical-institution-type-filter-menu" role="listbox" aria-label={t("institutionWorkspace.filterFacilitiesByType", "Filter facilities by type")}><button type="button" role="option" aria-selected={typeFilter === "all"} onClick={() => selectTypeFilter("all")}>{t("institutionWorkspace.filterAll", "All")}</button>{facilityTypes.map((value) => { const option = MEDICAL_INSTITUTION_TYPE_OPTIONS.find((item) => item.value === value); return <button key={value} type="button" role="option" aria-selected={typeFilter === value} onClick={() => selectTypeFilter(value)}>{option ? <InstitutionIcon src={option.icon} size={18} useMask /> : null}<span>{typeLabel(value)}</span></button>; })}</div> : null}
            </div>
          ) : null}
        </div>
        <div className="medical-institution-toolbar-actions"><InstitutionRegistryCtas onImport={() => setCsvOpen(true)} onCreate={() => navigate(institutionPath(institutionId, "/new"))} createLabel={t("institutionRegistryCtas.addFacility", "Add")} /></div>
      </div>
      {error ? <MedicalInstitutionLoadError onRetry={() => setRetryKey((key) => key + 1)} /> : null}
      {!network && !error ? <NetworkLoadingState /> : network?.pendingMigration ? <MedicalInstitutionLoadError onRetry={() => setRetryKey((key) => key + 1)} /> : null}
      {network && !network.pendingMigration && !facilities.length ? <MedicalInstitutionZeroState onCreate={() => navigate(institutionPath(institutionId, "/new"))} /> : null}
      {network && !network.pendingMigration && facilities.length ? <>{filteredFacilities.length ? <FacilitiesTable rows={filteredFacilities} onOpen={openFacility} t={t} /> : <p className="medical-institution-filter-empty">{t("institutionWorkspace.noFacilitiesMatch", "No Medical Institutions match this type.")}</p>}</> : null}
      {csvOpen ? <GovernedCsvImport title="Import Medical Institutions from CSV" institutionId={institutionId} importType="facility" fields={[{ key: "name", label: "Name", required: true, aliases: ["facility name"] }, { key: "type", label: "Type", required: true, aliases: ["facility type"] }, { key: "address", label: "Address", required: true }, { key: "city", label: "City", required: true }, { key: "country", label: "Country", required: true }, { key: "postalCode", label: "Postal code", aliases: ["zip", "postcode"] }, { key: "phone", label: "Phone" }, { key: "email", label: "Email" }, { key: "website", label: "Website" }, { key: "description", label: "Description" }]} onClose={() => setCsvOpen(false)} /> : null}
    </section>
  </PageShell>;
}

function MedicalInstitutionZeroState({ onCreate }) {
  const { t } = useLanguage();
  return <div className="medical-institution-zero-state" data-node-id="61317:34889"><div><span className="medical-institution-zero-icon"><InstitutionIcon src="/icons/medical-institution/hospital_avatar.svg" fallback={Building2} size={28} /></span><h2 className="text-title-large">{t("institutionWorkspace.facilityNetworkTitle", "Build your medical institution network")}</h2><p className="text-body-large">{t("institutionWorkspace.facilityNetworkBody", "Medical Institutions are the hospitals, clinics, pharmacies, laboratories and other facilities governed by this organisation. Registering them enables future member, service, patient, reporting-readiness and Regional Health facility mapping.")}</p><Button type="button" variant="default" size="large" onClick={onCreate}><Plus size={18} aria-hidden="true" />{t("institutionWorkspace.addMedicalInstitution", "Add new Medical Institution")}</Button></div></div>;
}

function MedicalInstitutionCreateView({ workspace }) {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const c = (key, fallback, values) => t(`institutionMedicalInstitutionCreate.${key}`, fallback, values);
  const institutionId = workspace?.activeInstitution?.id || "";
  const adminButtonRef = useRef(null);
  const availabilityButtonRef = useRef(null);
  const [network, setNetwork] = useState(null);
  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState("");
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [formError, setFormError] = useState("");
  const [retryKey, setRetryKey] = useState(0);
  const [adminOpen, setAdminOpen] = useState(false);
  const [availabilityOpen, setAvailabilityOpen] = useState(false);
  const [form, setForm] = useState({ name: "", type: "", address: "", city: "", postalCode: "", country: "", phone: "", email: "", website: "", description: "", serviceIds: [], specialtyIds: [], customSpecialties: [], additionalMembers: [], pendingMemberInvitations: [], openingHours: normaliseOpeningHours() });

  useEffect(() => { let active = true; setNetwork(null); setLoadError(""); loadMedicalInstitutionNetwork({ institutionId }).then((value) => { if (active) setNetwork(value); }).catch((err) => { console.error("Medical Institution creation load failed", err); if (active) setLoadError("load_failed"); }); return () => { active = false; }; }, [institutionId, retryKey]);
  useEffect(() => () => { if (logoPreview) URL.revokeObjectURL(logoPreview); }, [logoPreview]);
  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const selectLogo = (event) => { const file = event.target.files?.[0] || null; if (!file) return; if (![
    "image/png", "image/jpeg", "image/jpg", "image/webp"].includes(file.type) || file.size > 5 * 1024 * 1024) { setFormError("Use a PNG, JPEG, or WebP logo up to 5MB."); return; } if (logoPreview) URL.revokeObjectURL(logoPreview); setLogo(file); setLogoPreview(URL.createObjectURL(file)); setFormError(""); };
  const canSave = Boolean(form.name.trim() && form.type && form.address.trim() && form.city.trim() && form.country);
  const saveAdminDraft = (draft) => setForm((current) => {
    if (draft.kind === "existing") {
      const member = { ...draft.candidate, role: draft.role || "admin", customRoleLabel: draft.customRoleLabel || "", status: "active" };
      return { ...current, additionalMembers: [...current.additionalMembers.filter((item) => item.userId !== member.userId), member] };
    }
    const invitation = draft.invitation;
    return { ...current, pendingMemberInvitations: [...current.pendingMemberInvitations.filter((item) => String(item.email).toLowerCase() !== String(invitation.email).toLowerCase()), invitation] };
  });
  const submit = async (event) => { event.preventDefault(); if (busy || !canSave) return; setBusy(true); setFormError(""); try { const facility = { ...form, serviceIds: form.serviceIds, specialtyIds: normalizeMedicalInstitutionSpecialtyIds(form.specialtyIds, network?.specialties || []), customSpecialties: normalizeMedicalInstitutionCustomSpecialties(form.customSpecialties, network?.specialties || []).map((item) => item.name), additionalMembers: form.additionalMembers.map((member) => ({ userId: member.userId, role: member.role || "admin", customRoleLabel: member.customRoleLabel || null })), pendingMemberInvitations: form.pendingMemberInvitations }; const result = await createMedicalInstitutionFacility({ institutionId, facility }); if (logo) await uploadMedicalInstitutionFacilityLogo({ institutionId, facilityId: result.facilityId, file: logo }); navigate(institutionPath(institutionId, `/${result.facilityId}`), { replace: true, state: { medicalInstitutionNotice: `${form.name.trim()} was added successfully` } }); } catch (err) { console.error("Medical Institution creation failed", err); setFormError("Medical Institution could not be saved. Please check the details and try again."); } finally { setBusy(false); } };

  return <PageShell workspace={workspace} title={c("navigation", "Medical Institutions")} icon={Building2} iconSrc={ICONS.pageMedicalInstitutions}>
    <form className="medical-institution-create" onSubmit={submit} data-node-id="61169:66505">
        <div className="medical-institution-create-header"><div className="medical-institution-title-row"><MedicalInstitutionBackButton label={c("back", "Back to Medical Institutions")} onClick={() => navigate(institutionPath(institutionId))} /><h1 className="text-headline-small text-on-surface">{c("title", "New Medical Institution")}</h1></div><div className="medical-institution-create-actions"><Button type="button" variant="ghost" onClick={() => navigate(institutionPath(institutionId))}>{c("actions.cancel", "Cancel")}</Button><Button type="submit" variant="default" loading={busy} disabled={!canSave || Boolean(network?.pendingMigration) || Boolean(loadError)}>{c("actions.save", "Save")}</Button></div></div>
        <div className="medical-institution-create-body">
        {loadError ? <MedicalInstitutionLoadError onRetry={() => setRetryKey((key) => key + 1)} /> : null}
        {!network && !loadError ? <NetworkLoadingState /> : network?.pendingMigration ? <MedicalInstitutionLoadError onRetry={() => setRetryKey((key) => key + 1)} /> : <>
          {formError ? <div className="medical-institution-error medical-institution-error--compact" role="alert"><p className="text-body-medium">{formError}</p></div> : null}
          <MedicalInstitutionAvatarControl preview={logoPreview} onSelect={selectLogo} disabled={busy} ariaLabel={c("editLogo", "Edit Medical Institution logo")} />
          <section className="medical-institution-form-section"><h2 className="text-title-medium text-on-surface-variant">{c("sections.basicInformation", "Basic Information")}</h2><div className="medical-institution-form-grid"><TextField56 label={c("fields.institutionName", "Institution Name*")} value={form.name} onChange={(value) => update("name", value)} tone="surface" /><MedicalInstitutionTypePicker value={form.type} onChange={(value) => update("type", value)} label={c("fields.institutionType", "Institution type *")} placeholder={c("fields.selectInstitutionType", "Select institution type")} optionLabel={(option) => c(`types.${MEDICAL_INSTITUTION_TYPE_MESSAGE_KEYS[option.value] || option.value}`, option.label)} /></div></section>
          <section className="medical-institution-form-section"><MedicalInstitutionDescriptionField value={form.description} onChange={(value) => update("description", value)} disabled={busy} label={c("fields.facilityDescription", "Facility description")} help={c("fields.optionalDescription", "Optional concise description")} /></section>
          <MedicalInstitutionMemberRow workspace={workspace} admins={[...form.additionalMembers, ...form.pendingMemberInvitations.map((invitation) => ({ ...invitation, name: invitation.fullName, status: "pending" }))]} onManageAdmins={() => setAdminOpen(true)} actionRef={adminButtonRef} labels={{ member: c("sections.member", "Member"), accountableCreator: c("accountableCreator", "Accountable creator"), addMember: c("actions.addMember", "+ Add member"), edit: c("actions.edit", "Edit") }} />
          <MedicalInstitutionSpecialties specialties={network.specialties || []} selectedIds={form.specialtyIds} customSpecialties={form.customSpecialties} onChange={(value) => update("specialtyIds", value)} onCustomChange={(value) => update("customSpecialties", value)} heading={c("sections.specialties", "Specialties")} searchPlaceholder={c("actions.searchOrAdd", "Search or add")} addLabel={c("actions.add", "Add")} />
          <section className="medical-institution-form-section"><h2 className="text-title-medium text-on-surface-variant">{c("sections.address", "Address")}</h2><div className="medical-institution-form-grid"><TextField56 label={c("fields.address", "Address")} value={form.address} onChange={(value) => update("address", value)} tone="surface" /><TextField56 label={c("fields.zipCode", "ZIP Code")} value={form.postalCode} onChange={(value) => update("postalCode", value)} tone="surface" /><TextField56 label={c("fields.city", "City")} value={form.city} onChange={(value) => update("city", value)} tone="surface" /><SelectCountrySADC label={c("fields.country", "Country")} value={form.country} onChange={(value) => update("country", value)} tone="surface" useTextFieldTrigger /></div></section>
          <MedicalInstitutionAvailabilityRow openingHours={form.openingHours} onEdit={() => setAvailabilityOpen(true)} labels={{ availability: c("sections.availability", "Availability"), openingHours: c("fields.openingHours", "Opening hours"), setupPending: c("setupPending", "Setup pending"), edit: c("actions.edit", "Edit") }} />
          <section className="medical-institution-form-section"><h2 className="text-title-medium text-on-surface-variant">{c("sections.domain", "Domain")}</h2><TextField56 label={c("fields.domain", "Domain")} value={form.website} onChange={(value) => update("website", value)} tone="surface" /></section>
        </>}
        </div>
    </form>
    <MedicalInstitutionAdminSheet open={adminOpen} workspace={workspace} institutionId={institutionId} stagedAdmins={[...form.additionalMembers, ...form.pendingMemberInvitations]} onSave={saveAdminDraft} onClose={() => setAdminOpen(false)} returnFocusRef={adminButtonRef} />
    <MedicalInstitutionAvailabilitySheet open={availabilityOpen} value={form.openingHours} onCancel={() => setAvailabilityOpen(false)} onSave={(value) => { update("openingHours", value); setAvailabilityOpen(false); }} returnFocusRef={availabilityButtonRef} labels={{ title: c("availabilitySheet.title", "Availability"), open24Hours: c("availabilitySheet.open24Hours", "Open 24 hours"), openContinuously: c("availabilitySheet.openContinuously", "Open continuously every day"), weekdays: c("availabilitySheet.weekdays", "Weekdays"), days: Object.fromEntries(AVAILABILITY_DAYS.map((day) => [day.key, c(`availabilitySheet.days.${day.key}`, day.label)])), save: c("actions.save", "Save"), cancel: c("actions.cancel", "Cancel"), enableOpen24Hours: c("availabilitySheet.enableOpen24Hours", "Enable open 24 hours"), disableOpen24Hours: c("availabilitySheet.disableOpen24Hours", "Disable open 24 hours"), needsOpeningAndClosingTime: c("availabilitySheet.needsOpeningAndClosingTime", "{day} needs an opening and closing time."), overnightUnsupported: c("availabilitySheet.overnightUnsupported", "Overnight opening hours are not supported in this phase."), open24HoursHelp: c("availabilitySheet.open24HoursHelp", "Individual opening hours are not used while the facility is open 24 hours."), openDay: c("availabilitySheet.openDay", "Open {day}"), closeDay: c("availabilitySheet.closeDay", "Close {day}"), chooseOpeningTime: c("availabilitySheet.chooseOpeningTime", "Choose {day} opening time"), chooseClosingTime: c("availabilitySheet.chooseClosingTime", "Choose {day} closing time"), openingTime: c("availabilitySheet.openingTime", "{day} opening time"), closingTime: c("availabilitySheet.closingTime", "{day} closing time"), opensAt: c("availabilitySheet.opensAt", "On {day}, opens at"), closesAt: c("availabilitySheet.closesAt", "On {day}, closes at") }} />
  </PageShell>;
}

function MedicalInstitutionDetailView({ workspace, facilityId, settingsMode = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, languageCode } = useLanguage();
  const c = (key, fallback, values) => settingsMode ? fallback : t(`institutionMedicalInstitutionDetail.${key}`, fallback, values);
  const institutionId = workspace?.activeInstitution?.id || "";
  const [network, setNetwork] = useState(null);
  const [form, setForm] = useState(null);
  const [savedForm, setSavedForm] = useState(null);
  const [error, setError] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [logoCropSource, setLogoCropSource] = useState("");
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoMenuOpen, setLogoMenuOpen] = useState(false);
  const [logoRemovalOpen, setLogoRemovalOpen] = useState(false);
  const [coverDisplayUrl, setCoverDisplayUrl] = useState("");
  const [hasFacilityCover, setHasFacilityCover] = useState(false);
  const [coverBackendState, setCoverBackendState] = useState("checking");
  const [coverCropSource, setCoverCropSource] = useState("");
  const [coverUploading, setCoverUploading] = useState(false);
  const [coverMenuOpen, setCoverMenuOpen] = useState(false);
  const [coverRemovalOpen, setCoverRemovalOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [adminBusy, setAdminBusy] = useState(false);
  const [availabilityOpen, setAvailabilityOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(location.state?.medicalInstitutionNotice || "");
  const [refreshKey, setRefreshKey] = useState(0);
  const adminButtonRef = useRef(null);
  const availabilityButtonRef = useRef(null);
  const coverInputRef = useRef(null);
  const coverCameraInputRef = useRef(null);
  const logoInputRef = useRef(null);
  const logoCameraInputRef = useRef(null);
  const mobileCoverActions = useMobileSheetPresentation();
  const hydrateForm = (item) => ({
    name: item.name || "", type: item.type || "", registrationTaxId: item.registrationTaxId || item.registration_tax_id || "", accessibility: item.accessibility || "private", description: item.description || "", address: item.address || "", city: item.city || "", postalCode: item.postalCode || item.postal_code || "", country: item.country || "", website: item.website || item.domain || item.url || "",
    specialtyIds: (item.specialties || []).filter((specialty) => specialty.source !== "user_added").map((specialty) => specialty.id).filter(Boolean),
    customSpecialties: (item.specialties || []).filter((specialty) => specialty.source === "user_added").map((specialty) => ({ id: specialty.id, name: specialty.name })),
    openingHours: normaliseOpeningHours(item.openingHours || item.opening_hours),
  });
  useEffect(() => { let active = true; setError(""); loadMedicalInstitutionNetwork({ institutionId }).then((value) => { if (!active) return; setNetwork(value); const item = (value.facilities || []).find((candidate) => candidate.id === facilityId); if (item) { const nextForm = hydrateForm(item); setForm(nextForm); setSavedForm(nextForm); } }).catch((err) => { if (active) setError(err?.message || "Medical Institution could not load."); }); return () => { active = false; }; }, [institutionId, facilityId, refreshKey]);
  const facility = (network?.facilities || []).find((item) => item.id === facilityId);
  useEffect(() => {
    if (facility?.id && institutionId) {
      recordInstitutionHomeFacilityView(workspace?.user?.id, { parentInstitutionId: institutionId, facilityId: facility.id });
    }
  }, [facility?.id, institutionId, workspace?.user?.id]);
  useEffect(() => { let active = true; if (!facility?.logoPath) { setLogoUrl(""); return undefined; } getMedicalInstitutionFacilityLogoUrl(facility.logoPath).then((url) => { if (active) setLogoUrl(url); }).catch(() => { if (active) setLogoUrl(""); }); return () => { active = false; }; }, [facility?.logoPath]);
  useEffect(() => {
    let active = true;
    if (!facility?.id || !institutionId) { setCoverDisplayUrl(""); setHasFacilityCover(false); setCoverBackendState("unavailable"); return undefined; }
    loadMedicalInstitutionFacilityCoverDisplay({ institutionId, facilityId: facility.id })
      .then((result) => { if (active) { setCoverDisplayUrl(result.coverDisplayUrl); setHasFacilityCover(result.hasCover); setCoverBackendState("available"); } })
      .catch(() => { if (active) { setCoverDisplayUrl(""); setHasFacilityCover(false); setCoverBackendState("unavailable"); } });
    return () => { active = false; };
  }, [facility?.id, institutionId, refreshKey]);
  useEffect(() => { if (location.state?.medicalInstitutionNotice) navigate(location.pathname + location.search, { replace: true, state: {} }); }, [location.pathname, location.search, location.state, navigate]);
  useEffect(() => {
    if (!settingsMode) return undefined;
    const onKeyDown = (event) => { if (event.key === "Escape") navigate(institutionPath(institutionId, `/${facilityId}`)); };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [facilityId, institutionId, navigate, settingsMode]);
  if (!network && !error) return <PageShell workspace={workspace} title="Medical Institutions" icon={Building2} iconSrc={ICONS.pageMedicalInstitutions}><NetworkLoadingState /></PageShell>;
  if (!facility) return <PageShell workspace={workspace} title="Medical Institutions" icon={Building2} iconSrc={ICONS.pageMedicalInstitutions}><div className="medical-institution-error" role="alert">{error || "This Medical Institution is not available in the active parent organisation."}</div><div className="medical-institution-back-action"><MedicalInstitutionBackButton label="Back to Medical Institutions" onClick={() => navigate(institutionPath(institutionId))} /><span>Back to Medical Institutions</span></div></PageShell>;
  const facilityAdmins = [...(facility.members || facility.admins || []), ...(facility.memberInvitations || facility.adminInvitations || []).map((invitation) => ({ ...invitation, status: "pending" }))];
  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const selectSavedLogo = (event) => {
    const file = event.target.files?.[0] || null;
    event.target.value = "";
    if (!file) return;
    if (!["image/png", "image/jpeg", "image/jpg", "image/webp"].includes(file.type) || file.size > 5 * 1024 * 1024) { setError("Use a PNG, JPEG, or WebP logo up to 5MB."); return; }
    const reader = new FileReader();
    reader.onload = () => setLogoCropSource(String(reader.result || ""));
    reader.onerror = () => setError("The Institution logo could not be read. Try another image.");
    reader.readAsDataURL(file);
  };
  const saveCroppedLogo = async (blob) => {
    setLogoCropSource("");
    if (!blob) return;
    setLogoUploading(true);
    setError("");
    try {
      const contentType = blob.type || "image/jpeg";
      const extension = contentType.includes("png") ? "png" : contentType.includes("webp") ? "webp" : "jpg";
      const file = new File([blob], `facility-logo-${Date.now()}.${extension}`, { type: contentType });
      const preview = await new Promise((resolve, reject) => { const reader = new FileReader(); reader.onload = () => resolve(String(reader.result || "")); reader.onerror = reject; reader.readAsDataURL(file); });
      await uploadMedicalInstitutionFacilityLogo({ institutionId, facilityId, file });
      setLogoUrl(preview);
      setNotice("Institution logo was updated successfully");
      setRefreshKey((key) => key + 1);
    } catch (err) {
      console.error("Medical Institution logo update failed", err);
      setError("Institution logo could not be updated. Please try again.");
    } finally {
      setLogoUploading(false);
    }
  };
  const removeFacilityLogo = async () => {
    setLogoRemovalOpen(false);
    setLogoUploading(true);
    setError("");
    try {
      await removeMedicalInstitutionFacilityLogo({ institutionId, facilityId, logoPath: facility.logoPath });
      setLogoUrl("");
      setNotice("Institution logo was removed");
      setRefreshKey((key) => key + 1);
    } catch (err) {
      console.error("Medical Institution logo removal failed", err);
      setError("Institution logo could not be removed. Please try again.");
    } finally {
      setLogoUploading(false);
    }
  };
  const canManageFacilityCover = [workspace?.scopedRole, ...(workspace?.userRoles || []), ...(workspace?.memberships || [])
    .filter((membership) => String(membership?.institution_id || membership?.institutionId || "") === institutionId)
    .map((membership) => membership?.role)]
    .map((role) => String(role || "").trim().toLowerCase())
    .some((role) => ["owner", "admin", "super_admin", "moh_admin", "institution_admin", "facility_admin"].includes(role));
  const selectFacilityCover = (event) => {
    const file = event.target.files?.[0] || null;
    event.target.value = "";
    if (!file) return;
    if (!["image/png", "image/jpeg", "image/jpg", "image/webp"].includes(file.type) || file.size > 5 * 1024 * 1024) {
      setError("Use a PNG, JPEG, or WebP cover up to 5MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setCoverCropSource(String(reader.result || ""));
    reader.onerror = () => setError("The Institution cover could not be read. Try another image.");
    reader.readAsDataURL(file);
  };
  const saveCroppedCover = async (blob) => {
    setCoverCropSource("");
    if (!blob) return;
    setCoverUploading(true);
    setError("");
    try {
      const contentType = blob.type || "image/jpeg";
      const extension = contentType.includes("png") ? "png" : contentType.includes("webp") ? "webp" : "jpg";
      const file = new File([blob], `facility-cover-${Date.now()}.${extension}`, { type: contentType });
      const nextUrl = await uploadMedicalInstitutionFacilityCover({ institutionId, facilityId, file });
      setCoverDisplayUrl(nextUrl);
      setHasFacilityCover(true);
      setNotice("Institution cover was updated successfully");
      setRefreshKey((key) => key + 1);
    } catch (err) {
      console.error("Medical Institution cover update failed", err);
      setCoverBackendState("unavailable");
      setError("Institution cover management is unavailable until the protected media service is released.");
    } finally {
      setCoverUploading(false);
    }
  };
  const removeFacilityCover = async () => {
    setCoverRemovalOpen(false);
    setCoverUploading(true);
    setError("");
    try {
      await removeMedicalInstitutionFacilityCover({ institutionId, facilityId });
      setCoverDisplayUrl("");
      setHasFacilityCover(false);
      setNotice("Institution cover was removed");
      setRefreshKey((key) => key + 1);
    } catch (err) {
      console.error("Medical Institution cover removal failed", err);
      setCoverBackendState("unavailable");
      setError("Institution cover management is unavailable until the protected media service is released.");
    } finally {
      setCoverUploading(false);
    }
  };
  const isDirty = Boolean(form && savedForm && JSON.stringify(form) !== JSON.stringify(savedForm));
  const canSave = Boolean(isDirty && form?.name.trim() && form?.type && form?.address.trim() && form?.city.trim() && form?.country);
  const saveFacilityMember = async (draft) => {
    setAdminBusy(true);
    setError("");
    const role = draft?.role || draft?.invitation?.role || "admin";
    try {
      if (role === "clinician") {
        throw new Error("Clinicians are linked through the governed Institution Clinicians workflow.");
      }
      if (draft.kind === "existing") await assignMedicalInstitutionFacilityMember({ institutionId, facilityId, userId: draft.candidate.userId, role: draft.role || "admin", customRoleLabel: draft.customRoleLabel || "" });
      else await createMedicalInstitutionFacilityMemberInvitation({ institutionId, facilityId, fullName: draft.invitation.fullName, email: draft.invitation.email, phone: draft.invitation.phone, role: draft.invitation.role || "admin", customRoleLabel: draft.invitation.customRoleLabel || "" });
      setAdminOpen(false);
      setRefreshKey((key) => key + 1);
    } catch (err) {
      console.error("Medical Institution facility member save failed", { code: err?.code || "", message: err?.message || "" });
      setError(getMedicalInstitutionFacilityMemberErrorMessage(err, role));
      throw err;
    } finally {
      setAdminBusy(false);
    }
  };
  const save = async (event) => {
    event.preventDefault(); if (busy || !canSave) return; setBusy(true); setError("");
    try {
      await updateMedicalInstitutionFacility({ institutionId, facilityId, facility: {
        ...form,
        specialtyIds: normalizeMedicalInstitutionSpecialtyIds(form.specialtyIds, network?.specialties || []),
        customSpecialties: normalizeMedicalInstitutionCustomSpecialties(form.customSpecialties, network?.specialties || []).map((item) => item.name),
      } });
      setSavedForm(form); setNotice(`${form.name.trim()} was updated successfully`); setRefreshKey((key) => key + 1);
    } catch (err) { console.error(`Medical Institution update failed [${err?.code || "unknown"}]: ${err?.message || "Unknown error"}${err?.details ? ` — ${err.details}` : ""}`); setError("Medical Institution could not be updated. Please check the details and try again."); }
    finally { setBusy(false); }
  };
  const cancel = () => { setForm(savedForm || hydrateForm(facility)); setError(""); };
  const typeLabel = (option) => c(`types.${MEDICAL_INSTITUTION_TYPE_MESSAGE_KEYS[option.value] || option.value}`, option.label);
  const ownershipLabel = (option) => c(`ownership.${option.value}`, option.label);
  const detailLabels = {
    member: c("sections.member", "Member"),
    accountableCreator: c("member.accountableCreator", "Accountable creator"),
    edit: c("actions.edit", "Edit"),
    addMember: c("actions.addMember", "+ Add member"),
    availability: c("sections.availability", "Availability"),
    openingHours: c("availability.openingHours", "Opening hours"),
    openEveryDay: c("availability.openEveryDay", "Open 24 hours · Every day"),
    setupPending: c("availability.setupPending", "Setup pending"),
    allDays: c("availability.allDays", "S,M,T,W,T,F,S"),
    weekdays: c("availability.weekdays", "Mon – Fri"),
    daysConfigured: (count) => c("availability.daysConfigured", "{count} days configured", { count }),
    dayShorts: {
      sunday: c("availability.days.sunday", "S"), monday: c("availability.days.monday", "M"), tuesday: c("availability.days.tuesday", "T"), wednesday: c("availability.days.wednesday", "W"), thursday: c("availability.days.thursday", "T"), friday: c("availability.days.friday", "F"), saturday: c("availability.days.saturday", "S"),
    },
    timeLocale: !settingsMode && languageCode === "fr" ? "fr-FR" : "",
  };
  const openLogoMediaSheet = () => {
    if (!busy && !logoUploading) setLogoMenuOpen(true);
  };
  const facilityAvatar = <AvatarHeaderPositioned
    previewUrl={logoUrl}
    alt={`${form?.name || facility.name} logo`}
    interactive={false}
    showEditChip={false}
    railClassName="afrimed-page"
  >
    <div className="afrimed-profile-avatar institution-facility-cover-avatar pointer-events-auto h-20 w-20 md:h-[150px] md:w-[150px]" role="group" aria-label={c("media.logo", "Medical Institution logo")}>
      {logoUrl ? <img src={logoUrl} alt="" className="object-contain" /> : <img src={`${MI_ICON_ROOT}/hospital_avatar.svg`} alt="" className="institution-facility-avatar-fallback-image" aria-hidden="true" />}
      {canManageFacilityCover ? <button type="button" className="absolute left-[50px] top-[50px] z-10 h-11 w-11 p-0 border-0 bg-transparent md:left-[120px] md:top-[120px]" onClick={openLogoMediaSheet} disabled={busy || logoUploading} aria-label={c("media.editLogo", "Edit Medical Institution logo")}>
        <img src={ICONS.facilityEdit} alt="" className="h-full w-full" draggable="false" />
      </button> : null}
    </div>
  </AvatarHeaderPositioned>;
  return <PageShell workspace={workspace} title={c("title", "Medical Institutions")} icon={Building2} iconSrc={ICONS.pageMedicalInstitutions} profileDetail={!settingsMode} contentClassName={settingsMode ? undefined : "afrimed-rail pb-24"}>
    <form className={`medical-institution-create${settingsMode ? " medical-institution-settings-general" : ""}`} data-node-id="61277:45316" onSubmit={save}>
      {settingsMode ? <div className="medical-institution-create-header"><div className="medical-institution-title-row"><MedicalInstitutionBackButton label="Back to Medical Institution" onClick={() => navigate(institutionPath(institutionId, `/${facilityId}`))} /><h1 className="text-headline-small text-on-surface">Settings</h1></div><div className="medical-institution-create-actions"><Button type="button" variant="ghost" onClick={cancel} disabled={busy || !isDirty}>Cancel</Button><Button type="submit" variant="default" loading={busy} disabled={!canSave}>Save</Button></div></div> : <>
        <InstitutionProfileCover src={coverDisplayUrl} fallbackSrc="/icons/doctor/EditIntro_banner.svg" avatarContent={facilityAvatar}>
          <div className="mt-16 md:mt-20 lg:mt-24 w-full flex items-center justify-between pointer-events-auto">
            <MedicalInstitutionBackButton label={c("actions.back", "Back to Medical Institutions")} onClick={() => navigate(institutionPath(institutionId))} />
            <div className="institution-profile-cover-actions flex items-center gap-3">
              {canManageFacilityCover && !mobileCoverActions ? <div className="institution-clinician-overflow-wrap"><button type="button" className="institutions-icon-button institution-clinician-more-button" aria-label={c("media.moreCoverActions", "More Institution cover actions")} aria-expanded={coverMenuOpen} aria-haspopup="menu" onClick={() => setCoverMenuOpen((current) => !current)}><img src={INSTITUTION_PROFILE_MORE_VERT_ICON} alt="" /></button>{coverMenuOpen ? <div className="institution-clinician-overflow-menu" role="menu" aria-label={c("media.coverActions", "Institution cover actions")}>{coverBackendState === "available" ? <><button type="button" role="menuitem" onClick={() => { coverInputRef.current?.click(); setCoverMenuOpen(false); }}><SquarePen size={20} />{hasFacilityCover ? c("media.changeCover", "Change cover") : c("media.uploadCover", "Upload cover")}</button>{hasFacilityCover ? <button type="button" role="menuitem" className="is-destructive" onClick={() => { setCoverRemovalOpen(true); setCoverMenuOpen(false); }}><InstitutionIcon src={ICONS.clinicianDelete} size={20} />{c("media.removeCover", "Remove cover")}</button> : null}</> : <p className="px-4 py-3 text-body-small text-on-surface-variant">{c("media.unavailable", "Cover management is unavailable until the protected media service is released.")}</p>}</div> : null}</div> : null}
              {canManageFacilityCover && mobileCoverActions ? <button type="button" className="institutions-icon-button institution-clinician-more-button" aria-label={c("media.moreCoverActions", "More Institution cover actions")} aria-expanded={coverMenuOpen} onClick={() => setCoverMenuOpen(true)}><img src={INSTITUTION_PROFILE_MORE_VERT_ICON} alt="" /></button> : null}
              <div className="hidden md:flex items-center gap-3"><Button type="button" variant="ghost" className="institution-cover-action-button" onClick={cancel} disabled={busy || !isDirty}>{c("actions.cancel", "Cancel")}</Button><Button type="submit" variant="default" className="institution-cover-action-button" loading={busy} disabled={!canSave}>{c("actions.save", "Save")}</Button></div>
            </div>
          </div>
        </InstitutionProfileCover>
        <div className="afrimed-rail afrimed-page pt-4">
          <div className="relative min-h-[40px]" aria-hidden="true" />
          <div className="mt-2 md:mt-14"><h1 className="text-headline-small text-on-surface">{form?.name || facility.name}</h1><p className="mt-1 text-body-medium text-on-surface-var">{facility.reference || "Medical Institution"}</p>{canManageFacilityCover && coverBackendState === "unavailable" ? <p className="mt-2 text-body-small text-on-surface-variant" role="status">{c("media.unavailable", "Cover management is unavailable until the protected media service is released.")}</p> : null}{mobileCoverActions && isDirty ? <div className="mt-4 flex gap-3"><Button type="button" variant="ghost" onClick={cancel} disabled={busy}>{c("actions.cancel", "Cancel")}</Button><Button type="submit" variant="default" loading={busy} disabled={!canSave}>{c("actions.save", "Save")}</Button></div> : null}</div>
        </div>
      </>}
      <input ref={logoInputRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={selectSavedLogo} hidden />
      <input ref={logoCameraInputRef} type="file" accept="image/png,image/jpeg,image/webp" capture="environment" onChange={selectSavedLogo} hidden />
      <input ref={coverInputRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={selectFacilityCover} hidden />
      <input ref={coverCameraInputRef} type="file" accept="image/png,image/jpeg,image/webp" capture="environment" onChange={selectFacilityCover} hidden />
      <div className={`medical-institution-create-body${settingsMode ? "" : " medical-institution-profile-body afrimed-rail afrimed-page mt-5 pb-12"}`}>
        {error ? <div className="medical-institution-error medical-institution-error--compact" role="alert"><p className="text-body-medium">{error}</p></div> : null}
        {form ? <>{settingsMode ? <div className="medical-institution-settings-nav" role="navigation" aria-label="Medical Institution settings"><button type="button" className="is-active" aria-current="page" onClick={() => navigate(facilitySettingsPath(institutionId, facilityId))}><FileText size={20} />General</button></div> : null}{settingsMode ? <div className="medical-institution-settings-heading"><h2 className="text-title-medium text-on-surface-variant">General Information</h2><p className="text-body-medium">Change the information of this Medical Institution.</p></div> : null}{settingsMode ? <MedicalInstitutionAvatarControl preview={logoUrl} onSelect={selectSavedLogo} disabled={busy || logoUploading} /> : null}
          <div className="medical-institution-detail-section-stack">
          <section className="medical-institution-form-section institution-facility-basic-information-section"><h2 className="text-title-medium text-on-surface-variant">{c("sections.basicInformation", "Basic Information")}</h2><div className="medical-institution-form-grid"><TextField56 label={c("fields.institutionName", "Institution Name*")} value={form.name} onChange={(value) => update("name", value)} tone="surface" /><MedicalInstitutionTypePicker value={form.type} onChange={(value) => update("type", value)} label={c("fields.institutionType", "Institution type *")} placeholder={c("fields.selectInstitutionType", "Select institution type")} optionLabel={typeLabel} /><TextField56 label={c("fields.registrationTaxId", "Registration / VAT ID")} value={form.registrationTaxId} onChange={(value) => update("registrationTaxId", value)} tone="surface" /><MedicalInstitutionAccessibilityPicker value={form.accessibility} onChange={(value) => update("accessibility", value)} label={c("fields.facilityOwnership", "Facility ownership")} optionLabel={ownershipLabel} /></div></section>
          <section className="medical-institution-form-section"><MedicalInstitutionDescriptionField value={form.description} onChange={(value) => update("description", value)} disabled={busy} label={c("fields.description", "Facility description")} help={c("fields.descriptionHelp", "Optional concise description")} /></section>
          {!settingsMode ? <MedicalInstitutionMemberRow id="medical-institution-members" workspace={workspace} admins={facilityAdmins} onManageAdmins={() => setAdminOpen(true)} actionRef={adminButtonRef} labels={detailLabels} /> : null}
          <MedicalInstitutionSpecialties specialties={network.specialties || []} selectedIds={form.specialtyIds} customSpecialties={form.customSpecialties} onChange={(value) => update("specialtyIds", value)} onCustomChange={(value) => update("customSpecialties", value)} heading={c("sections.specialties", "Specialties")} addLabel={c("actions.add", "Add")} searchPlaceholder={c("actions.searchOrAdd", "Search or add")} />
          <section className="medical-institution-form-section"><h2 className="text-title-medium text-on-surface-variant">{c("sections.address", "Address")}</h2><div className="medical-institution-form-grid"><TextField56 label={c("fields.address", "Address")} value={form.address} onChange={(value) => update("address", value)} tone="surface" /><TextField56 label={c("fields.zipCode", "ZIP Code")} value={form.postalCode} onChange={(value) => update("postalCode", value)} tone="surface" /><TextField56 label={c("fields.city", "City")} value={form.city} onChange={(value) => update("city", value)} tone="surface" /><SelectCountrySADC label={c("fields.country", "Country")} value={form.country} onChange={(value) => update("country", value)} tone="surface" useTextFieldTrigger /></div></section>
          <MedicalInstitutionAvailabilityRow openingHours={form.openingHours} onEdit={() => setAvailabilityOpen(true)} labels={detailLabels} />
          <section className="medical-institution-form-section"><h2 className="text-title-medium text-on-surface-variant">{c("sections.domain", "Domain")}</h2><TextField56 label={c("fields.domain", "Domain")} value={form.website} onChange={(value) => update("website", value)} tone="surface" /></section>
          </div></> : <NetworkLoadingState />}
      </div>
    </form>
    {!settingsMode ? <MedicalInstitutionAdminSheet open={adminOpen} workspace={workspace} institutionId={institutionId} stagedAdmins={facilityAdmins} onSave={saveFacilityMember} onClose={() => setAdminOpen(false)} returnFocusRef={adminButtonRef} pending={adminBusy} /> : null}
    {canManageFacilityCover ? <InstitutionImageMediaSheet open={logoMenuOpen} title={c("media.editLogo", "Edit Institution logo")} hasCustomImage={Boolean(facility.logoPath)} pending={logoUploading} onClose={() => setLogoMenuOpen(false)} onUpload={() => { logoInputRef.current?.click(); setLogoMenuOpen(false); }} onTakePhoto={() => { logoCameraInputRef.current?.click(); setLogoMenuOpen(false); }} onRemove={() => { setLogoRemovalOpen(true); setLogoMenuOpen(false); }} /> : null}
    {canManageFacilityCover && mobileCoverActions ? <InstitutionImageMediaSheet open={coverMenuOpen} title={c("media.editCover", "Edit Institution cover")} available={coverBackendState === "available"} hasCustomImage={hasFacilityCover} pending={coverUploading} unavailableMessage={c("media.unavailable", "Cover management is unavailable until the protected media service is released.")} onClose={() => setCoverMenuOpen(false)} onUpload={() => { coverInputRef.current?.click(); setCoverMenuOpen(false); }} onTakePhoto={() => { coverCameraInputRef.current?.click(); setCoverMenuOpen(false); }} onRemove={() => { setCoverRemovalOpen(true); setCoverMenuOpen(false); }} /> : null}
    {form ? <MedicalInstitutionAvailabilitySheet open={availabilityOpen} value={form.openingHours} onCancel={() => setAvailabilityOpen(false)} onSave={(value) => { update("openingHours", value); setAvailabilityOpen(false); }} returnFocusRef={availabilityButtonRef} labels={{ title: c("sections.availability", "Availability"), open24Hours: c("availabilitySheet.open24Hours", "Open 24 hours"), openContinuously: c("availabilitySheet.openContinuously", "Open continuously every day"), weekdays: c("availabilitySheet.weekdays", "Weekdays"), days: Object.fromEntries(AVAILABILITY_DAYS.map((day) => [day.key, c(`availabilitySheet.days.${day.key}`, day.label)])), save: c("actions.save", "Save"), cancel: c("actions.cancel", "Cancel"), enableOpen24Hours: c("availabilitySheet.enableOpen24Hours", "Enable open 24 hours"), disableOpen24Hours: c("availabilitySheet.disableOpen24Hours", "Disable open 24 hours"), needsOpeningAndClosingTime: c("availabilitySheet.needsOpeningAndClosingTime", "{day} needs an opening and closing time."), overnightUnsupported: c("availabilitySheet.overnightUnsupported", "Overnight opening hours are not supported in this phase."), open24HoursHelp: c("availabilitySheet.open24HoursHelp", "Individual opening hours are not used while the facility is open 24 hours."), openDay: c("availabilitySheet.openDay", "Open {day}"), closeDay: c("availabilitySheet.closeDay", "Close {day}"), chooseOpeningTime: c("availabilitySheet.chooseOpeningTime", "Choose {day} opening time"), chooseClosingTime: c("availabilitySheet.chooseClosingTime", "Choose {day} closing time"), openingTime: c("availabilitySheet.openingTime", "{day} opening time"), closingTime: c("availabilitySheet.closingTime", "{day} closing time"), opensAt: c("availabilitySheet.opensAt", "On {day}, opens at"), closesAt: c("availabilitySheet.closesAt", "On {day}, closes at") }} /> : null}
    {logoCropSource ? <CoverCropModal src={logoCropSource} aspect={1 / 1} onCancel={() => setLogoCropSource("")} onConfirm={saveCroppedLogo} /> : null}
    {coverCropSource ? <CoverCropModal src={coverCropSource} aspect={944 / 320} onCancel={() => setCoverCropSource("")} onConfirm={saveCroppedCover} /> : null}
    <BasicDialogCritical open={logoRemovalOpen} title={c("media.removeLogoTitle", "Remove Institution logo?")} body={c("media.removeLogoBody", "Your current Institution logo will be removed.")} cancelText={c("actions.cancel", "Cancel")} okText={c("media.removeImage", "Remove image")} onCancel={() => setLogoRemovalOpen(false)} onOk={removeFacilityLogo} disableBackdropClose={logoUploading} />
    <BasicDialogCritical open={coverRemovalOpen} title={c("media.removeCoverTitle", "Remove Institution cover?")} body={c("media.removeCoverBody", "Your current Institution cover will be removed.")} cancelText={c("actions.cancel", "Cancel")} okText={c("media.removeImage", "Remove image")} onCancel={() => setCoverRemovalOpen(false)} onOk={removeFacilityCover} disableBackdropClose={coverUploading} />
    <Snackbar open={Boolean(notice)} message={notice} onClose={() => setNotice("")} />
  </PageShell>;
}

function clinicianDisplayName(clinician) {
  const fullName = String(clinician?.fullName || [clinician?.firstName, clinician?.lastName].filter(Boolean).join(" ") || "Clinician draft").trim();
  return resolveAddressedProfileName("doctor", {
    salutation: clinician?.salutation,
    last_name: clinician?.lastName,
  }, fullName) || fullName;
}

function clinicianReference(clinician) {
  return String(clinician?.id || clinician?.clinicianProfileId || "").slice(0, 8).toUpperCase() || "Pending";
}

function clinicianProfessionLabel(value) {
  return CLINICIAN_PROFESSION_OPTIONS.find((option) => option.value === value)?.label || formatRole(value);
}

// Keep the service value canonical (YYYY-MM-DD), but never expose its storage
// representation as the human-facing clinical date.
function formatClinicianDate(value) {
  const match = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return match ? `${match[3]}-${match[2]}-${match[1]}` : "";
}

function clinicianInitialForm(clinician = {}) {
  const registration = clinician?.registration || {};
  const derivedPhoneCountry = getPhoneMarketFromE164(clinician?.phone)?.iso?.toUpperCase() || "";
  return {
    firstName: clinician?.firstName || "",
    lastName: clinician?.lastName || "",
    email: clinician?.email || "",
    phone: clinician?.phone || "",
    phoneCountry: String(clinician?.phoneCountry || derivedPhoneCountry || "").toUpperCase(),
    salutation: normalizeProfileSalutation("doctor", clinician?.salutation),
    gender: clinician?.gender || "",
    dateOfBirth: clinician?.dateOfBirth || "",
    yearsOfExperience: clinician?.yearsOfExperience ?? "",
    profession: clinician?.profession || "clinician",
    specialtyId: clinician?.specialty?.id || "",
    specialtyRequested: clinician?.specialtyRequested || "",
    registrationNumber: registration?.number || "",
    registrationDate: registration?.date || "",
    registrationYear: registration?.year || "",
    registrationBody: registration?.body || "",
    registrationJurisdiction: registration?.jurisdiction || "",
    registrationEvidence: registration?.evidence && Object.keys(registration.evidence).length ? registration.evidence : null,
    institutionImage: clinician?.institutionImage && Object.keys(clinician.institutionImage).length ? clinician.institutionImage : null,
    credentialEvidenceState: registration?.evidenceState === "pending_review" ? "pending_review" : "supplied",
    facilityIds: (clinician?.facilities || []).map((facility) => facility.id),
    focusAreas: Array.isArray(clinician?.focusAreas) ? clinician.focusAreas : [],
    education: Array.isArray(clinician?.education) ? clinician.education : [],
    languages: Array.isArray(clinician?.languages) ? clinician.languages : [],
  };
}

function normaliseInstitutionLanguageDrafts(items = []) {
  const seen = new Set();
  return (Array.isArray(items) ? items : []).reduce((next, item) => {
    const language = String(typeof item === "string" ? item : item?.language || "").trim();
    const key = language.toLocaleLowerCase();
    if (!language || seen.has(key)) return next;
    seen.add(key);
    next.push(typeof item === "string" ? { language } : { ...item, language });
    return next;
  }, []);
}

const EMPTY_CLINICIAN_EDUCATION_DRAFT = { title: "", institution: "", country: "", completionDate: "", year: "", evidence: null };

function ClinicianFormationThumbnail({ evidence = null }) {
  const previewUrl = String(evidence?.previewUrl || "");
  const hasImagePreview = previewUrl && String(evidence?.mimeType || "").startsWith("image/");
  return <span className="institution-clinician-formation-thumbnail" aria-label={evidence?.fileName || "Formation evidence"}>
    <img src={hasImagePreview ? previewUrl : ICONS.clinicianThumbnail} alt={hasImagePreview ? "" : ""} />
  </span>;
}

function clinicianPhoneDigits(phone, countryCode = "+27") {
  const digits = String(phone || "").replace(/\D/g, "");
  const countryDigits = String(countryCode || "").replace(/\D/g, "");
  return countryDigits && digits.startsWith(countryDigits) ? digits.slice(countryDigits.length) : digits;
}

function clinicianPhoneE164(phone) {
  const digits = String(phone || "").replace(/\D/g, "");
  return digits ? `+${digits}` : "";
}

function clinicianPhoneCountry(phone, preferredCountry = "") {
  const normalized = String(preferredCountry || "").trim().toUpperCase();
  if (getPhoneMarketByIso(normalized)) return normalized;
  return getPhoneMarketFromE164(phone)?.iso?.toUpperCase() || "";
}

function clinicianSearchText(clinician) {
  return [
    clinicianDisplayName(clinician), clinician?.firstName, clinician?.lastName, clinician?.email, clinician?.phone,
    clinicianProfessionLabel(clinician?.profession), clinician?.specialty?.name, clinician?.registration?.number,
    clinician?.status, ...(clinician?.facilities || []).map((facility) => facility?.name),
  ].filter(Boolean).join(" ").toLocaleLowerCase();
}

function getInstitutionClinicianSearchResults(clinicians = [], query = "") {
  const normalizedQuery = String(query || "").trim().replace(/\s+/g, " ").toLocaleLowerCase();
  if (!normalizedQuery) return [];
  return clinicians.filter((clinician) => clinicianSearchText(clinician).includes(normalizedQuery));
}

function InstitutionClinicianSearchResults({ query, clinicians, onSelect }) {
  const { t } = useLanguage();
  const normalizedQuery = String(query || "").trim();
  if (!normalizedQuery) return null;
  const results = getInstitutionClinicianSearchResults(clinicians, normalizedQuery);
  if (!results.length) {
    return <p className="medical-institution-search-empty text-body-medium">{t("institutionClinicians.noResults", "No clinicians match “{query}”.", { query: normalizedQuery })}</p>;
  }
  return <section className="medical-institution-search-group" aria-label={t("institutionClinicians.searchGroup", "Clinicians")}>
    <div className="medical-institution-search-group-heading"><span>{t("institutionClinicians.searchGroup", "Clinicians")}</span></div>
    {results.map((clinician) => {
      const name = clinicianDisplayName(clinician);
      const supporting = [
        clinicianProfessionLabel(clinician?.profession),
        clinician?.specialty?.name,
        clinician?.facilities?.map((facility) => facility?.name).filter(Boolean).join(", "),
      ].filter(Boolean).join(" · ");
      return <button key={clinician.id} type="button" className="medical-institution-search-result institution-clinician-search-result" onClick={() => onSelect?.(clinician)}>
        <span className="institution-clinician-search-avatar" aria-hidden="true"><img src={clinician?.imagePath || ICONS.clinicianAvatar} alt="" /></span>
        <span className="medical-institution-search-result-copy"><strong>{name}</strong><small>{supporting || formatRole(clinician?.status || "draft")}</small><small>ID: CL-{clinicianReference(clinician)}</small></span>
        <ChevronRight size={22} aria-hidden="true" />
      </button>;
    })}
  </section>;
}

function InstitutionClinicianSearchSurface({ open, clinicians = [], onClose, onSelect }) {
  const { t } = useLanguage();
  const [query, setQuery] = useState("");
  const inputRef = useRef(null);
  useEffect(() => {
    if (!open) {
      setQuery("");
      return undefined;
    }
    const frame = window.requestAnimationFrame(() => inputRef.current?.focus());
    const onKeyDown = (event) => { if (event.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKeyDown);
    return () => { window.cancelAnimationFrame(frame); document.removeEventListener("keydown", onKeyDown); };
  }, [onClose, open]);
  if (!open || typeof document === "undefined") return null;
  return createPortal(
    <div className="medical-institution-search-scrim" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose?.(); }}>
      <section id="institution-clinician-search-dialog" className="medical-institution-search-surface" role="dialog" aria-modal="true" aria-label={t("institutionClinicians.search", "Search clinicians")}>
        <div className="medical-institution-search-header">
          <InstitutionIcon src={ICONS.search} fallback={Search} size={24} />
          <input ref={inputRef} value={query} onChange={(event) => setQuery(event.target.value)} placeholder={t("institutionClinicians.search", "Search clinicians")} aria-label={t("institutionClinicians.search", "Search clinicians")} autoComplete="off" />
        </div>
        <div className="medical-institution-search-divider" aria-hidden="true" />
        <div className="medical-institution-search-list"><InstitutionClinicianSearchResults query={query} clinicians={clinicians} onSelect={onSelect} /></div>
      </section>
    </div>,
    document.body,
  );
}

function CliniciansView({ workspace }) {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const institutionId = workspace?.activeInstitution?.id || "";
  const [status, setStatus] = useState("all");
  const [filterOpen, setFilterOpen] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState({ facility: "", profession: "", specialty: "" });
  const [invitationByRosterId, setInvitationByRosterId] = useState({});
  const [csvOpen, setCsvOpen] = useState(false);
  useEffect(() => {
    let active = true;
    if (!institutionId) {
      setInvitationByRosterId({});
      return () => { active = false; };
    }
    loadInstitutionClinicianInvitationManagerSnapshot({ institutionId }).then((snapshot) => {
      if (!active) return;
      setInvitationByRosterId(Object.fromEntries((snapshot?.invitations || []).map((invitation) => [invitation.rosterId, invitation])));
    }).catch(() => {
      if (active) setInvitationByRosterId({});
    });
    return () => { active = false; };
  }, [institutionId]);
  const clinicians = (workspace?.linkedClinicians || []).map((clinician) => ({
    ...clinician,
    relationshipStatus: clinicianRelationshipStatus(clinician, invitationByRosterId[clinician.id]),
  }));
  const rows = clinicians.filter((clinician) => {
    const statusMatches = status === "all" || clinician?.relationshipStatus === status || clinician?.status === status;
    const facilityMatches = !advancedFilters.facility || clinician?.facilities?.some((facility) => facility.id === advancedFilters.facility);
    const professionMatches = !advancedFilters.profession || clinician?.profession === advancedFilters.profession;
    const specialtyMatches = !advancedFilters.specialty || clinician?.specialty?.id === advancedFilters.specialty;
    return statusMatches && facilityMatches && professionMatches && specialtyMatches;
  });
  const facilities = workspace?.clinicianFacilities || workspace?.linkedFacilities || [];
  const hasFacilities = facilities.length > 0;
  const statusOptions = CLINICIAN_STATUS_OPTIONS.map((option) => ({ ...option, label: clinicianStatusLabel(t, option.value) }));
  return (
    <PageShell workspace={workspace} title={t("institutionClinicians.title", "Clinicians")} icon={Stethoscope} clinicianSearch pageTopBarTitle={t("institutionClinicians.title", "Clinicians")} contentClassName="afrimed-rail afrimed-rail--comfortable pb-24">
      <section className="institutions-clinicians-page" aria-label="Institution clinicians">
        <div className="institutions-clinicians-tools">
          <div className="institutions-list-filter-group" aria-label={t("institutionClinicians.filters.group", "Clinician list filters")}>
            <InstitutionStatusFilterChip value={status} options={statusOptions} onChange={setStatus} ariaLabel={t("institutionClinicians.filters.status", "Filter clinicians by status")} />
            <button type="button" className="institutions-icon-button institution-clinician-filter-button" aria-label={t("institutionClinicians.filters.open", "Filter clinicians")} aria-expanded={filterOpen} onClick={() => setFilterOpen(true)}><InstitutionIcon src={ICONS.clinicianFilter} fallback={Filter} size={20} useMask /></button>
          </div>
          <InstitutionRegistryCtas onImport={() => setCsvOpen(true)} onCreate={() => navigate(clinicianPath(institutionId, "new"))} createLabel={t("institutionRegistryCtas.addClinician", "Add clinician")} createDisabled={!hasFacilities} createTitle={hasFacilities ? "" : t("institutionClinicians.noFacilities", "Create a Medical Institution first")} />
        </div>
        {!facilities.length ? (
          <ClinicianEmptyState noFacilities onCreateFacility={() => navigate(institutionPath(institutionId, "/new"))} />
        ) : rows.length ? (
          <CliniciansTable rows={rows} onOpen={(id) => navigate(clinicianPath(institutionId, id))} />
        ) : (
          <ClinicianEmptyState filtered={Boolean(status !== "all" || advancedFilters.facility || advancedFilters.profession || advancedFilters.specialty)} onCreate={() => navigate(clinicianPath(institutionId, "new"))} />
        )}
      </section>
      <InstitutionSideSheet open={filterOpen} title={t("institutionClinicians.filters.title", "Filter clinicians")} onClose={() => setFilterOpen(false)} labelledBy="institution-clinician-filter-title" footer={<><button type="button" className="institutions-side-sheet-cancel" onClick={() => { setAdvancedFilters({ facility: "", profession: "", specialty: "" }); setFilterOpen(false); }}>{t("institutionClinicians.actions.clear", "Clear")}</button><button type="button" className="institutions-primary-button" onClick={() => setFilterOpen(false)}>{t("institutionClinicians.actions.apply", "Apply")}</button></>}>
        <div className="institutions-side-sheet-form">
          <Select label={t("institutionClinicians.fields.facility", "Facility")} value={advancedFilters.facility} placeholder={t("institutionClinicians.selectOption", "Select an option")} options={[{ value: "", label: t("institutionClinicians.filters.allFacilities", "All facilities") }, ...facilities.map((facility) => ({ value: facility.id, label: facility.name || facility.facility?.facility_name || "Medical Institution" }))]} onValueChange={(value) => setAdvancedFilters((current) => ({ ...current, facility: value }))} tone="surface" />
          <Select label={t("institutionClinicians.fields.profession", "Profession")} value={advancedFilters.profession} placeholder={t("institutionClinicians.selectOption", "Select an option")} options={[{ value: "", label: t("institutionClinicians.filters.allProfessions", "All professions") }, ...CLINICIAN_PROFESSION_OPTIONS.filter((option) => option.value).map((option) => ({ value: option.value, label: option.label }))]} onValueChange={(value) => setAdvancedFilters((current) => ({ ...current, profession: value }))} tone="surface" />
          <Select label={t("institutionClinicians.fields.specialty", "Specialty")} value={advancedFilters.specialty} placeholder={t("institutionClinicians.selectOption", "Select an option")} options={[{ value: "", label: t("institutionClinicians.filters.allSpecialties", "All specialties") }, ...Array.from(new Map((workspace?.linkedClinicians || []).filter((row) => row?.specialty?.id).map((row) => [row.specialty.id, { value: row.specialty.id, label: row.specialty.name }])).values())]} onValueChange={(value) => setAdvancedFilters((current) => ({ ...current, specialty: value }))} tone="surface" />
        </div>
      </InstitutionSideSheet>
      {csvOpen ? <GovernedCsvImport title="Import clinicians from CSV" institutionId={institutionId} importType="clinician" fields={[{ key: "firstName", label: "First name", required: true, aliases: ["given name"] }, { key: "lastName", label: "Last name", required: true, aliases: ["surname"] }, { key: "email", label: "Email", required: true }, { key: "profession", label: "Profession", required: true }, { key: "facility", label: "Facility", required: true, aliases: ["facility name"] }, { key: "phone", label: "Phone" }, { key: "phoneCountry", label: "Country code" }, { key: "dateOfBirth", label: "Date of birth", aliases: ["dob"] }, { key: "gender", label: "Gender" }, { key: "registrationNumber", label: "Registration number" }, { key: "specialtyId", label: "Specialty ID" }]} onClose={() => setCsvOpen(false)} /> : null}
    </PageShell>
  );
}

function ClinicianEmptyState({ noFacilities = false, filtered = false, onCreate, onCreateFacility }) {
  const { t } = useLanguage();
  return (
    <div className={`institutions-clinician-empty-state${noFacilities ? " institutions-clinician-empty-state--prerequisite" : ""}`} role="status">
      <InstitutionIcon src={ICONS.clinicians} fallback={Stethoscope} size={28} />
      <div>
        <h2 className="text-title-large">{noFacilities ? t("institutionClinicians.noFacilities", "Create a Medical Institution first") : filtered ? t("institutionClinicians.noFilterResults", "No clinicians match this filter") : t("institutionClinicians.noClinicians", "No clinicians yet")}</h2>
        <p className="text-body-medium">{noFacilities ? t("institutionClinicians.noFacilitiesBody", "A clinician relationship must be assigned to one or more Medical Institutions in this parent workspace.") : t("institutionClinicians.noCliniciansBody", "Add clinicians manually now. Future MoH and team CSV imports will create the same pending roster relationships.")}</p>
      </div>
      <Button type="button" variant="default" size="large" onClick={noFacilities ? onCreateFacility : onCreate}>{noFacilities ? t("institutionClinicians.createMedicalInstitution", "Create Medical Institution") : `+ ${t("institutionClinicians.new", "New Clinician")}`}</Button>
    </div>
  );
}

function InstitutionMembersView({ workspace }) {
  const { t } = useLanguage();
  const c = (key, fallback) => t(`institutionMembers.${key}`, fallback);
  const institution = workspace?.activeInstitution || {};
  const navigate = useNavigate();
  const location = useLocation();
  const members = useMemo(() => (workspace?.members || []).filter((member) => (
    getMemberStatus(member) === "active"
    && (!institution?.id || String(member?.institution_id || member?.institutionId || institution.id) === String(institution.id))
  )), [institution?.id, workspace?.members]);
  const selectedMemberId = new URLSearchParams(location.search).get("member") || "";
  const selectedMember = members.find((member) => String(member?.id || "") === selectedMemberId) || null;
  const [csvOpen, setCsvOpen] = useState(false);
  const [evaluatorInviteOpen, setEvaluatorInviteOpen] = useState(false);
  const [evaluatorEmail, setEvaluatorEmail] = useState("");
  const [evaluatorInvitePending, setEvaluatorInvitePending] = useState(false);
  const [evaluatorInviteError, setEvaluatorInviteError] = useState("");
  const [evaluatorInviteNotice, setEvaluatorInviteNotice] = useState("");
  const currentMembership = members.find((member) => String(member?.user_id || member?.userId || "") === String(workspace?.user?.id || ""));
  const canInviteEvaluator = ["owner", "institution_admin"].includes(String(currentMembership?.role || "").toLowerCase());

  const closeEvaluatorInvite = () => {
    if (evaluatorInvitePending) return;
    setEvaluatorInviteOpen(false);
    setEvaluatorInviteError("");
  };

  const submitEvaluatorInvite = async (event) => {
    event.preventDefault();
    setEvaluatorInvitePending(true);
    setEvaluatorInviteError("");
    setEvaluatorInviteNotice("");
    try {
      const result = await sendInstitutionEvaluationReviewerInvite({
        institutionId: institution.id,
        email: evaluatorEmail,
      });
      setEvaluatorInviteNotice(c("evaluationInviteSent", "Evaluation reviewer invitation sent. Provider delivery evidence has been recorded."));
      setEvaluatorEmail("");
      setEvaluatorInviteOpen(false);
      return result;
    } catch (error) {
      setEvaluatorInviteError(error?.message || c("evaluationInviteError", "AfriMed™ could not send this invitation. No membership was granted."));
    } finally {
      setEvaluatorInvitePending(false);
    }
  };

  const setMemberQuery = (memberId = "") => {
    const params = new URLSearchParams(location.search);
    if (institution?.id) params.set("institution", institution.id);
    if (memberId) params.set("member", memberId);
    else params.delete("member");
    navigate({ pathname: "/institutions/members", search: `?${params.toString()}` });
  };

  const openMember = (member) => {
    const membershipId = String(member?.id || "").trim();
    if (!membershipId || !institution?.id) return;
    // General Membership is the only source for Home member recency. Rendering
    // this list and honouring an existing Home Visit remain side-effect free.
    recordInstitutionHomeMemberView(workspace?.user?.id, {
      parentInstitutionId: institution.id,
      facilityId: null,
      membershipId,
      userId: member?.user_id || member?.userId || null,
    });
    setMemberQuery(membershipId);
  };

  return (
    <PageShell
      workspace={workspace}
      title={t("institutionShell.navigation.members", "Members")}
      iconSrc={ICONS.members}
      iconMask
      pageTopBarTitle={c("title", "Institution Members")}
      pageTopBarSubtitle={c("description", "Authorised administrative and operational members of this Institution.")}
      pageTopBarCentered={false}
      pageTopBarNavigation={INSTITUTION_TOP_BAR_NAVIGATION.BACK}
      pageTopBarBackTo="/institutions/home"
      pageTopBarBackLabel={c("backToInstitutionHome", "Back to Institution home")}
    >
      <section className="institutions-members-page" aria-label={c("title", "Institution Members")}>
        <div className="institutions-members-actions" aria-label={c("actions", "Member actions")}>
          {canInviteEvaluator ? <Button type="button" variant="tonal" size="large" onClick={() => setEvaluatorInviteOpen(true)}>{c("inviteEvaluator", "Invite evaluator")}</Button> : null}
          <InstitutionRegistryCtas onImport={() => setCsvOpen(true)} onCreate={() => navigate(`/institutions/surveillance-team?institution=${encodeURIComponent(institution.id)}&invite=1`)} createLabel={t("institutionRegistryCtas.addMember", "Add member")} />
        </div>
        {evaluatorInviteNotice ? <div className="institutions-surveillance-notice" role="status">{evaluatorInviteNotice}</div> : null}
        <div className="institutions-members-table" role="list" aria-label={c("title", "Institution Members")}>
          {members.length ? members.map((member) => (
            <button
              key={member.id || member.user_id || member.email}
              type="button"
              role="listitem"
              className={`institutions-members-row${selectedMemberId === String(member?.id || "") ? " is-selected" : ""}`}
              onClick={() => openMember(member)}
            >
              <InstitutionMemberAvatar member={member} />
              <span className="institutions-members-copy"><strong>{getMemberName(member)}</strong><small>{getMemberEmail(member) || formatRole(getMemberProfession(member))}</small></span>
              <span className="institutions-members-role">{institutionControlledLabel(t, getMemberProfession(member))}</span>
              <ChevronRight size={20} aria-hidden="true" />
            </button>
          )) : <SetupState title={c("emptyTitle", "No Institution Members yet")}>{c("emptyBody", "Authorised administrative and operational members will appear here.")}</SetupState>}
        </div>
      </section>
      <InstitutionSideSheet
        open={Boolean(selectedMember)}
        title={selectedMember ? getMemberName(selectedMember) : c("member", "Member")}
        description={c("member", "Member")}
        iconSrc={ICONS.members}
        onClose={() => setMemberQuery("")}
        labelledBy="institution-member-sheet-title"
        variant="member-detail"
        footer={<button type="button" className="institutions-primary-button" onClick={() => setMemberQuery("")}>{c("close", "Close")}</button>}
      >
        {selectedMember ? <div className="institutions-member-detail-sheet">
          <InstitutionMemberAvatar member={selectedMember} size={56} />
          <dl>
            <div><dt>{c("role", "Role")}</dt><dd>{institutionControlledLabel(t, getMemberProfession(selectedMember))}</dd></div>
            <div><dt>{c("email", "Email")}</dt><dd>{getMemberEmail(selectedMember) || c("notSupplied", "Not supplied")}</dd></div>
            <div><dt>{c("status", "Status")}</dt><dd><InstitutionLifecycleBadge status={getMemberStatus(selectedMember)} label={institutionControlledLabel(t, getMemberStatus(selectedMember))} /></dd></div>
          </dl>
        </div> : null}
      </InstitutionSideSheet>
      <InstitutionSideSheet
        open={evaluatorInviteOpen}
        title={c("evaluationInviteTitle", "Invite evaluation reviewer")}
        description={c("evaluationInviteDescription", "Creates a pending, exact-email invitation to this Institution. No membership is active until the invited person accepts.")}
        iconSrc={ICONS.members}
        onClose={closeEvaluatorInvite}
        pending={evaluatorInvitePending}
        labelledBy="institution-evaluation-invite-title"
        footer={<><button type="button" className="institutions-side-sheet-cancel" onClick={closeEvaluatorInvite} disabled={evaluatorInvitePending}>{c("cancel", "Cancel")}</button><button type="submit" form="institution-evaluation-invite-form" className="institutions-primary-button" disabled={evaluatorInvitePending || !evaluatorEmail.trim()}>{evaluatorInvitePending ? c("sending", "Sending…") : c("sendInvitation", "Send invitation")}</button></>}
      >
        <form id="institution-evaluation-invite-form" className="institutions-side-sheet-form" onSubmit={submitEvaluatorInvite}>
          <TextField56 label={c("email", "Email")} value={evaluatorEmail} onChange={setEvaluatorEmail} tone="surface" inputProps={{ type: "email", required: true, autoComplete: "email" }} />
          <TextField56 label={c("institutionRole", "Institution role")} value={c("evaluationReviewer", "Evaluation reviewer")} tone="surface" inputProps={{ readOnly: true }} />
          {evaluatorInviteError ? <p className="institutions-surveillance-error" role="alert">{evaluatorInviteError}</p> : null}
        </form>
      </InstitutionSideSheet>
      {csvOpen ? <GovernedCsvImport title="Import Institution members from CSV" institutionId={institution.id} importType="member" fields={[{ key: "email", label: "Email", required: true, aliases: ["member email"] }, { key: "role", label: "Role", aliases: ["institution role"] }]} onClose={() => setCsvOpen(false)} /> : null}
    </PageShell>
  );
}

function SurveillanceTeamView({ workspace, onRefresh }) {
  const { t } = useLanguage();
  const location = useLocation();
  const c = (key, fallback) => t(`institutionSurveillance.${key}`, fallback);
  const institution = workspace.activeInstitution || {};
  const surveillance = workspace.surveillance || {};
  const members = workspace.members || [];
  const facilities = surveillance.facilities || [];
  const programmes = surveillance.programmes || surveillance.programs || [];
  const assignments = surveillance.assignments || [];
  const events = surveillance.events || [];
  const historyRows = groupAuthorityHistory(events);
  const allowedRoles = surveillance.allowedRoles || [
    "facility_surveillance_officer",
    "facility_supervisor",
    "clinician",
    "nurse",
    "laboratory_officer",
  ];
  const [assigningMember, setAssigningMember] = useState(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [pending, setPending] = useState(false);
  const [openActionMenuId, setOpenActionMenuId] = useState("");
  const [csvOpen, setCsvOpen] = useState(false);
  const [scopedInvitations, setScopedInvitations] = useState([]);
  const [invitationLoading, setInvitationLoading] = useState(false);
  const inviteTriggerRef = useRef(null);
  const mappedFacilities = facilities.filter((facility) => facility?.regionalHealthOrgUnitId || facility?.regional_health_org_unit_id);
  const [form, setForm] = useState(() => ({
    facilityLinkId: "",
    programmeId: "",
    role: "facility_surveillance_officer",
    startsAt: new Date().toISOString().slice(0, 10),
    endsAt: "",
    reason: "",
  }));
  const supervisors = members.filter((member) => MEMBER_ADMIN_ROLES.has(String(member?.role || "").trim().toLowerCase()) && getMemberStatus(member) === "active");
  const [inviteForm, setInviteForm] = useState({ email: "", audience: "researcher", supervisorMemberId: "", reason: "" });

  const refreshScopedInvitations = React.useCallback(async () => {
    if (!institution?.id) return;
    setInvitationLoading(true);
    try {
      const snapshot = await loadInstitutionScopedInvitationSnapshot({ institutionId: institution.id });
      setScopedInvitations(Array.isArray(snapshot?.invitations) ? snapshot.invitations : []);
    } catch (err) {
      setError(err?.message || c("invitationStatusError", "Unable to load Institution invitation status."));
    } finally {
      setInvitationLoading(false);
    }
  }, [institution?.id]);

  useEffect(() => { refreshScopedInvitations(); }, [refreshScopedInvitations]);

  useEffect(() => {
    if (new URLSearchParams(location.search).get("invite") === "1") setInviteOpen(true);
  }, [location.search]);

  const closeInvite = React.useCallback(() => {
    if (pending) return;
    setInviteOpen(false);
    window.requestAnimationFrame(() => inviteTriggerRef.current?.focus?.());
  }, [pending]);

  const completeInvite = React.useCallback(() => {
    setInviteOpen(false);
    window.requestAnimationFrame(() => inviteTriggerRef.current?.focus?.());
  }, []);

  useEffect(() => {
    if (!assigningMember && !inviteOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key !== "Escape" || pending || document.querySelector("[role=listbox]")) return;
      if (assigningMember) setAssigningMember(null);
      if (inviteOpen) closeInvite();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [assigningMember, closeInvite, inviteOpen, pending]);

  const openAssign = (member) => {
    setError("");
    setNotice("");
    setAssigningMember(member);
    setForm({
      facilityLinkId: mappedFacilities[0]?.id || "",
      programmeId: programmes[0]?.id || "",
      role: "facility_surveillance_officer",
      startsAt: new Date().toISOString().slice(0, 10),
      endsAt: "",
      reason: "",
    });
  };

  const closeAssign = () => {
    if (pending) return;
    setAssigningMember(null);
  };

  const submitAssignment = async (event) => {
    event.preventDefault();
    if (!assigningMember?.id) return;
    setPending(true);
    setError("");
    setNotice("");
    try {
      await assignInstitutionSurveillanceAuthority({
        institutionId: institution.id,
        memberId: assigningMember.id,
        facilityLinkId: form.facilityLinkId,
        programmeId: form.programmeId,
        role: form.role,
        startsAt: form.startsAt,
        endsAt: form.endsAt,
        reason: form.reason,
      });
      setNotice(c("assignedNotice", "Surveillance authority assigned."));
      setAssigningMember(null);
      onRefresh?.();
    } catch (err) {
      setError(err?.message || c("assignError", "Unable to assign surveillance authority."));
    } finally {
      setPending(false);
    }
  };

  const submitInvite = async (event) => {
    event.preventDefault();
    setPending(true);
    setError("");
    setNotice("");
    try {
      const result = await inviteInstitutionScopedMember({
        institutionId: institution.id,
        email: inviteForm.email,
        audience: inviteForm.audience,
        supervisorMemberId: inviteForm.supervisorMemberId,
        reason: inviteForm.reason,
      });
      setNotice(c("scopedInvitationCreated", "Scoped Institution invitation created. Acceptance grants membership only; authority remains separately assigned."));
      completeInvite();
      setInviteForm({ email: "", audience: "researcher", supervisorMemberId: "", reason: "" });
      await refreshScopedInvitations();
      onRefresh?.();
    } catch (err) {
      setError(getInstitutionMemberIntakeErrorMessage(err));
    } finally {
      setPending(false);
    }
  };

  const revokeScopedInvitation = async (invitation) => {
    const reason = window.prompt(c("inviteRevocationReason", "Reason for revoking this pending invitation"));
    if (!reason) return;
    setPending(true);
    setError("");
    setNotice("");
    try {
      await revokeInstitutionScopedMemberInvitation({ institutionId: institution.id, invitationId: invitation.id, reason });
      setNotice(c("invitationRevoked", "Pending Institution invitation revoked."));
      await refreshScopedInvitations();
    } catch (err) {
      setError(err?.message || c("inviteRevokeError", "Unable to revoke this pending invitation."));
    } finally {
      setPending(false);
    }
  };

  const copyScopedInvitationLink = async (invitation) => {
    if (invitation?.status !== "invited" || !invitation?.id) return;
    const acceptanceUrl = new URL("/institutions/surveillance-invitations/accept", window.location.origin);
    acceptanceUrl.searchParams.set("invitation", invitation.id);
    try {
      await navigator.clipboard.writeText(acceptanceUrl.toString());
      setNotice(c("acceptanceLinkCopied", "Acceptance link copied. Share it only with the invited account holder."));
      setError("");
    } catch {
      setError(c("acceptanceLinkCopyFailed", "Could not copy the acceptance link. Check browser clipboard access."));
    }
  };

  const revokeAssignment = async (assignment) => {
    const reason = window.prompt(c("removeReason", "Reason for removing this surveillance authority"));
    if (!reason) return;
    setPending(true);
    setError("");
    setNotice("");
    try {
      await revokeInstitutionSurveillanceAuthority({
        institutionId: institution.id,
        assignmentId: assignment.id,
        reason,
      });
      setNotice(c("removedNotice", "Surveillance authority removed."));
      onRefresh?.();
    } catch (err) {
      setError(err?.message || c("removeError", "Unable to remove surveillance authority."));
    } finally {
      setPending(false);
    }
  };

  const updateMemberStatus = async (member, status) => {
    const action = status === "removed" ? "removing" : "suspending";
    const reason = window.prompt(c("memberReason", "Reason for {action} this institution member").replace("{action}", action));
    if (!reason) return;
    setPending(true);
    setError("");
    setNotice("");
    try {
      await updateInstitutionMemberStatus({
        institutionId: institution.id,
        memberId: member.id,
        status,
        reason,
      });
      setNotice(status === "removed" ? c("memberRemovedNotice", "Institution member removed and authority revoked.") : c("memberSuspendedNotice", "Institution member suspended and authority revoked."));
      onRefresh?.();
    } catch (err) {
      setError(err?.message || c("updateError", "Unable to update institution membership."));
    } finally {
      setPending(false);
    }
  };

  const viewAuthorityHistory = () => {
    setOpenActionMenuId("");
    document.getElementById("institution-authority-history")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <PageShell
      workspace={workspace}
      title={t("institutionShell.navigation.surveillanceTeam", "Surveillance Team")}
      iconSrc={ICONS.surveillance}
      iconMask
      pageTopBarTitle={c("title", "Institution surveillance team")}
      pageTopBarSubtitle={c("description", "Membership does not grant surveillance authority until a scoped assignment is approved here.")}
      pageTopBarCentered={false}
      pageTopBarNavigation={INSTITUTION_TOP_BAR_NAVIGATION.BACK}
      pageTopBarBackTo="/institutions/home"
      pageTopBarBackLabel={c("backToInstitutionHome", "Back to Institution home")}
      contentClassName="afrimed-rail afrimed-rail--comfortable pb-24"
    >
      <section className="institutions-surveillance-page" aria-label={c("title", "Institution surveillance team")}>
        <div className="institutions-surveillance-actions-row" aria-label={c("actions", "Actions")}>
          <InstitutionRegistryCtas onImport={() => setCsvOpen(true)} onCreate={() => setInviteOpen(true)} createLabel={t("institutionRegistryCtas.addMember", "Add member")} createRef={inviteTriggerRef} />
        </div>
        {notice ? <div className="institutions-surveillance-notice" role="status">{notice}</div> : null}
        {error ? <div className="institutions-surveillance-error" role="alert">{error}</div> : null}

        <div className="institutions-surveillance-summary" aria-label={c("authority", "Surveillance authority")}>
          <div>
            <span>{c("members", "Members")}</span>
            <strong>{members.length}</strong>
          </div>
          <div>
            <span>{c("facilityMappings", "Facility mappings")}</span>
            <strong>{mappedFacilities.length}/{facilities.length}</strong>
          </div>
          <div>
            <span>{c("activeAuthority", "Active authority")}</span>
            <strong>{assignments.filter((item) => item.status === "active").length}</strong>
          </div>
          <div>
            <span>{c("programmes", "Programmes")}</span>
            <strong>{programmes.length}</strong>
          </div>
        </div>

        <section className="institutions-surveillance-history" aria-label={c("invitationStatus", "Institution invitation status")}>
          <div className="institutions-surveillance-history-title-row">
            <div>
              <h2>{c("invitationStatus", "Institution invitation status")}</h2>
              <p>{c("invitationStatusDescription", "Pending invitations establish no membership or surveillance authority until the invited verified account accepts.")}</p>
            </div>
            {invitationLoading ? <span className="institutions-authority-history-pill">{c("loading", "Loading…")}</span> : null}
          </div>
          {scopedInvitations.length ? (
            <div className="institutions-authority-history-list">
              {scopedInvitations.map((invitation) => (
                <article className="institutions-authority-history-row" key={invitation.id}>
                  <div>
                    <strong>{invitation.invitedEmail}</strong>
                    <p>{SCOPED_MEMBER_AUDIENCE_OPTIONS.find((option) => option.value === invitation.orientationAudience)?.label || invitation.orientationAudience} · {c("supervisor", "Supervisor")}: {invitation.supervisorName}</p>
                    <p>{invitation.status === "invited" ? c("pendingAcceptance", "Pending acceptance") : invitation.status} · {c("expires", "Expires")}: {invitation.expiresAt ? new Date(invitation.expiresAt).toLocaleDateString() : "—"}</p>
                  </div>
                  {invitation.status === "invited" ? (
                    <div className="institutions-surveillance-actions-row">
                      <button type="button" className="institutions-side-sheet-cancel" disabled={pending} onClick={() => copyScopedInvitationLink(invitation)}>{c("copyAcceptanceLink", "Copy acceptance link")}</button>
                      <button type="button" className="institutions-side-sheet-cancel" disabled={pending} onClick={() => revokeScopedInvitation(invitation)}>{c("revokeInvitation", "Revoke invitation")}</button>
                    </div>
                  ) : null}
                </article>
              ))}
            </div>
          ) : <p className="institutions-authority-history-empty">{c("noInvitations", "No scoped Institution invitations are recorded.")}</p>}
        </section>

        <div className="institutions-surveillance-table" role="table" aria-label={c("tableLabel", "Institution members and surveillance authority")}>
          <div className="institutions-surveillance-row institutions-surveillance-row--head" role="row">
            <span role="columnheader">{c("profile", "Profile")}</span>
            <span role="columnheader">{c("profession", "Profession")}</span>
            <span role="columnheader">{c("facilities", "Facilities")}</span>
            <span role="columnheader">{c("authority", "Surveillance authority")}</span>
            <span role="columnheader">{c("programmes", "Programmes")}</span>
            <span role="columnheader">{c("status", "Status")}</span>
            <span role="columnheader">{c("actions", "Actions")}</span>
          </div>
          {members.length ? members.map((member) => {
            const memberAssignments = assignments.filter((assignment) => assignment.institutionMemberId === member.id || assignment.userId === member.user_id);
            const activeAssignments = memberAssignments.filter((assignment) => assignment.status === "active");
            const facilityNames = activeAssignments.map((assignment) => assignment.orgUnitName).filter(Boolean);
            const hasActiveAuthority = activeAssignments.length > 0;
            const menuOpen = openActionMenuId === member.id;
            const memberActionItems = [
              { key: "history", label: c("viewHistory", "View authority history"), onClick: viewAuthorityHistory },
              { key: "remove-authority", label: c("removeAuthority", "Remove authority"), onClick: () => activeAssignments[0] ? revokeAssignment(activeAssignments[0]) : null, disabled: !hasActiveAuthority || pending },
              { key: "suspend", label: c("suspendMember", "Suspend member"), onClick: () => updateMemberStatus(member, "suspended"), disabled: getMemberStatus(member) !== "active" || pending || member.user_id === workspace.user?.id },
              { key: "remove-member", label: c("removeMember", "Remove member"), onClick: () => updateMemberStatus(member, "removed"), disabled: getMemberStatus(member) === "removed" || pending || member.user_id === workspace.user?.id, danger: true },
            ];
            return (
              <div className="institutions-surveillance-row" role="row" key={member.id || member.email}>
                <span role="cell" data-label={c("member", "Member")} className="institutions-member-identity">
                  <InstitutionMemberAvatar member={member} />
                  <span className="institutions-member-copy"><strong className="text-body-medium">{getMemberName(member)}</strong><small className="text-body-small">{getMemberEmail(member)}</small></span>
                  <MemberActionMenu
                    items={memberActionItems}
                    open={menuOpen}
                    label={c("moreActions", "More actions for {name}").replace("{name}", getMemberName(member))}
                    onToggle={() => setOpenActionMenuId(menuOpen ? "" : member.id)}
                    onRequestClose={() => setOpenActionMenuId("")}
                    triggerIcon={<InstitutionIcon src={ICONS.moreVert} size={20} useMask className="institutions-surveillance-action-icon" />}
                    className="institutions-surveillance-mobile-more"
                    menuTestId="institution-member-action-menu"
                  />
                </span>
                <span role="cell" data-label={c("institutionRole", "Profession / Institution role")}>{institutionControlledLabel(t, getMemberProfession(member))}</span>
                <span role="cell" data-label={c("facilityAuthority", "Facility authority")}>{facilityNames.length ? facilityNames.join(", ") : c("noFacilityAuthority", "No facility authority")}</span>
                <span role="cell" data-label={c("authority", "Surveillance authority")}>
                  {activeAssignments.length ? (
                    <span className="institutions-chip-stack">
                      {activeAssignments.map((assignment) => (
                        <StatusPill key={assignment.id} tone="info">{institutionControlledLabel(t, assignment.role)}</StatusPill>
                      ))}
                    </span>
                  ) : (
                    <StatusPill>{c("notAssigned", "Not assigned")}</StatusPill>
                  )}
                </span>
                <span role="cell" data-label={c("programmeScope", "Programme scope")}>{getProgrammeScope(activeAssignments, t)}</span>
                <span role="cell" data-label={c("status", "Status")}><StatusPill tone={getMemberStatus(member) === "active" ? "success" : "neutral"}>{institutionControlledLabel(t, getMemberStatus(member))}</StatusPill></span>
                <span role="cell" data-label={c("authorityActions", "Authority actions")} className="institutions-surveillance-actions">
                  <button
                    type="button"
                    className="institutions-surveillance-primary-action"
                    onClick={() => openAssign(member)}
                    disabled={!mappedFacilities.length || pending}
                  >
                    {hasActiveAuthority ? c("manageAuthority", "Manage authority") : c("assignAuthority", "Assign authority")}
                  </button>
                  <MemberActionMenu
                    items={memberActionItems}
                    open={menuOpen}
                    label={c("moreActions", "More actions for {name}").replace("{name}", getMemberName(member))}
                    onToggle={() => setOpenActionMenuId(menuOpen ? "" : member.id)}
                    onRequestClose={() => setOpenActionMenuId("")}
                    triggerIcon={<InstitutionIcon src={ICONS.moreVert} size={20} useMask className="institutions-surveillance-action-icon" />}
                    className="institutions-surveillance-desktop-more"
                    menuTestId="institution-member-action-menu"
                  />
                </span>
              </div>
            );
          }) : (
            <div className="institutions-surveillance-empty" role="row">
              <strong>{c("noMembersTitle", "No institution members yet")}</strong>
              <span>{c("noMembersBody", "Invite members before assigning surveillance authority.")}</span>
            </div>
          )}
        </div>

        <section className="institutions-surveillance-audit" id="institution-authority-history" aria-label={c("history", "Authority history")}>
          <div className="institutions-section-header">
            <h2 className="text-headline-small text-on-surface">{c("history", "Authority history")}</h2>
            <StatusPill>{c("appendOnly", "Append-only")}</StatusPill>
          </div>
          {historyRows.length ? (
            <div className="institutions-surveillance-event-list">
              {historyRows.map((row) => {
                const item = row.item;
                if (row.type === "observation-group" && row.items.length > 1) {
                  return (
                    <details className="institutions-surveillance-event-group" key={`observation-${authorityObservationKey(item)}`}>
                      <summary className="institutions-surveillance-event">
                        <span><InstitutionIcon src={ICONS.surveillance} size={18} className="institutions-surveillance-adaptive-icon" useMask /></span>
                        <div>
                          <strong className="text-title-medium">{getAuthorityEventTitle(item, t)}</strong>
                          <small className="text-body-small">{getAuthorityEventDetail(item)}</small>
                          <small className="text-body-small">{row.items.length} identical read observations that day · Expand for preserved raw events</small>
                          {getAuthorityEventReason(item) ? <small className="text-body-small">{getAuthorityEventReason(item)}</small> : null}
                        </div>
                        <time className="text-body-small">{formatDateTime(item.createdAt || item.created_at)}</time>
                      </summary>
                      <div className="institutions-surveillance-event-group-raw" aria-label="Raw reporting map observations">
                        {row.items.map((rawItem) => (
                          <div className="institutions-surveillance-event institutions-surveillance-event--raw" key={rawItem.id}>
                            <span><InstitutionIcon src={ICONS.surveillance} size={16} className="institutions-surveillance-adaptive-icon" useMask /></span>
                            <div>
                              <strong className="text-title-medium">{getAuthorityEventTitle(rawItem, t)}</strong>
                              <small className="text-body-small">{getAuthorityEventDetail(rawItem)}</small>
                              {getAuthorityEventReason(rawItem) ? <small className="text-body-small">{getAuthorityEventReason(rawItem)}</small> : null}
                            </div>
                            <time className="text-body-small">{formatDateTime(rawItem.createdAt || rawItem.created_at)}</time>
                          </div>
                        ))}
                      </div>
                    </details>
                  );
                }
                return (
                  <div className="institutions-surveillance-event" key={item.id}>
                    <span><InstitutionIcon src={ICONS.surveillance} size={18} className="institutions-surveillance-adaptive-icon" useMask /></span>
                    <div>
                      <strong className="text-title-medium">{getAuthorityEventTitle(item, t)}</strong>
                      <small className="text-body-small">{getAuthorityEventDetail(item)}</small>
                      {getAuthorityEventReason(item) ? <small className="text-body-small">{getAuthorityEventReason(item)}</small> : null}
                    </div>
                    <time className="text-body-small">{formatDateTime(item.createdAt || item.created_at)}</time>
                  </div>
                );
              })}
            </div>
          ) : (
            <SetupState>{c("noHistory", "No surveillance-authority events have been recorded for this institution yet.")}</SetupState>
          )}
        </section>
        {csvOpen ? <GovernedCsvImport title="Import surveillance authority from CSV" institutionId={institution.id} importType="surveillance" fields={[{ key: "email", label: "Member email", required: true, aliases: ["email"] }, { key: "facility", label: "Facility", required: true, aliases: ["facility name"] }, { key: "role", label: "Role", required: true }, { key: "programmeId", label: "Programme ID" }, { key: "startsAt", label: "Start date", aliases: ["start date"] }, { key: "endsAt", label: "End date", aliases: ["end date"] }, { key: "reason", label: "Reason" }]} onClose={() => setCsvOpen(false)} /> : null}
      </section>

      {assigningMember ? (
        <InstitutionSideSheet
          open
          title={c("assignAuthority", "Assign authority")}
          description={c("assignDescription", "{name} · Membership does not grant surveillance authority.").replace("{name}", getMemberName(assigningMember))}
          onClose={closeAssign}
          pending={pending}
          labelledBy="institution-assign-authority-title"
          variant="member institutions-side-sheet--authority"
          footer={
            <>
              <button type="button" className="institutions-side-sheet-cancel" onClick={closeAssign} disabled={pending}>{c("cancel", "Cancel")}</button>
              <button type="submit" form="institution-assign-authority-form" className="institutions-primary-button institutions-primary-button--tonal" disabled={pending || !mappedFacilities.length || !programmes.length}>
                {pending ? c("assigning", "Assigning…") : c("assignAuthority", "Assign authority")}
              </button>
            </>
          }
        >
          <form id="institution-assign-authority-form" className="institutions-side-sheet-form" onSubmit={submitAssignment}>
            <Select
              label={c("facility", "Facility")}
              value={form.facilityLinkId}
              options={mappedFacilities.map((facility) => ({ value: facility.id, label: `${getSnapshotFacilityName(facility)} · ${getSnapshotOrgUnitName(facility)}` }))}
              onValueChange={(value) => setForm((current) => ({ ...current, facilityLinkId: value }))}
              className="institutions-side-sheet-select"
              triggerClassName="institutions-side-sheet-select-trigger"
              leadingIcon={ICONS.fieldFacility}
              leadingIconMask
              required
            />
            <Select
              label={c("programmeScope", "Programme scope")}
              value={form.programmeId}
              options={programmes.map((programme) => ({ value: programme.id, label: programme.name || programme.code }))}
              onValueChange={(value) => setForm((current) => ({ ...current, programmeId: value }))}
              className="institutions-side-sheet-select"
              triggerClassName="institutions-side-sheet-select-trigger"
              leadingIcon={ICONS.fieldProgramme}
              leadingIconMask
              required
            />
            <Select
              label={c("authorityRole", "Authority role")}
              value={form.role}
              options={allowedRoles.map((role) => ({ value: role, label: institutionControlledLabel(t, role, formatRole(role)) }))}
              onValueChange={(value) => setForm((current) => ({ ...current, role: value }))}
              className="institutions-side-sheet-select"
              triggerClassName="institutions-side-sheet-select-trigger"
              leadingIcon={ICONS.fieldRole}
              leadingIconMask
              mobileSheet
              mobileSheetTitle={c("authorityRole", "Authority role")}
              required
            />
            <div className="institutions-side-sheet-date-grid institutions-side-sheet-date-grid--stacked">
              <TextField56 label={c("effectiveDate", "Effective date")} type="date" value={form.startsAt} onChange={(value) => setForm((current) => ({ ...current, startsAt: value }))} tone="surface" leadingIcon={ICONS.fieldDate} leadingIconMask />
              <TextField56 label={c("optionalExpiry", "Optional expiry")} type="date" value={form.endsAt} onChange={(value) => setForm((current) => ({ ...current, endsAt: value }))} tone="surface" leadingIcon={ICONS.fieldDate} leadingIconMask />
            </div>
            <label className="institutions-side-sheet-textarea-label">
              <span><img src={ICONS.fieldReason} alt="" aria-hidden="true" />{c("reason", "Reason")}</span>
              <textarea value={form.reason} onChange={(event) => setForm((current) => ({ ...current, reason: event.target.value }))} required rows={3} />
            </label>
          </form>
        </InstitutionSideSheet>
      ) : null}

      {inviteOpen ? (
        <InstitutionSideSheet
          open
          title={c("newMember", "New Member")}
          onClose={closeInvite}
          pending={pending}
          labelledBy="institution-new-member-title"
          variant="member"
          returnFocusRef={inviteTriggerRef}
          footer={
            <>
              <button type="button" className="institutions-side-sheet-cancel" onClick={() => closeInvite()} disabled={pending}>{c("cancel", "Cancel")}</button>
              <button type="submit" form="institution-invite-member-form" className="institutions-primary-button" disabled={pending || !inviteForm.email.trim() || !inviteForm.supervisorMemberId || !inviteForm.reason.trim()}>
                {pending ? c("inviting", "Inviting…") : c("sendScopedInvitation", "Send invitation")}
              </button>
            </>
          }
        >
          <form id="institution-invite-member-form" className="institutions-side-sheet-form medical-institution-admin-sheet" onSubmit={submitInvite}>
            <TextField56
              label={t("institutionMembers.email", "Email")}
              type="email"
              value={inviteForm.email}
              onChange={(value) => setInviteForm((current) => ({ ...current, email: value }))}
              tone="surface"
              className="institutions-member-field"
              leadingIcon={ICONS.clinicianEmail}
              inputProps={{ required: true, autoComplete: "email" }}
            />
            <Select
              label={c("orientationAudience", "Orientation audience")}
              value={inviteForm.audience}
              options={SCOPED_MEMBER_AUDIENCE_OPTIONS}
              onValueChange={(value) => setInviteForm((current) => ({ ...current, audience: value }))}
              className="institutions-side-sheet-select institutions-member-select"
              triggerClassName="institutions-side-sheet-select-trigger institutions-member-select-trigger"
              leadingIcon={ICONS.fieldRole}
              leadingIconMask
              mobileSheet
              mobileSheetTitle={c("orientationAudience", "Orientation audience")}
              required
            />
            <Select
              label={c("supervisor", "Responsible Institution administrator")}
              value={inviteForm.supervisorMemberId}
              options={supervisors.map((member) => ({ value: member.id, label: member.name || member.email || "Institution administrator" }))}
              onValueChange={(value) => setInviteForm((current) => ({ ...current, supervisorMemberId: value }))}
              className="institutions-side-sheet-select institutions-member-select"
              triggerClassName="institutions-side-sheet-select-trigger institutions-member-select-trigger"
              leadingIcon={ICONS.fieldRole}
              leadingIconMask
              mobileSheet
              mobileSheetTitle={c("supervisor", "Responsible Institution administrator")}
              required
            />
            <label className="institutions-side-sheet-textarea-label">
              <span><img src={ICONS.fieldReason} alt="" aria-hidden="true" />{c("invitationReason", "Invitation reason")}</span>
              <textarea value={inviteForm.reason} onChange={(event) => setInviteForm((current) => ({ ...current, reason: event.target.value }))} required rows={3} />
            </label>
            <div className="institutions-member-modal-copy">
              <p className="institutions-member-modal-note">{c("scopedInvitationNotice", "The invitee must have a verified account. Acceptance grants Institution membership only; facility, programme, authority role, dates and clinical actions remain separately governed.")}</p>
            </div>
          </form>
        </InstitutionSideSheet>
      ) : null}
    </PageShell>
  );
}

function CliniciansTable({ rows = [], onOpen }) {
  const { t } = useLanguage();
  return (
    <div className="institutions-clinicians-table" role="table" aria-label={t("institutionClinicians.searchGroup", "Clinicians")}>
      <div className="institutions-clinicians-header" role="row">
        <span role="columnheader" className="text-body-medium text-on-surface-variant">{t("institutionClinicians.columns.name", "Name")}</span>
        <span role="columnheader" className="text-body-medium text-on-surface-variant">{t("institutionClinicians.columns.email", "Email")}</span>
        <span role="columnheader" className="text-body-medium text-on-surface-variant">{t("institutionClinicians.columns.phone", "Phone")}</span>
        <span role="columnheader" className="text-body-medium text-on-surface-variant">{t("institutionClinicians.columns.professionSpecialty", "Profession / specialty")}</span>
        <span role="columnheader" className="text-body-medium text-on-surface-variant">{t("institutionClinicians.columns.status", "Status")}</span>
      </div>
      {rows.map((clinician) => <ClinicianTableRow key={clinician.id} clinician={clinician} onOpen={onOpen} />)}
    </div>
  );
}

export function ClinicianPickerField({ label, value, placeholder, options, onChange, className = "", leadingIcon, labelLeadingIcon, valueLeadingIcon, trailingIcon = ICONS.clinicianExpand, menuClassName = "", optionClassName = "", emptyLabelAsPlaceholder = false, alignLabelWithLeading = false, disabled = false }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const selected = options.find((option) => option.value === value);
  const selectedIndex = Math.max(0, options.findIndex((option) => option.value === value));
  const [activeIndex, setActiveIndex] = useState(selectedIndex);
  const selectOption = (option) => { if (disabled) return; onChange(option.value); setOpen(false); triggerRef.current?.focus(); };
  const openAt = (index) => { if (disabled) return; setActiveIndex(Math.max(0, Math.min(options.length - 1, index))); setOpen(true); };
  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
      if (event.type !== "keydown" && !rootRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", close);
    return () => { document.removeEventListener("mousedown", close); document.removeEventListener("keydown", close); };
  }, [open]);
  return <div ref={rootRef} className={`institution-clinician-picker-field ${className}`}>
    <TextField56 as="button" inputRef={triggerRef} label={label} value={selected?.label || ""} placeholder={placeholder} leadingIcon={valueLeadingIcon || leadingIcon} triggerLabelLeadingIcon={labelLeadingIcon} trailingSlot={<InstitutionIcon src={trailingIcon} size={18} />} onTriggerClick={() => { if (open) setOpen(false); else openAt(selectedIndex); }} triggerAriaHaspopup="listbox" triggerAriaExpanded={open} triggerTrailingInteractive disabled={disabled} triggerProps={{ onKeyDown: (event) => { if (event.key === "ArrowDown") { event.preventDefault(); if (!open) openAt(selectedIndex); else setActiveIndex((index) => Math.min(options.length - 1, index + 1)); } else if (event.key === "ArrowUp") { event.preventDefault(); if (!open) openAt(selectedIndex); else setActiveIndex((index) => Math.max(0, index - 1)); } else if (event.key === "Enter" && open && options[activeIndex]) { event.preventDefault(); selectOption(options[activeIndex]); } else if (event.key === "Escape" && open) { event.preventDefault(); setOpen(false); } } }} tone="surface" emptyLabelAsPlaceholder={emptyLabelAsPlaceholder} alignLabelWithLeading={alignLabelWithLeading} />
    {open ? <div className={`institution-clinician-picker-menu ${menuClassName}`.trim()} role="listbox" aria-label={label}>{options.map((option, index) => <button key={option.value} type="button" role="option" aria-selected={option.value === value} className={`${optionClassName}${index === activeIndex ? " is-active" : ""}`.trim()} onMouseEnter={() => setActiveIndex(index)} onClick={() => selectOption(option)}>{option.label}</button>)}</div> : null}
  </div>;
}

function ClinicianTableRow({ clinician, onOpen }) {
  const { t } = useLanguage();
  const name = clinicianDisplayName(clinician);
  const facilities = clinician?.facilities || [];
  const relationshipStatus = clinician?.relationshipStatus || clinician?.status || "draft";
  return (
    <button type="button" className="institutions-clinicians-row" role="row" onClick={() => onOpen?.(clinician.id)} aria-label={`${t("common.actions.open", "Open")} ${name}`}>
      <span className="institutions-clinician-name" role="cell" data-label={t("institutionClinicians.columns.name", "Name")}>
        <span className="institutions-clinician-list-avatar institutions-clinician-list-avatar--registry" aria-hidden="true"><img src={clinician?.imagePath || ICONS.clinicianListAvatar} alt="" /></span>
        <span>
          <strong className="text-body-medium text-on-surface">{name}</strong>
          <small className="text-body-small text-on-surface-variant">ID: CL-{clinicianReference(clinician)}{facilities.length ? ` · ${facilities.map((facility) => facility.name).join(", ")}` : ""}</small>
        </span>
      </span>
      <span className="institutions-clinician-icon-cell text-body-medium text-on-surface" role="cell" data-label={t("institutionClinicians.columns.email", "Email")}>
        <InstitutionIcon src={ICONS.clinicianEmail} size={18} />
        {clinician?.email || t("institutionClinicians.notSupplied", "Not provided")}
      </span>
      <span className="institutions-clinician-icon-cell text-body-medium text-on-surface" role="cell" data-label={t("institutionClinicians.columns.phone", "Phone")}>
        <InstitutionIcon src={ICONS.clinicianPhone} size={18} />
        {clinician?.phone || t("institutionClinicians.notSupplied", "Not provided")}
      </span>
      <span role="cell" className="text-body-medium text-on-surface" data-label={t("institutionClinicians.columns.professionSpecialty", "Profession / specialty")}><span className="institutions-clinician-profession">{clinicianProfessionLabel(clinician?.profession)}<small className="text-body-small text-on-surface-variant">{clinician?.specialty?.name || t("institutionClinicians.notSupplied", "Not supplied")}</small></span></span>
      <span role="cell" data-label={t("institutionClinicians.columns.status", "Status")}>
        <InstitutionLifecycleBadge status={relationshipStatus} label={clinicianRelationshipStatusLabel(t, relationshipStatus)} />
      </span>
    </button>
  );
}

const CLINICIAN_LANGUAGE_CSV_PATHS = [
  "/data/language_use_angola.csv",
  "/data/language_use_botswana.csv",
  "/data/language_use_mozambique.csv",
  "/data/language_use_namibia.csv",
  "/data/language_use_southafrica.csv",
  "/data/language_use_zambia.csv",
  "/data/language_use_zimbabwe.csv",
];

function ClinicianEditor({ workspace, clinician = null, onRefresh }) {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const c = (key, fallback, values) => t(`institutionClinicians.${CLINICIAN_EDITOR_MESSAGE_ALIASES[key] || key}`, fallback, values);
  const institutionId = workspace?.activeInstitution?.id || "";
  const [form, setForm] = useState(() => clinicianInitialForm(clinician));
  const [savedForm, setSavedForm] = useState(() => clinicianInitialForm(clinician));
  const [specialties, setSpecialties] = useState([]);
  const [specialtyOpen, setSpecialtyOpen] = useState(false);
  const [facilityOpen, setFacilityOpen] = useState(false);
  const [formationOpen, setFormationOpen] = useState(false);
  const [languagesOpen, setLanguagesOpen] = useState(false);
  const [dateOpen, setDateOpen] = useState(false);
  const [registrationDateOpen, setRegistrationDateOpen] = useState(false);
  const [overflowOpen, setOverflowOpen] = useState(false);
  const [action, setAction] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [invitation, setInvitation] = useState(null);
  const [invitationLoading, setInvitationLoading] = useState(false);
  const [educationDraft, setEducationDraft] = useState(EMPTY_CLINICIAN_EDUCATION_DRAFT);
  const [editingEducationIndex, setEditingEducationIndex] = useState(null);
  const [languageOptions, setLanguageOptions] = useState([]);
  const [completionDateOpen, setCompletionDateOpen] = useState(false);
  const [diplomaPickerOpen, setDiplomaPickerOpen] = useState(false);
  const [diplomaEvidence, setDiplomaEvidence] = useState(() => clinician?.registration?.evidence && Object.keys(clinician.registration.evidence).length ? clinician.registration.evidence : null);
  const [diplomaError, setDiplomaError] = useState("");
  const [formationEvidencePickerOpen, setFormationEvidencePickerOpen] = useState(false);
  const [formationEvidenceError, setFormationEvidenceError] = useState("");
  const [avatarPreview, setAvatarPreview] = useState("");
  const [avatarFile, setAvatarFile] = useState(null);
  const [clinicianCoverDisplayUrl, setClinicianCoverDisplayUrl] = useState("");
  const [identityState, setIdentityState] = useState(() => clinician?.id ? "resolved" : "idle");
  const [identityMatch, setIdentityMatch] = useState(null);
  const [linkedExistingClinician, setLinkedExistingClinician] = useState(false);
  const mobileCoverActions = useMobileSheetPresentation();
  const [specialtyQuery, setSpecialtyQuery] = useState("");
  const registrationDateTriggerRef = useRef(null);
  const registrationDateAnchorRef = useRef(null);
  const completionDateTriggerRef = useRef(null);
  const diplomaFileInputRef = useRef(null);
  const diplomaCameraInputRef = useRef(null);
  const formationEvidenceFileInputRef = useRef(null);
  const formationEvidenceCameraInputRef = useRef(null);
  const formIsSaved = Boolean(clinician?.id);
  const facilities = workspace?.clinicianFacilities || workspace?.linkedFacilities || [];
  const hasUnsavedChanges = !formIsSaved || Boolean(avatarFile) || JSON.stringify(form) !== JSON.stringify(savedForm);
  const identityResolved = formIsSaved || identityState === "no_match" || (identityState === "matched" && linkedExistingClinician);
  const canSave = Boolean(identityResolved && hasUnsavedChanges && form.firstName.trim() && form.lastName.trim() && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim()) && form.profession && form.facilityIds.length);
  const selectedPhoneCountry = clinicianPhoneCountry(form.phone, form.phoneCountry);
  const selectedPhoneMarket = getPhoneMarketByIso(selectedPhoneCountry) || getPhoneMarketFromE164(form.phone) || getPhoneMarketByIso("ZA");
  const phoneCc = selectedPhoneMarket?.dialCode || "+27";
  const salutationOptions = useMemo(() => DOCTOR_SALUTATION_OPTIONS.map((value) => ({
    value,
    label: value ? c({ "Dr.": "salutation.dr", PharmD: "salutation.pharmd", "Prof.": "salutation.prof", "Rn.": "salutation.rn", "Mr.": "salutation.mr", "Mrs.": "salutation.mrs", Miss: "salutation.miss", "Ms.": "salutation.ms", "Mme.": "salutation.mme" }[value], value) : c("noTitle", "No title"),
  })), [c]);

  useEffect(() => {
    const nextForm = clinicianInitialForm(clinician);
    setForm(nextForm);
    setSavedForm(nextForm);
    setAvatarFile(null);
    setAvatarPreview("");
    setIdentityState(clinician?.id ? "resolved" : "idle");
    setIdentityMatch(null);
    setLinkedExistingClinician(false);
  }, [clinician?.id]);

  useEffect(() => {
    let active = true;
    setClinicianCoverDisplayUrl("");
    if (!institutionId || !clinician?.id || !clinician?.clinicianProfileId) return () => { active = false; };
    loadInstitutionClinicianCoverDisplay({ institutionId, rosterId: clinician.id })
      .then((url) => { if (active) setClinicianCoverDisplayUrl(url || ""); })
      .catch(() => { if (active) setClinicianCoverDisplayUrl(""); });
    return () => { active = false; };
  }, [clinician?.clinicianProfileId, clinician?.id, institutionId]);

  useEffect(() => {
    let active = true;
    loadMedicalInstitutionNetwork({ institutionId }).then((network) => {
      if (active) setSpecialties(network?.specialties || []);
    }).catch(() => { if (active) setSpecialties([]); });
    return () => { active = false; };
  }, [institutionId]);

  const refreshInvitation = async () => {
    if (!clinician?.id || !institutionId) return;
    setInvitationLoading(true);
    try {
      const snapshot = await loadInstitutionClinicianInvitationManagerSnapshot({ institutionId });
      setInvitation((snapshot?.invitations || []).find((item) => item.rosterId === clinician.id) || null);
    } catch {
      setInvitation(null);
    } finally {
      setInvitationLoading(false);
    }
  };

  useEffect(() => { refreshInvitation(); }, [clinician?.id, institutionId]);

  const manageInvitation = async (operation) => {
    if (!clinician?.id || busy) return;
    setBusy(true); setError("");
    try {
      if (operation === "send") await sendInstitutionClinicianInvitation({ institutionId, rosterId: clinician.id });
      if (operation === "resend" && invitation?.id) await resendInstitutionClinicianInvitation({ institutionId, invitationId: invitation.id });
      if (operation === "revoke" && invitation?.id) await revokeInstitutionClinicianInvitation({ institutionId, invitationId: invitation.id });
      await refreshInvitation();
      setNotice(operation === "revoke" ? "Clinician invitation revoked." : operation === "resend" ? "Clinician invitation resent." : "Clinician invitation sent.");
    } catch (err) {
      setError(getInstitutionClinicianIntakeErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const relationshipStatus = clinicianRelationshipStatus(clinician, invitation);
  const relationshipFacilityName = (clinician?.facilities || []).find((facility) => facility?.status !== "removed")?.name || institutionDisplayName(workspace?.activeInstitution) || "this Institution";
  const relationshipName = clinicianDisplayName(clinician);
  const relationshipState = (() => {
    const variables = { clinician: relationshipName, facility: relationshipFacilityName };
    if (invitationLoading) return { body: c("relationship.loading", "Checking the current clinician relationship.") };
    if (relationshipStatus === "active") return { body: c("relationship.active", "{clinician} has accepted the {facility} clinician relationship.", variables) };
    if (relationshipStatus === "invitation_pending") return { body: c("relationship.pending", "{clinician} has not yet accepted the {facility} clinician relationship.", variables), action: "pending" };
    if (relationshipStatus === "declined") return { body: c("relationship.declined", "{clinician} declined the {facility} clinician relationship.", variables), action: "send" };
    if (relationshipStatus === "expired") return { body: c("relationship.expired", "The invitation for {clinician} to review the {facility} clinician relationship expired.", variables), action: "resend" };
    if (relationshipStatus === "revoked") return { body: c("relationship.revoked", "The invitation for {clinician} to review the {facility} clinician relationship was revoked.", variables), action: "send" };
    if (relationshipStatus === "suspended") return { body: c("relationship.suspended", "{clinician}’s {facility} clinician relationship is suspended.", variables) };
    if (relationshipStatus === "removed") return { body: c("relationship.removed", "{clinician}’s {facility} clinician relationship was removed.", variables) };
    return { body: c("relationship.notSent", "Send an invitation to {clinician} so they can review and accept the {facility} clinician relationship.", variables), action: "send" };
  })();

  useEffect(() => {
    let active = true;
    loadLanguagesFromCSVs(CLINICIAN_LANGUAGE_CSV_PATHS).then((items) => {
      if (active) setLanguageOptions(items);
    }).catch(() => { if (active) setLanguageOptions([]); });
    return () => { active = false; };
  }, []);

  const resetIdentityResolution = () => {
    if (formIsSaved) return;
    setIdentityState("idle");
    setIdentityMatch(null);
    setLinkedExistingClinician(false);
  };
  const update = (key, value) => {
    if (key === "email" || key === "registrationNumber") resetIdentityResolution();
    setForm((current) => ({ ...current, [key]: value }));
  };
  const findExistingClinician = async () => {
    if (busy) return;
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) {
      setIdentityState("invalid_identifier");
      setIdentityMatch(null);
      return;
    }
    setBusy(true);
    setError("");
    setIdentityState("searching");
    setLinkedExistingClinician(false);
    try {
      const result = await findInstitutionClinicianExactMatch({
        institutionId,
        email: form.email.trim(),
        registrationNumber: form.registrationNumber.trim(),
      });
      setIdentityState(result?.state || "no_match");
      setIdentityMatch(result?.match || null);
    } catch (err) {
      setIdentityState(err?.code === "42501" ? "denied" : "error");
      setIdentityMatch(null);
    } finally {
      setBusy(false);
    }
  };
  const useExistingClinician = () => {
    if (!identityMatch?.clinicianProfileId) return;
    setForm((current) => ({
      ...current,
      firstName: identityMatch.firstName || current.firstName,
      lastName: identityMatch.lastName || current.lastName,
      institutionImage: null,
    }));
    setAvatarFile(null);
    setAvatarPreview("");
    setLinkedExistingClinician(true);
  };
  const identityMessage = {
    invalid_identifier: c("exactMatch.invalidIdentifier", "Enter a valid email address; add the professional registration number where available."),
    searching: c("exactMatch.searching", "Checking the verified clinician identity…"),
    no_match: c("exactMatch.noMatch", "No verified Global clinician matched exactly. Continue with the Institution draft below."),
    identifier_mismatch: c("exactMatch.identifierMismatch", "The verified email and professional registration do not identify the same clinician."),
    ambiguous: c("exactMatch.ambiguous", "These identifiers cannot be resolved to one clinician. No relationship was created."),
    already_linked: c("exactMatch.alreadyLinked", "This clinician is already linked to the active Institution."),
    pending: c("exactMatch.pending", "A pending relationship for this clinician already exists in the active Institution."),
    denied: c("exactMatch.denied", "You are not authorised to search or link clinicians for this Institution."),
    error: c("exactMatch.failed", "The protected clinician lookup could not be completed. Try again."),
  }[identityState] || "";
  const closeFormation = () => {
    setCompletionDateOpen(false);
    setFormationEvidencePickerOpen(false);
    setFormationEvidenceError("");
    setFormationOpen(false);
    setEditingEducationIndex(null);
    setEducationDraft(EMPTY_CLINICIAN_EDUCATION_DRAFT);
  };
  const openNewFormation = () => {
    setEditingEducationIndex(null);
    setEducationDraft(EMPTY_CLINICIAN_EDUCATION_DRAFT);
    setFormationOpen(true);
  };
  const openFormationEdit = (item, index) => {
    setEditingEducationIndex(index);
    setEducationDraft({
      title: String(item?.title || ""),
      institution: String(item?.institution || ""),
      country: String(item?.country || ""),
      completionDate: String(item?.completionDate || ""),
      year: String(item?.year || ""),
      evidence: item?.evidence && typeof item.evidence === "object" ? item.evidence : null,
    });
    setFormationOpen(true);
  };
  const saveEducation = () => {
    if (!educationDraft.title.trim()) return;
    const nextEducation = { ...educationDraft, title: educationDraft.title.trim() };
    update("education", Number.isInteger(editingEducationIndex)
      ? form.education.map((item, index) => index === editingEducationIndex ? nextEducation : item)
      : [...form.education, nextEducation]);
    closeFormation();
  };
  const focusCatalogue = useMemo(() => specialties.map((item) => ({ ...item, id: item.id || item.name })), [specialties]);
  const focusRecords = useMemo(() => normaliseClinicianFocusAreas(form.focusAreas, focusCatalogue), [form.focusAreas, focusCatalogue]);
  const focusSelectedIds = focusRecords.filter((item) => !item.isCustom && !item.unresolved).map((item) => item.id);
  const focusCustom = focusRecords.filter((item) => item.isCustom || item.unresolved).map((item) => ({ id: item.unresolved ? `unresolved:${item.id}` : item.id, name: item.label }));
  const updateFocusAreas = (canonicalIds, customItems) => update("focusAreas", serialiseClinicianFocusAreas(canonicalIds, customItems, focusCatalogue));
  useEffect(() => {
    if (import.meta.env.DEV) focusRecords.filter((item) => item.unresolved).forEach((item) => console.warn("Unresolved Institution clinician focus area", item.id));
  }, [focusRecords]);
  const languageCatalogue = useMemo(() => languageOptions.map((name) => ({ id: name, name })), [languageOptions]);
  const languageCanonicalNames = useMemo(() => new Set(languageCatalogue.map((item) => item.name)), [languageCatalogue]);
  const languageDrafts = normaliseInstitutionLanguageDrafts(form.languages);
  const setLanguageDrafts = (items) => update("languages", normaliseInstitutionLanguageDrafts(items));
  const languageSelectedIds = languageDrafts.map((item) => item.language || item).filter((name) => languageCanonicalNames.has(name));
  const languageCustom = languageDrafts.filter((item) => !languageCanonicalNames.has(item.language || item)).map((item) => ({ name: item.language || item }));
  const mergeLanguageDrafts = (canonicalNames, customNames) => {
    const existing = new Map(languageDrafts.map((item) => [item.language || item, typeof item === "string" ? { language: item } : item]));
    return normaliseInstitutionLanguageDrafts([...canonicalNames, ...customNames].map((language) => existing.get(language) || { language }));
  };
  const removeLanguage = (language) => setLanguageDrafts(languageDrafts.filter((item) => (item.language || item) !== language));
  const chooseDiploma = (file) => {
    if (!file) return;
    const allowed = ["application/pdf", "image/jpeg", "image/png", "image/webp"];
    if (!allowed.includes(file.type) || file.size > 10 * 1024 * 1024) {
      setDiplomaError(c("diplomaFileError", "Choose a PDF, JPG, PNG or WEBP file up to 10 MB."));
      return;
    }
    setDiplomaError("");
    setDiplomaEvidence({ fileName: file.name, mimeType: file.type, size: file.size, uploadState: "selected_for_roster_draft", file });
    setDiplomaPickerOpen(false);
    setNotice(c("diplomaSelected", "Diploma selected for this Institution roster draft."));
  };
  const removeDiploma = () => {
    setDiplomaEvidence(null);
    setDiplomaError("");
    setDiplomaPickerOpen(false);
    setNotice(c("diplomaRemoved", "Diploma removed from this Institution roster draft."));
  };
  const chooseFormationEvidence = (file) => {
    if (!file) return;
    const allowed = ["application/pdf", "image/jpeg", "image/png", "image/webp"];
    if (!allowed.includes(file.type) || file.size > 10 * 1024 * 1024) {
      setFormationEvidenceError(c("diplomaFileError", "Choose a PDF, JPG, PNG or WEBP file up to 10 MB."));
      return;
    }
    setFormationEvidenceError("");
    setEducationDraft((current) => ({ ...current, evidence: { fileName: file.name, mimeType: file.type, size: file.size, uploadState: "selected_for_formation", file } }));
    setFormationEvidencePickerOpen(false);
  };
  const removeFormationEvidence = () => {
    setEducationDraft((current) => ({ ...current, evidence: null }));
    setFormationEvidenceError("");
    setFormationEvidencePickerOpen(false);
  };
  const prepareEvidenceRoster = async () => {
    const uploadedPaths = [];
    const upload = async (candidate, kind, imageOnly = false) => {
      if (!candidate?.file) return candidate ? (({ file, previewUrl, ...metadata }) => metadata)(candidate) : null;
      const metadata = await uploadInstitutionClinicianEvidence({ institutionId, file: candidate.file, kind, imageOnly });
      uploadedPaths.push(metadata.path);
      return metadata;
    };
    const institutionImage = linkedExistingClinician
      ? null
      : avatarFile
      ? await upload({ file: avatarFile }, "roster-images", true)
      : form.institutionImage;
    const registrationEvidence = await upload(diplomaEvidence, "registration-evidence");
    const education = await Promise.all(form.education.map(async (item) => ({
      ...item,
      evidence: await upload(item?.evidence, "formation-evidence"),
    })));
    return {
      roster: {
        ...form,
        // Older roster snapshots can contain a dial-code-prefixed number
        // without its E.164 plus sign. The controlled phone field always
        // renders a local number, so normalise only at the service boundary.
        phone: clinicianPhoneE164(form.phone),
        institutionImage,
        registrationEvidence,
        education,
      },
      uploadedPaths,
    };
  };
  const evidencePathsFor = (source) => [
    source?.institutionImage?.path,
    source?.registrationEvidence?.path,
    ...(Array.isArray(source?.education) ? source.education.map((item) => item?.evidence?.path) : []),
  ].filter(Boolean);
  const submit = async (event) => {
    event.preventDefault();
    if (!canSave || busy) return;
    setBusy(true); setError("");
    let prepared = null;
    try {
      prepared = await prepareEvidenceRoster();
      const result = formIsSaved
        ? await updateInstitutionClinician({ institutionId, rosterId: clinician.id, roster: prepared.roster })
        : await createInstitutionClinician({ institutionId, roster: {
          ...prepared.roster,
          sourceType: "manual",
          identityResolution: linkedExistingClinician ? "matched" : "no_match",
          clinicianProfileId: linkedExistingClinician ? identityMatch?.clinicianProfileId : "",
        } });
      if (result?.alreadyExists) {
        await Promise.all(prepared.uploadedPaths.map((path) => removeInstitutionClinicianEvidence(path).catch(() => {})));
      } else if (formIsSaved) {
        const priorPaths = evidencePathsFor({
          institutionImage: clinician?.institutionImage,
          registrationEvidence: clinician?.registration?.evidence,
          education: clinician?.education,
        });
        const nextPaths = new Set(evidencePathsFor(prepared.roster));
        await Promise.all(priorPaths.filter((path) => !nextPaths.has(path)).map((path) => removeInstitutionClinicianEvidence(path).catch(() => {})));
      }
      setForm(prepared.roster);
      setSavedForm(prepared.roster);
      setAvatarFile(null);
      setNotice(result?.alreadyExists ? c("alreadyExists", "This clinician already has an Institution roster relationship.") : formIsSaved ? c("updated", "Institution clinician relationship updated.") : c("savedDraft", "Clinician roster draft saved. No account or invitation was created."));
      onRefresh?.();
      if (!formIsSaved && result?.id) navigate(clinicianPath(institutionId, result.id), { replace: true });
    } catch (err) {
      if (prepared?.uploadedPaths?.length) await Promise.all(prepared.uploadedPaths.map((path) => removeInstitutionClinicianEvidence(path).catch(() => {})));
      setError(getInstitutionClinicianIntakeErrorMessage(err));
    }
    finally { setBusy(false); }
  };
  const runStatusAction = async () => {
    if (!action || !reason.trim() || busy || !clinician?.id) return;
    setBusy(true); setError("");
    try {
      await changeInstitutionClinicianStatus({ institutionId, rosterId: clinician.id, status: action, reason });
      setAction(""); setReason(""); onRefresh?.();
      navigate(clinicianPath(institutionId), { replace: true });
    } catch (err) { setError(err?.message || c("statusError", "The Institution relationship could not be changed.")); }
    finally { setBusy(false); }
  };
  const copyInternalLink = async () => {
    const link = typeof window === "undefined" ? "" : window.location.href;
    try {
      await navigator.clipboard?.writeText(link);
      setNotice(c("linkCopied", "Internal clinician relationship link copied."));
    } catch {
      setNotice(c("linkCopyFallback", "Copy the clinician relationship URL from your browser address bar."));
    }
    setOverflowOpen(false);
  };

  return (
    <PageShell workspace={workspace} title={c("title", "Clinicians")} icon={Stethoscope} clinicianSearch profileDetail contentClassName="afrimed-rail pb-24">
      <form className="institution-clinician-editor" onSubmit={submit}>
        <InstitutionProfileCover
          src={clinicianCoverDisplayUrl}
          fallbackSrc="/icons/doctor/EditIntro_banner.svg"
          avatarSrc={avatarPreview || clinician?.imagePath || ""}
          avatarAlt={clinicianDisplayName(clinician || form)}
          avatarRailClassName="afrimed-page"
          avatarPlaceholderSrc="/icons/Profile_Avatar_borderless.svg"
        >
        <div className="mt-16 md:mt-20 lg:mt-24 w-full flex items-center justify-between pointer-events-auto">
          <MedicalInstitutionBackButton label={c("backToClinicians", "Back to Clinicians")} onClick={() => navigate(clinicianPath(institutionId))} />
          <div className="institution-profile-cover-actions flex items-center gap-3">
            {formIsSaved ? <div className="institution-clinician-overflow-wrap"><button type="button" className="institutions-icon-button institution-clinician-more-button" aria-label={c("moreActions", "More clinician actions")} aria-expanded={overflowOpen} aria-haspopup="menu" onClick={() => setOverflowOpen((current) => !current)}><img src={INSTITUTION_PROFILE_MORE_VERT_ICON} alt="" /></button>{overflowOpen && !mobileCoverActions ? <div className="institution-clinician-overflow-menu" role="menu" aria-label={c("moreActions", "Clinician relationship actions")}><button type="button" role="menuitem" onClick={copyInternalLink}><InstitutionIcon src={ICONS.clinicianLink} size={20} />{c("copyLink", "Copy link")}</button><button type="button" role="menuitem" onClick={() => { window.open(window.location.href, "_blank", "noopener,noreferrer"); setOverflowOpen(false); }}><InstitutionIcon src={ICONS.clinicianNewTab} size={20} />{c("openInNewTab", "Open in new tab")}</button><span role="separator" /><button type="button" role="menuitem" className="is-destructive" onClick={() => { setAction("suspended"); setOverflowOpen(false); }}><InstitutionIcon src={ICONS.clinicianReport} size={20} />{c("blockClinician", "Block Clinician")}</button><button type="button" role="menuitem" className="is-destructive" onClick={() => { setAction("removed"); setOverflowOpen(false); }}><InstitutionIcon src={ICONS.clinicianDelete} size={20} />{c("delete", "Delete")}</button></div> : null}</div> : null}
            <div className={`${formIsSaved ? "hidden md:flex" : "flex"} items-center gap-3`}><Button type="button" variant="ghost" className="institution-cover-action-button" onClick={() => navigate(clinicianPath(institutionId))} disabled={busy || (formIsSaved && !hasUnsavedChanges)}>{c("cancel", "Cancel")}</Button><Button type="submit" variant="default" className="institution-cover-action-button" loading={busy} disabled={!canSave}>{c("save", "Save")}</Button></div>
          </div>
        </div>
        </InstitutionProfileCover>
        <div className="afrimed-rail afrimed-page pt-4 pb-12">
        <div className="relative min-h-[40px]" aria-hidden="true" />
        <div className="mt-2 md:mt-14 flex items-start justify-between">
          <div>
            <h1 className="text-headline-small text-on-surface">{formIsSaved ? clinicianDisplayName(clinician) : c("newClinician", "New Clinician")}</h1>
            <p className="mt-1 text-body-medium text-on-surface-var">{clinician?.specialty?.name || form.specialtyRequested || c("specialtyPending", "Specialty not supplied")}</p>
          </div>
        </div>
        {formIsSaved && mobileCoverActions && hasUnsavedChanges ? <div className="mt-4 flex gap-3"><Button type="button" variant="ghost" onClick={() => navigate(clinicianPath(institutionId))} disabled={busy}>{c("cancel", "Cancel")}</Button><Button type="submit" variant="default" loading={busy} disabled={!canSave}>{c("save", "Save")}</Button></div> : null}
        {error ? <div className="medical-institution-error medical-institution-error--compact" role="alert"><p>{error}</p></div> : null}
        <div className="institution-clinician-editor-body mt-5">
          {!formIsSaved ? <section className="medical-institution-form-section institution-clinician-find-section" aria-labelledby="institution-clinician-find-title">
            <Button id="institution-clinician-find-title" type="button" variant="tonal" className="institution-clinician-find-action" onClick={findExistingClinician} disabled={busy}>{c("exactMatch.action", "Find existing clinician")}</Button>
            <p className="text-body-medium text-on-surface-variant">{c("exactMatch.help", "Search with a verified email address or exact professional registration number before starting a new clinician form.")}</p>
            <div className="medical-institution-form-grid institution-clinician-find-grid">
              <TextField56 label={c("email", "Email")} type="email" value={form.email} onChange={(value) => update("email", value)} tone="surface" clearable clearLabel={c("clearEmail", "Clear Email")} leadingIcon={ICONS.clinicianEmail} inputProps={{ autoComplete: "email" }} />
              <TextField56 label={c("registrationNumber", "Professional registration number")} value={form.registrationNumber} onChange={(value) => update("registrationNumber", value)} tone="surface" clearable clearLabel={c("exactMatch.clearRegistration", "Clear professional registration number")} />
            </div>
            {identityMessage ? <p className="institution-patient-notice institution-patient-notice--inline" role={identityState === "no_match" || identityState === "searching" ? "status" : "alert"}>{identityMessage}</p> : null}
            {identityMatch && ["matched", "already_linked", "pending"].includes(identityState) ? <div className="institution-patient-match-card institution-clinician-match-card">
              <img src="/icons/Profile_Avatar_borderless.svg" alt="" />
              <div><strong>{identityMatch.fullName || [identityMatch.firstName, identityMatch.lastName].filter(Boolean).join(" ") || c("exactMatch.verifiedClinician", "Verified clinician")}</strong><span>{[identityMatch.maskedEmail, identityMatch.registrationEnding ? c("exactMatch.registrationEnding", "Registration ending {ending}", { ending: identityMatch.registrationEnding }) : ""].filter(Boolean).join(" · ")}</span></div>
              {identityState === "matched" ? <Button type="button" variant="tonal" onClick={useExistingClinician} disabled={linkedExistingClinician}>{linkedExistingClinician ? c("exactMatch.selected", "Selected") : c("exactMatch.useClinician", "Use this clinician")}</Button> : null}
            </div> : null}
          </section> : null}
          <section className="medical-institution-form-section institution-clinician-basic-information-section"><h2 className="text-title-medium text-on-surface-variant">{c("basicInformation", "Basic Information")}</h2><div className="medical-institution-form-grid institution-clinician-basic-grid">
            <DoctorSalutationDropdown id="institution-clinician-salutation" name="salutation" label={c("professionalTitle", "Title")} value={form.salutation} placeholder={c("selectTitle", "Select title")} options={salutationOptions} onChange={(event) => update("salutation", event?.target?.value || "")} tone="surface" mobileSheet mobileSheetTitle={c("professionalTitle", "Title")} />
            <TextField56 label={`${c("firstName", "First name")}*`} value={form.firstName} onChange={(value) => update("firstName", value)} tone="surface" readOnly={linkedExistingClinician} clearable={!linkedExistingClinician} clearLabel={c("clearFirstName", "Clear First name")} inputProps={{ autoComplete: "given-name" }} />
            <TextField56 label={`${c("lastName", "Last name")}*`} value={form.lastName} onChange={(value) => update("lastName", value)} tone="surface" readOnly={linkedExistingClinician} clearable={!linkedExistingClinician} clearLabel={c("clearLastName", "Clear Last name")} inputProps={{ autoComplete: "family-name" }} />
            <TextField56 label={`${c("email", "Email")}*`} type="email" value={form.email} onChange={(value) => update("email", value)} tone="surface" readOnly={linkedExistingClinician} clearable={!linkedExistingClinician} clearLabel={c("clearEmail", "Clear Email")} leadingIcon={ICONS.clinicianEmail} inputProps={{ autoComplete: "email" }} />
            <InputPhoneSADC countryCodeLabel={c("countryCode", "Country code")} numberLabel={c("phoneNumber", "Phone number")} cc={phoneCc} countryIso={selectedPhoneCountry} onChangeCountry={(iso, market) => {
              const nextDialCode = market?.dialCode || getPhoneMarketByIso(iso)?.dialCode || phoneCc;
              const localNumber = clinicianPhoneDigits(form.phone, phoneCc);
              update("phoneCountry", String(iso || "").toUpperCase());
              update("phone", localNumber ? `${nextDialCode}${localNumber}` : "");
            }} value={clinicianPhoneDigits(form.phone, phoneCc)} onChange={(value) => update("phone", value ? `${phoneCc}${value}` : "")} tone="surface" className="institution-clinician-phone" />
            <ClinicianPickerField className="institution-clinician-gender" label={c("gender", "Gender")} value={form.gender} placeholder={c("selectOption", "Select an option")} options={[{ value: "", label: c("selectOption", "Select an option") }, { value: "female", label: c("female", "Female") }, { value: "male", label: c("male", "Male") }, { value: "nonbinary", label: c("nonBinary", "Non-binary") }, { value: "prefer_not_to_say", label: c("preferNotToSay", "Prefer not to say") }]} onChange={(value) => update("gender", value)} />
            <div className="institution-clinician-dob-anchor"><TextField56 label={c("dateOfBirth", "Date of birth")} as="button" value={formatClinicianDate(form.dateOfBirth)} placeholder={c("selectDate", "Select date")} onTriggerClick={() => setDateOpen(true)} triggerAriaLabel={c("dateOfBirth", "Date of birth")} triggerAriaHaspopup="dialog" triggerAriaExpanded={dateOpen} tone="surface" leadingIcon={ICONS.clinicianDate} />{dateOpen ? <div className="institution-clinician-date-picker" role="dialog" aria-label={c("dateOfBirth", "Date of birth")}><DatePicker value={form.dateOfBirth ? new Date(`${form.dateOfBirth}T12:00:00`) : null} maxDate={new Date()} commitOnSelect={false} onConfirm={(date) => { if (date && date <= new Date()) update("dateOfBirth", date.toISOString().slice(0, 10)); setDateOpen(false); }} onCancel={() => setDateOpen(false)} /></div> : null}</div>
            <TextField56 className="institution-clinician-experience-tf56" label={c("yearsOfExperience", "Years of experience")} value={form.yearsOfExperience} inputMode="numeric" leadingIcon={ICONS.clinicianExperience} trailingSlot={<span className="institution-clinician-stepper"><button type="button" aria-label={c("increaseYears", "Increase years of experience")} onClick={() => update("yearsOfExperience", String(Math.min(80, Number(form.yearsOfExperience || 0) + 1)))}><span className="institution-clinician-stepper-up" /></button><button type="button" aria-label={c("decreaseYears", "Decrease years of experience")} onClick={() => update("yearsOfExperience", String(Math.max(0, Number(form.yearsOfExperience || 0) - 1)))}><span className="institution-clinician-stepper-down" /></button></span>} trailingSlotFullHeight onChange={(value) => update("yearsOfExperience", value.replace(/[^0-9]/g, ""))} tone="surface" />
            <div className="institution-clinician-facility-field"><TextField56 label={`${c("assignedFacilities", "Assigned facilities")}*`} as="button" value={form.facilityIds.length ? `${form.facilityIds.length} ${c("selected", "selected")}` : c("selectFacilities", "Select facilities")} onTriggerClick={() => setFacilityOpen(true)} triggerAriaLabel={c("assignedFacilities", "Assign Medical Institution facilities")} triggerAriaHaspopup="dialog" triggerAriaExpanded={facilityOpen} tone="surface" />{!form.facilityIds.length ? <p className="institution-clinician-facility-validation" role="alert">{c("facilitySelectionRequired", "Select at least one active Medical Institution before saving.")}</p> : null}</div>
          </div></section>
          <div className="institution-clinician-detail-section-stack">
  {formIsSaved ? <section className="medical-institution-form-section institution-clinician-relationship-section" aria-label={c("relationship.title", "Institution relationship")}><h2 className="text-title-medium text-on-surface-variant">{c("relationship.title", "Institution relationship")}</h2><div className="institution-clinician-relationship"><p className="text-body-medium text-on-surface-variant">{relationshipState.body}{invitation?.expiresAt && relationshipStatus === "invitation_pending" ? ` ${c("relationship.expires", "Expires {date}.", { date: new Date(invitation.expiresAt).toLocaleDateString() })}` : ""}</p>{relationshipState.action === "pending" ? <span className="institution-clinician-relationship-actions"><Button type="button" variant="tonal" onClick={() => manageInvitation("resend")} disabled={busy || invitationLoading}>{c("relationship.resend", "Resend")}</Button><Button type="button" variant="ghost" onClick={() => manageInvitation("revoke")} disabled={busy || invitationLoading}>{c("relationship.revoke", "Revoke")}</Button></span> : relationshipState.action === "resend" ? <Button type="button" variant="tonal" onClick={() => manageInvitation("resend")} disabled={busy || invitationLoading}>{c("relationship.resend", "Resend")}</Button> : relationshipState.action === "send" ? <Button type="button" variant="tonal" onClick={() => manageInvitation("send")} disabled={busy || invitationLoading}>{c("relationship.send", "Send invitation to clinician")}</Button> : invitationLoading ? null : <InstitutionLifecycleBadge status={relationshipStatus} label={clinicianRelationshipStatusLabel(t, relationshipStatus)} className="institution-clinician-relationship-status" />}</div></section> : null}
          <section className="medical-institution-form-section institution-clinician-specialty-section"><h2 className="text-title-medium text-on-surface-variant">{c("specialty", "Specialty")}</h2>{form.specialtyId || form.specialtyRequested ? <div className="institution-clinician-specialty-summary"><img src={ICONS.clinicianStamp} alt="" /><span><span className="text-body-large">{specialties.find((item) => item.id === form.specialtyId)?.name || form.specialtyRequested || clinician?.specialty?.name || c("notSupplied", "Not supplied")}</span><small>{[form.registrationNumber && `${c("registrationNumber", "Registration number")}: ${form.registrationNumber}`, formatClinicianDate(form.registrationDate) || form.registrationYear, form.registrationJurisdiction].filter(Boolean).join(" · ")}</small></span><Button type="button" variant="tonal" onClick={() => setSpecialtyOpen(true)}>{c("edit", "Edit")}</Button></div> : <div className="institution-clinician-blueprint-row"><InstitutionIcon src={ICONS.clinicianDiploma} size={24} /><span><strong>{c("addSpecialty", "Add the specialty of the clinician")}</strong><small>{c("specialtySupport", "Make sure to have certification documents at hand.")}</small></span><Button type="button" variant="tonal" onClick={() => setSpecialtyOpen(true)}>{c("add", "Add")}</Button></div>}</section>
          <section className="medical-institution-form-section"><div className="institution-clinician-section-row"><div><h2 className="text-title-medium text-on-surface-variant">{c("focusOn", "Focus On")}</h2><p className="text-body-medium">{c("focusSupport", "Medical areas of knowledge")}</p></div></div><MedicalInstitutionSpecialties className="institution-clinician-focus-picker" heading="" menuId="institution-clinician-focus-menu" searchPlaceholder={c("searchOrAdd", "Search or add")} addLabel={c("add", "Add")} addIconSrc={ICONS.clinicianFormationAdd} clearSearch clearSearchLabel={c("clearFocusSearch", "Clear Focus On search")} mobileSheetTitle={c("focusOn", "Focus On")} helperText={c("startTyping", "Start typing to search or add a specialty.")} specialties={focusCatalogue} selectedIds={focusSelectedIds} customSpecialties={focusCustom} onChange={(ids) => updateFocusAreas(ids, focusCustom)} onCustomChange={(items) => updateFocusAreas(focusSelectedIds, items)} /></section>
          <section className="medical-institution-form-section institution-clinician-formation-section"><div className="institution-clinician-section-row"><h2 className="text-title-medium text-on-surface-variant">{c("formation", "Formation")}</h2><button type="button" className="institution-clinician-header-add" aria-label={c("addFormation", "Add qualification")} onClick={openNewFormation}><InstitutionIcon src={ICONS.clinicianFormationAdd} size={24} /></button></div>{form.education.length ? form.education.map((item, index) => <button type="button" className="institution-clinician-formation-row" key={`${item.title}-${index}`} onClick={() => openFormationEdit(item, index)} aria-label={`${c("edit", "Edit")} ${item.title || c("formation", "Formation")}`}><ClinicianFormationThumbnail evidence={item.evidence} /><span className="institution-clinician-formation-copy"><span className="text-body-large">{item.title || c("detailsPending", "Details pending")}</span><small>{[item.institution, item.country].filter(Boolean).join(" · ") || c("detailsPending", "Details pending")}</small><small>{formatClinicianDate(item.completionDate) || item.year || c("detailsPending", "Details pending")}</small></span><ChevronRight size={20} aria-hidden="true" /></button>) : <div className="institution-clinician-formation-empty"><ClinicianFormationThumbnail /><p>{c("formationEmpty", "Add the highest level of education and any relevant professional qualifications.")}</p></div>}</section>
          <section className="medical-institution-form-section institution-clinician-languages-section"><div><h2 className="text-title-medium text-on-surface-variant">{c("languages", "Languages")}</h2>{!languageDrafts.length ? <p className="text-body-medium">{c("languagesEmpty", "Add the language spoken by the clinician.")}</p> : null}</div><div className="institution-clinician-language-chip-row">{languageDrafts.map((item) => <FocusChip key={item.language || item} label={item.language || item} removable onRemove={() => removeLanguage(item.language || item)} />)}<button type="button" className="medical-institution-add-chip" onClick={() => setLanguagesOpen(true)}><Plus size={18} aria-hidden="true" />{c("add", "Add")}</button></div></section>
          </div>
        </div>
        </div>
      </form>
      {formIsSaved && mobileCoverActions ? <InstitutionSideSheet open={overflowOpen} title={c("moreActions", "Clinician actions")} onClose={() => setOverflowOpen(false)} labelledBy="institution-clinician-cover-actions" variant="cover-media"><div className="institution-cover-media-sheet" role="menu" aria-label={c("moreActions", "Clinician relationship actions")}><button type="button" role="menuitem" onClick={copyInternalLink}><InstitutionIcon src={ICONS.clinicianLink} size={20} /><span>{c("copyLink", "Copy link")}</span><span aria-hidden="true">›</span></button><div aria-hidden="true" /><button type="button" role="menuitem" onClick={() => { window.open(window.location.href, "_blank", "noopener,noreferrer"); setOverflowOpen(false); }}><InstitutionIcon src={ICONS.clinicianNewTab} size={20} /><span>{c("openInNewTab", "Open in new tab")}</span><span aria-hidden="true">›</span></button><div aria-hidden="true" /><button type="button" role="menuitem" className="is-destructive" onClick={() => { setAction("suspended"); setOverflowOpen(false); }}><InstitutionIcon src={ICONS.clinicianReport} size={20} /><span>{c("blockClinician", "Block Clinician")}</span></button><div aria-hidden="true" /><button type="button" role="menuitem" className="is-destructive" onClick={() => { setAction("removed"); setOverflowOpen(false); }}><InstitutionIcon src={ICONS.clinicianDelete} size={20} /><span>{c("delete", "Delete")}</span></button></div></InstitutionSideSheet> : null}
      <InstitutionSideSheet open={specialtyOpen} title={c("specialty", "Specialty")} iconSrc={ICONS.clinicianDiploma} variant="clinician-specialty" onClose={() => { setSpecialtyQuery(""); setRegistrationDateOpen(false); setSpecialtyOpen(false); }} labelledBy="institution-clinician-specialty-title" footer={<><button type="button" className="institutions-primary-button" onClick={() => { setSpecialtyQuery(""); setRegistrationDateOpen(false); setSpecialtyOpen(false); }}>{c("add", "Add")}</button><button type="button" className="institutions-side-sheet-cancel" onClick={() => { setSpecialtyQuery(""); setRegistrationDateOpen(false); setSpecialtyOpen(false); }}>{c("cancel", "Cancel")}</button></>}>
        <div className="institutions-side-sheet-form institution-clinician-specialty-sheet"><TextField56 label={c("specialty", "Specialty")} value={specialtyQuery} placeholder={c("searchOrAdd", "Search or add")} onChange={setSpecialtyQuery} tone="surface" clearable clearLabel={c("clearSpecialtySearch", "Clear Specialty search")} leadingSlot={<Search size={18} aria-hidden="true" />} />{specialtyQuery.trim() ? specialties.filter((item) => item.name.toLowerCase().includes(specialtyQuery.trim().toLowerCase())).map((item) => <button key={item.id} type="button" className="institution-clinician-search-add-row" onClick={() => { update("specialtyId", item.id); update("specialtyRequested", ""); setSpecialtyQuery(""); }}><span className="institution-clinician-search-add-icon"><Plus size={18} aria-hidden="true" /></span><span>{item.name}</span></button>) : null}{specialtyQuery.trim() && !specialties.some((item) => item.name.toLowerCase() === specialtyQuery.trim().toLowerCase()) ? <button type="button" className="institution-clinician-search-add-row" onClick={() => { update("specialtyId", ""); update("specialtyRequested", specialtyQuery.trim()); setSpecialtyQuery(""); }}><span className="institution-clinician-search-add-icon"><Plus size={18} aria-hidden="true" /></span><span>{`${c("add", "Add")} “${specialtyQuery.trim()}”`}</span></button> : null}{!specialtyQuery.trim() ? <p className="institution-clinician-search-hint">{c("startTyping", "Start typing to search or add a specialty.")}</p> : null}{form.specialtyId || form.specialtyRequested ? <div className="institution-clinician-selected-chip"><FocusChip label={specialties.find((item) => item.id === form.specialtyId)?.name || form.specialtyRequested} removable onRemove={() => { update("specialtyId", ""); update("specialtyRequested", ""); }} /></div> : null}<TextField56 label={c("registrationNumber", "Registration number")} value={form.registrationNumber} onChange={(value) => update("registrationNumber", value)} tone="surface" clearable clearLabel={c("clearRegistrationNumber", "Clear Registration number")} /><div ref={registrationDateAnchorRef} className="institution-clinician-registration-date-anchor"><TextField56 label={c("registrationDate", "Registration date")} as="button" value={formatClinicianDate(form.registrationDate)} placeholder={c("selectDate", "Select date")} onTriggerClick={() => setRegistrationDateOpen(true)} triggerAriaLabel={c("registrationDate", "Registration date")} triggerAriaHaspopup="dialog" triggerAriaExpanded={registrationDateOpen} triggerProps={{ ref: registrationDateTriggerRef }} tone="surface" leadingIcon={ICONS.clinicianDate} />{registrationDateOpen ? <div className="institution-clinician-registration-date-picker" role="dialog" aria-label={c("registrationDate", "Registration date")}><DatePicker value={form.registrationDate ? new Date(`${form.registrationDate}T12:00:00`) : null} maxDate={new Date()} anchorRef={registrationDateAnchorRef} dismissOnOutside dismissOnEscape returnFocusOnDismiss commitOnSelect onChange={(date) => { if (date && date <= new Date()) { update("registrationDate", date.toISOString().slice(0, 10)); update("registrationYear", String(date.getFullYear())); } setRegistrationDateOpen(false); window.requestAnimationFrame(() => registrationDateTriggerRef.current?.focus()); }} onCancel={() => setRegistrationDateOpen(false)} /></div> : null}</div>{form.registrationYear && !form.registrationDate ? <p className="institution-clinician-legacy-registration-year">{`${c("previouslyRecordedYear", "Previously recorded year")}: ${form.registrationYear}`}</p> : null}<SelectCountrySADC label={c("registrationCountry", "Registration country")} value={form.registrationJurisdiction} onChange={(value) => update("registrationJurisdiction", value)} tone="surface" useTextFieldTrigger /><div className="institution-clinician-diploma-card"><span>{diplomaEvidence ? <><strong className="text-title-medium">{diplomaEvidence.fileName}</strong><small>{c("diplomaDraftOnly", "Selected for this Institution roster draft")}</small><span className="institution-clinician-diploma-actions"><button type="button" className="institution-clinician-diploma-add" onClick={() => setDiplomaPickerOpen(true)}>{c("replace", "Replace")}</button><button type="button" className="institution-clinician-diploma-add institution-clinician-diploma-remove" onClick={removeDiploma}>{c("remove", "Remove")}</button></span></> : <><strong className="text-title-medium">{c("addDiploma", "Add your diploma")}</strong><button type="button" className="institution-clinician-diploma-add" onClick={() => setDiplomaPickerOpen(true)}>{c("add", "Add")}</button></>}</span><img src={ICONS.clinicianDiplomaLarge} alt="" /></div></div>
      </InstitutionSideSheet>
      <input ref={diplomaFileInputRef} type="file" accept="image/jpeg,image/png,image/webp,application/pdf" hidden onChange={(event) => { chooseDiploma(event.target.files?.[0]); event.target.value = ""; }} />
      <input ref={diplomaCameraInputRef} type="file" accept="image/jpeg,image/png,image/webp" capture="environment" hidden onChange={(event) => { chooseDiploma(event.target.files?.[0]); event.target.value = ""; }} />
      <InstitutionEvidenceSourceDialog open={diplomaPickerOpen} onClose={() => { setDiplomaError(""); setDiplomaPickerOpen(false); }} hasDocument={Boolean(diplomaEvidence)} error={diplomaError} title={c("addDiploma", "Add your diploma")} t={t} onPickFiles={() => diplomaFileInputRef.current?.click()} onTakePhoto={() => diplomaCameraInputRef.current?.click()} onRemove={removeDiploma} />
      <InstitutionSideSheet open={facilityOpen} title={c("assignedFacilities", "Assigned facilities")} description={c("facilityAssignmentSupport", "A clinician relationship requires one or more active Medical Institutions in this parent workspace.")} onClose={() => setFacilityOpen(false)} labelledBy="institution-clinician-facility-title" footer={<><button type="button" className="institutions-side-sheet-cancel" onClick={() => setFacilityOpen(false)}>{c("cancel", "Cancel")}</button><button type="button" className="institutions-primary-button" onClick={() => setFacilityOpen(false)}>{c("done", "Done")}</button></>}>
        <div className="institution-clinician-facility-options" role="group" aria-label={c("assignedFacilities", "Assigned facilities")}>{facilities.map((facility) => {
          const selected = form.facilityIds.includes(facility.id);
          return <button key={facility.id} type="button" className={selected ? "is-selected" : ""} aria-pressed={selected} onClick={() => update("facilityIds", selected ? form.facilityIds.filter((id) => id !== facility.id) : [...form.facilityIds, facility.id])}><span><strong>{facility.name || facility.facility?.facility_name}</strong><small>{facility.reference ? `ID: ${facility.reference}` : c("activeMedicalInstitution", "Active Medical Institution")}</small></span><span aria-hidden="true">{selected ? c("selected", "Selected") : c("select", "Select")}</span></button>;
        })}</div>
      </InstitutionSideSheet>
      <InstitutionSideSheet open={formationOpen} title={c("formation", "Formation")} onClose={closeFormation} footer={<><button type="button" className="institutions-side-sheet-cancel" onClick={closeFormation}>{c("cancel", "Cancel")}</button><button type="button" className="institutions-primary-button" onClick={saveEducation}>{editingEducationIndex === null ? c("add", "Add") : c("save", "Save")}</button></>}>
        <div className="institutions-side-sheet-form">
          <TextField56 label={c("qualification", "Qualification")} value={educationDraft.title} onChange={(value) => setEducationDraft((current) => ({ ...current, title: value }))} tone="surface" clearable clearLabel={c("clearQualification", "Clear Qualification")} />
          <TextField56 label={c("institution", "Institution")} value={educationDraft.institution} onChange={(value) => setEducationDraft((current) => ({ ...current, institution: value }))} tone="surface" clearable clearLabel={c("clearInstitution", "Clear Institution")} />
          <SelectCountrySADC label={c("country", "Country")} value={educationDraft.country} onChange={(value) => setEducationDraft((current) => ({ ...current, country: value }))} tone="surface" useTextFieldTrigger />
          <div className="institution-clinician-completion-date-anchor"><TextField56 label={c("completionDate", "Completion date")} as="button" value={formatClinicianDate(educationDraft.completionDate)} placeholder={c("selectDate", "Select date")} onTriggerClick={() => setCompletionDateOpen(true)} triggerAriaLabel={c("completionDate", "Completion date")} triggerAriaHaspopup="dialog" triggerAriaExpanded={completionDateOpen} triggerProps={{ ref: completionDateTriggerRef }} tone="surface" leadingIcon={ICONS.clinicianDate} />{completionDateOpen ? <div className="institution-clinician-completion-date-picker" role="dialog" aria-label={c("completionDate", "Completion date")}><DatePicker value={educationDraft.completionDate ? new Date(`${educationDraft.completionDate}T12:00:00`) : null} maxDate={new Date()} commitOnSelect={false} onConfirm={(date) => { if (date && date <= new Date()) setEducationDraft((current) => ({ ...current, completionDate: date.toISOString().slice(0, 10), year: String(date.getFullYear()) })); setCompletionDateOpen(false); window.requestAnimationFrame(() => completionDateTriggerRef.current?.focus()); }} onCancel={() => { setCompletionDateOpen(false); window.requestAnimationFrame(() => completionDateTriggerRef.current?.focus()); }} /></div> : null}</div>
          <div className="institution-clinician-diploma-card institution-clinician-formation-evidence-card"><span>{educationDraft.evidence ? <><strong className="text-title-medium">{educationDraft.evidence.fileName}</strong><small>{c("formationEvidenceSupplied", "Selected for this Formation record")}</small><span className="institution-clinician-diploma-actions"><button type="button" className="institution-clinician-diploma-add" onClick={() => setFormationEvidencePickerOpen(true)}>{c("replace", "Replace")}</button><button type="button" className="institution-clinician-diploma-add institution-clinician-diploma-remove" onClick={removeFormationEvidence}>{c("remove", "Remove")}</button></span></> : <><strong className="text-title-medium">{c("addQualificationCertificate", "Add qualification certificate")}</strong><button type="button" className="institution-clinician-diploma-add" onClick={() => setFormationEvidencePickerOpen(true)}>{c("add", "Add")}</button></>}</span><img src={ICONS.clinicianDiplomaLarge} alt="" /></div>
        </div>
      </InstitutionSideSheet>
      <input ref={formationEvidenceFileInputRef} type="file" accept="image/jpeg,image/png,image/webp,application/pdf" hidden onChange={(event) => { chooseFormationEvidence(event.target.files?.[0]); event.target.value = ""; }} />
      <input ref={formationEvidenceCameraInputRef} type="file" accept="image/jpeg,image/png,image/webp" capture="environment" hidden onChange={(event) => { chooseFormationEvidence(event.target.files?.[0]); event.target.value = ""; }} />
      <InstitutionEvidenceSourceDialog open={formationEvidencePickerOpen} onClose={() => { setFormationEvidenceError(""); setFormationEvidencePickerOpen(false); }} hasDocument={Boolean(educationDraft.evidence)} error={formationEvidenceError} title={c("addQualificationCertificate", "Add qualification certificate")} t={t} onPickFiles={() => formationEvidenceFileInputRef.current?.click()} onTakePhoto={() => formationEvidenceCameraInputRef.current?.click()} onRemove={removeFormationEvidence} />
      <InstitutionSideSheet open={languagesOpen} title={c("languages", "Languages")} onClose={() => setLanguagesOpen(false)} footer={<><button type="button" className="institutions-primary-button" onClick={() => setLanguagesOpen(false)}>{c("add", "Add")}</button><button type="button" className="institutions-side-sheet-cancel" onClick={() => setLanguagesOpen(false)}>{c("cancel", "Cancel")}</button></>}><div className="institutions-side-sheet-form"><MedicalInstitutionSpecialties className="institution-clinician-language-picker" heading="" menuId="institution-clinician-language-menu" searchPlaceholder={c("searchOrAdd", "Search or add")} addLabel={c("add", "Add")} addIconSrc={ICONS.clinicianFormationAdd} clearSearch clearSearchLabel={c("clearLanguageSearch", "Clear Languages search")} initialOpen specialties={languageCatalogue} selectedIds={languageSelectedIds} customSpecialties={languageCustom} onChange={(names) => setLanguageDrafts(mergeLanguageDrafts(names, languageCustom.map((item) => item.name)))} onCustomChange={(items) => setLanguageDrafts(mergeLanguageDrafts(languageSelectedIds, items.map((item) => item.name)))} /></div></InstitutionSideSheet>
      <InstitutionSideSheet open={Boolean(action)} title={action === "suspended" ? c("blockClinician", "Block Clinician") : c("delete", "Delete")} description={action === "suspended" ? c("blockScope", "This blocks only this Institution relationship.") : c("deleteScope", "This removes this Institution relationship while preserving the clinician account and records.")} onClose={() => { setAction(""); setReason(""); }} pending={busy} labelledBy="institution-clinician-status-title" variant="member" placement="center" footer={<><button type="button" className="institutions-side-sheet-cancel" onClick={() => { setAction(""); setReason(""); }} disabled={busy}>{c("cancel", "Cancel")}</button><button type="button" className="institutions-primary-button" onClick={runStatusAction} disabled={busy || reason.trim().length < 3}>{action === "suspended" ? c("blockClinician", "Block Clinician") : c("delete", "Delete")}</button></>}>
        <div className="institutions-side-sheet-form"><TextField56 label={`${c("reason", "Reason")}*`} value={reason} onChange={setReason} tone="surface" clearable clearLabel={c("clearReason", "Clear Reason")} inputProps={{ minLength: 3 }} /></div>
      </InstitutionSideSheet>
      <Snackbar open={Boolean(notice)} message={notice} onClose={() => setNotice("")} />
    </PageShell>
  );
}

function RegionalHealthView({ workspace, onInstitutionSelect }) {
  const institutionId = String(workspace?.activeInstitution?.id || "").trim();
  const readOnly = String(workspace?.scopedRole || "").toLowerCase() === "institution_evaluation_reviewer";
  const { t } = useLanguage();

  return (
    <PageShell
      workspace={workspace}
      title={t("institutionRegionalHealth.title", "Regional Health")}
      iconSrc={ICONS.regionalHealth}
      iconMask
      dashboardHeader
      onInstitutionSelect={onInstitutionSelect}
      pageTopBarTitle={t("institutionRegionalHealth.title", "Regional Health")}
      contentClassName="afrimed-rail afrimed-rail--comfortable pb-24"
    >
      <InstitutionRegionalHealthWorkspace institutionId={institutionId} readOnly={readOnly} />
    </PageShell>
  );
}

function DocumentsView({ workspace, mode }) {
  const rows = workspace.documents.filter((doc) => {
    if (mode === "payments") return doc.document_type === "payment_agreement";
    if (mode === "governance") return doc.document_type !== "payment_agreement";
    return true;
  });
  return (
    <PageShell workspace={workspace} title={mode === "payments" ? "Payments" : "Governance"} icon={mode === "payments" ? Landmark : FileText}>
      <section className="institutions-section">
        <div className="institutions-section-header">
          <h2>{mode === "payments" ? "Payment documents" : "MoU and governance documents"}</h2>
          <StatusPill>Read-only v0.1</StatusPill>
        </div>
        <DataTable
          empty="No documents have been uploaded by Super Admin yet."
          columns={["Document", "Type", "Status", "Signed", "Download"]}
          rows={rows}
          render={(doc) => [
            doc.title || "Untitled document",
            doc.document_type || "governance_document",
            doc.status || "pending",
            doc.signed_at ? formatDate(doc.signed_at) : "Setup pending",
            doc.storage_path ? "Storage path available" : "Setup pending",
          ]}
        />
      </section>
    </PageShell>
  );
}

function FacilityLogo({ facility }) {
  const logo = getFacilityLogo(facility);
  return (
    <span className={`institutions-facility-logo${logo ? "" : " is-placeholder"}`} aria-hidden="true">
      {logo ? <img src={logo} alt="" /> : <img className="is-placeholder" src={ICONS.facilityListAvatar} alt="" />}
    </span>
  );
}

function getMedicalInstitutionSearchResults(facilities = [], query = "") {
  const normalizedQuery = String(query || "").trim().toLocaleLowerCase();
  if (!normalizedQuery) return facilities;
  return facilities.filter((facility) => {
    const searchable = [
      getFacilityName(facility),
      getFacilityType(facility),
      facility?.reference,
      facility?.id,
      getFacilityDescription(facility),
      facility?.city,
      facility?.country,
    ].join(" ").toLocaleLowerCase();
    return searchable.includes(normalizedQuery);
  });
}

function getMedicalInstitutionSearchMembers(members = [], query = "") {
  return filterMedicalInstitutionMemberSearchIndex(members, query);
}

function MedicalInstitutionSearchGroup({ label, rows, expanded, onViewAll, renderRow }) {
  const { t } = useLanguage();
  const preview = expanded ? rows : rows.slice(0, 3);
  if (!preview.length) return null;
  return (
    <section className="medical-institution-search-group" aria-label={label}>
      <header className="medical-institution-search-group-heading">
        <span>{label}</span>
        {rows.length > 3 ? <button type="button" onClick={onViewAll}>{expanded ? t("institutionWorkspace.showLess", "Show less") : t("institutionWorkspace.viewAll", "View all")}</button> : null}
      </header>
      <div className="medical-institution-search-group-rows" role="listbox" aria-label={t("institutionWorkspace.searchResults", "{label} search results").replace("{label}", label)}>
        {preview.map(renderRow)}
      </div>
    </section>
  );
}

function MemberSearchAvatar({ member }) {
  const avatar = getMemberAvatar(member);
  return avatar ? <AvatarThumb44 src={avatar} alt="" size={44} shape="circle" role="doctor" /> : <span className="medical-institution-member-search-avatar" aria-hidden="true"><InstitutionIcon src={ICONS.facilityMember} fallback={UserRound} size={24} useMask /></span>;
}

function MedicalInstitutionSearchResults({ query, facilities, members = [], loading = false, error = "", onSelect, onSelectMember }) {
  const { t } = useLanguage();
  const [expandedFacilities, setExpandedFacilities] = useState(false);
  const [expandedMembers, setExpandedMembers] = useState(false);
  const hasQuery = hasMedicalInstitutionSearchQuery(query);
  const facilityRows = getMedicalInstitutionSearchResults(facilities, query);
  const memberRows = getMedicalInstitutionSearchMembers(members, query);

  useEffect(() => {
    setExpandedFacilities(false);
    setExpandedMembers(false);
  }, [query]);

  // Opening Search is intentionally quiet: authorised records render only
  // after a deliberate keyword is entered, on desktop and mobile alike.
  if (!hasQuery) return null;
  if (loading) return <div className="medical-institution-search-status"><RouteFallback label={t("institutionWorkspace.loading", "Loading Medical Institutions…")} /></div>;
  if (error) return <p className="medical-institution-search-status" role="alert">{error}</p>;
  if (!facilityRows.length && !memberRows.length) return <p className="medical-institution-search-status">{t("institutionWorkspace.noSearchResults", "No Medical Institutions or Members match “{query}”.").replace("{query}", String(query || "").trim())}</p>;
  return (
    <div className="medical-institution-search-results">
      <MedicalInstitutionSearchGroup
        label={t("institutionShell.navigation.medicalInstitutions", "Medical Institutions")}
        rows={facilityRows}
        expanded={expandedFacilities}
        onViewAll={() => setExpandedFacilities((current) => !current)}
        renderRow={(facility) => (
          <button className="medical-institution-search-result-row" key={facility.id} type="button" role="option" onClick={() => onSelect(facility)}>
          <FacilityLogo facility={facility} />
          <span className="medical-institution-search-result-copy">
            <strong>{getFacilityName(facility)}</strong>
            <small>{getFacilityType(facility)}</small>
            <small>ID: {facility.reference || truncateId(facility.id)}</small>
          </span>
          <ChevronRight size={20} aria-hidden="true" />
          </button>
        )}
      />
      <MedicalInstitutionSearchGroup
        label={t("institutionShell.navigation.members", "Members")}
        rows={memberRows}
        expanded={expandedMembers}
        onViewAll={() => setExpandedMembers((current) => !current)}
        renderRow={(member) => (
          <button className="medical-institution-search-result-row medical-institution-search-result-row--member" key={member.membershipKey} type="button" role="option" onClick={() => onSelectMember?.(member)}>
            <MemberSearchAvatar member={member} />
            <span className="medical-institution-search-result-copy">
              <span className="medical-institution-search-member-headline"><strong>{getMemberName(member)}</strong><StatusPill tone="success">{facilityMemberRoleLabel(member.role, member.customRoleLabel)}</StatusPill></span>
              <small>{[member.phone, member.email, member.facilityName ? t("institutionWorkspace.facilityLabel", "Facility: {name}").replace("{name}", member.facilityName) : ""].filter(Boolean).join(" · ")}</small>
            </span>
            <ChevronRight size={20} aria-hidden="true" />
          </button>
        )}
      />
    </div>
  );
}

function MedicalInstitutionSearchSurface({ open, facilities, members = [], loading = false, error = "", onClose, onSelect, onSelectMember }) {
  const { t } = useLanguage();
  const [query, setQuery] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    setQuery("");
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    window.requestAnimationFrame(() => inputRef.current?.focus());
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose, open]);

  if (!open || typeof document === "undefined") return null;
  return createPortal(
    <div className="medical-institution-search-backdrop" onMouseDown={onClose}>
      <section id="medical-institution-search-dialog" className="medical-institution-search-surface" role="dialog" aria-modal="true" aria-label={t("institutionShell.searchMedicalInstitutions", "Search medical institutions")} onMouseDown={(event) => event.stopPropagation()}>
        <div className="medical-institution-search-header">
          <span className="medical-institution-search-header-icon" aria-hidden="true"><InstitutionIcon src={ICONS.search} fallback={Search} size={24} /></span>
          <input
            ref={inputRef}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t("institutionShell.searchMedicalInstitutions", "Search medical institutions")}
            aria-label={t("institutionShell.searchMedicalInstitutions", "Search medical institutions")}
            autoComplete="off"
          />
        </div>
        <div className="medical-institution-search-divider" aria-hidden="true" />
        <div className="medical-institution-search-list">
          <MedicalInstitutionSearchResults query={query} facilities={facilities} members={members} loading={loading} error={error} onSelect={onSelect} onSelectMember={onSelectMember} />
        </div>
      </section>
    </div>,
    document.body,
  );
}

function SpecialtyChips({ facility, notProvided = "Not provided" }) {
  const specialties = getFacilitySpecialties(facility);
  if (!specialties.length) return <span className="institutions-list-cell-truncate">{notProvided}</span>;
  const [first, ...rest] = specialties;
  return (
    <span className="institutions-specialty-stack">
      <StatusPill tone="info">{first}</StatusPill>
      {rest.length ? <StatusPill tone="info">+{rest.length}</StatusPill> : null}
    </span>
  );
}

function FacilitiesTable({ rows = [], onOpen, t }) {
  return (
    <div className="institutions-list-table" data-node-id="60781:68895">
      <div className="institutions-list-header" data-node-id="61354:139824">
        <span className="text-body-medium text-on-surface-variant">{t("institutionRegistry.columns.name", "Institution Name")}</span>
        <span className="text-body-medium text-on-surface-variant">{t("institutionRegistry.columns.type", "Type")}</span>
        <span className="text-body-medium text-on-surface-variant">{t("institutionRegistry.columns.specialties", "Specialties")}</span>
        <span className="text-body-medium text-on-surface-variant">{t("institutionRegistry.columns.description", "Description")}</span>
        <span className="text-body-medium text-on-surface-variant">{t("institutionRegistry.columns.address", "Address")}</span>
        <span className="text-body-medium text-on-surface-variant">{t("institutionRegistry.columns.domain", "Domain")}</span>
        <span className="text-body-medium text-on-surface-variant">{t("institutionRegistry.columns.openingHours", "Opening hours")}</span>
      </div>
      <div className="institutions-list-body">
        {rows.map((link) => <FacilityRow key={link.id || link.healthcare_location_id} link={link} onOpen={onOpen} t={t} />)}
      </div>
    </div>
  );
}

function FacilityRow({ link, onOpen, t }) {
  const facility = link?.facility || link || {};
  const notProvided = t("institutionMedicalInstitutionDetail.notSupplied", "Not provided");
  const setupPending = t("institutionControlled.setup_pending", "Setup pending");
  const hours = getFacilityHours(facility, setupPending);
  const domain = getFacilityDomain(facility, notProvided);
  const rawDomain = facility?.domain || facility?.website || facility?.url || "";
  const reference = link?.reference || facility?.reference || `MI-${String(facility?.id || link?.id || "").split("-")[0].toUpperCase()}`;
  const open = () => onOpen?.(link?.id || facility?.id);
  const handleClick = (event) => {
    if (event.target.closest?.("a, button, input, select, textarea")) return;
    if (typeof window !== "undefined" && window.getSelection?.()?.toString()) return;
    open();
  };
  const handleKeyDown = (event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); open(); } };
  return (
    <div role="link" tabIndex={0} className="institutions-list-row institutions-list-row--interactive" data-node-id="60802:55599" onClick={handleClick} onKeyDown={handleKeyDown}>
      <span className="institutions-facility-name">
        <FacilityLogo facility={facility} />
        <span>
          <strong className="text-body-medium text-on-surface">{getFacilityName(facility)}</strong>
          <small className="text-body-small text-on-surface-variant">ID: {reference}</small>
        </span>
      </span>
      <span className="institutions-facility-type text-body-medium text-on-surface" data-mobile-label={t("institutionRegistry.columns.type", "Type")}>
        <span className="institutions-facility-type-value">
          <InstitutionIcon src={getFacilityTypeIcon(getFacilityType(link))} fallback={Building2} size={18} useMask />
          <span>{t(`institutionMedicalInstitutionDetail.types.${String(getFacilityType(link)).toLowerCase().replace(/\s+(.)/g, (_, character) => character.toUpperCase())}`, getFacilityType(link))}</span>
        </span>
      </span>
      <span data-mobile-label={t("institutionRegistry.columns.specialties", "Specialties")}><SpecialtyChips facility={facility} notProvided={notProvided} /></span>
      <span className="institutions-list-cell-truncate text-body-medium text-on-surface" data-mobile-label={t("institutionRegistry.columns.description", "Description")} title={getFacilityDescription(facility, notProvided)}>{getFacilityDescription(facility, notProvided)}</span>
      <span className="institutions-list-cell-truncate text-body-medium text-on-surface" data-mobile-label={t("institutionRegistry.columns.address", "Address")} title={getFacilityAddress(facility, notProvided)}>{getFacilityAddress(facility, notProvided)}</span>
      <span className="institutions-list-cell-truncate" data-mobile-label={t("institutionRegistry.columns.domain", "Domain")}>{rawDomain ? <a className="institutions-domain-link text-body-medium" href={/^https?:\/\//i.test(rawDomain) ? rawDomain : `https://${rawDomain}`} target="_blank" rel="noreferrer" onClick={(event) => event.stopPropagation()}>{domain}</a> : domain}</span>
      <span className="institutions-hours" data-mobile-label={t("institutionRegistry.columns.openingHours", "Opening hours")}>
        <span className="institutions-hours-days text-body-medium text-on-surface">{hours.days}</span>
        {hours.time ? <small>{hours.time}</small> : null}
      </span>
    </div>
  );
}

function DataTable({ columns, rows, render, empty }) {
  if (!rows?.length) {
    return <SetupState>{empty}</SetupState>;
  }
  return (
    <div className="institutions-table-wrap">
      <table className="institutions-table">
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column}>{column}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={row?.id || index}>
              {render(row).map((cell, cellIndex) => (
                <td key={`${row?.id || index}-${cellIndex}`}>{cell || "Setup pending"}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Forbidden({ workspace }) {
  const { t } = useLanguage();
  return (
    <div className="institutions-standalone institutions-standalone--access">
      <InstitutionIcon src={ICONS.surveillance} size={32} useMask />
      <h1>{t("institutionWorkspace.accessUnavailable", "Institution access unavailable")}</h1>
      <p>{t("institutionWorkspace.accessUnavailableBody", "Your account is not currently linked to an active Institution Administrator or Owner membership.")}</p>
      <div className="institutions-setup-list">
        {(workspace?.userRoles || []).map((role) => (
          <StatusPill key={role}>{formatRole(role)}</StatusPill>
        ))}
      </div>
    </div>
  );
}

function WorkspaceLoadingFailure({ error, onRetry }) {
  const { t } = useLanguage();
  const failedOperation = error?.workspaceOperation || t("institutionWorkspace.workspaceRequest", "Institution workspace request");
  const failureMessage = error?.message || t("institutionWorkspace.workspaceLoadFallback", "The Institution workspace could not be loaded.");
  return (
    <div className="institutions-standalone institutions-standalone--access">
      <MessageSquareWarning size={32} aria-hidden="true" />
      <h1>{t("institutionWorkspace.workspaceLoadTitle", "Institution workspace could not load")}</h1>
      <p>{t("institutionWorkspace.workspaceLoadBody", "Your authenticated session was established, but a required workspace request did not complete.")}</p>
      <div className="institutions-setup-list">
        <p><strong>{t("institutionWorkspace.failedRequest", "Failed request:")}</strong> {failedOperation}</p>
        <p><strong>{t("institutionWorkspace.response", "Response:")}</strong> {failureMessage}</p>
      </div>
      <Button type="button" variant="secondary" onClick={onRetry}>{t("institutionWorkspace.retry", "Retry")}</Button>
    </div>
  );
}

export default function InstitutionsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const auth = useAuth() || {};
  const [workspace, setWorkspace] = useState(null);
  const [mohDisposition, setMoHDisposition] = useState({ checking: true });
  const [refreshKey, setRefreshKey] = useState(0);
  const [error, setError] = useState("");
  // React development StrictMode intentionally re-runs effects. The workspace
  // snapshot is governed but read-only, so concurrent equivalent requests can
  // safely share one promise instead of making first Home paint wait twice.
  const workspaceRequestRef = useRef({ key: "", promise: null });
  const selectedInstitutionId = useMemo(
    () => String(new URLSearchParams(location.search).get("institution") || "").trim(),
    [location.search],
  );
  const section = useMemo(() => {
    const last = location.pathname.split("/").filter(Boolean).pop();
    return last === "institutions" ? "dashboard" : last;
  }, [location.pathname]);
  const explicitMoHExploreArrival = location.state?.mohExploreArrival === true;

  useEffect(() => {
    let alive = true;
    if (!auth?.user?.id || auth?.profileResolved === false) return undefined;
    setMoHDisposition({ checking: true });
    resolveMoHWorkspaceDisposition()
      .then((next) => {
        if (alive) setMoHDisposition({ checking: false, ...next });
      })
      .catch(() => {
        if (alive) setMoHDisposition({ checking: false, shouldResumeOnboarding: false });
      });
    return () => {
      alive = false;
    };
  }, [auth?.profileResolved, auth?.user?.id]);

  const selectInstitution = React.useCallback((institutionId) => {
    const nextId = String(institutionId || "").trim();
    const params = new URLSearchParams(location.search);
    if (nextId) params.set("institution", nextId);
    else params.delete("institution");
    const search = params.toString();
    navigate({ pathname: location.pathname, search: search ? `?${search}` : "" });
  }, [location.pathname, location.search, navigate]);

  useEffect(() => {
    let alive = true;
    setError(null);
    const requestKey = `${selectedInstitutionId}|${section}|${refreshKey}`;
    const cachedRequest = workspaceRequestRef.current;
    const request = cachedRequest.key === requestKey && cachedRequest.promise
      ? cachedRequest.promise
      : loadInstitutionWorkspace({
          institutionId: selectedInstitutionId,
          loadMode: section === "home" ? "home" : section === "regional-health" ? "regional-health" : ["follow-ups", "alerts"].includes(section) ? "follow-ups" : "full",
        });
    if (cachedRequest.key !== requestKey || !cachedRequest.promise) {
      workspaceRequestRef.current = { key: requestKey, promise: request };
    }

    request
      .then((result) => {
        if (alive) {
          setWorkspace(result);
          setError(null);
        }
      })
      .catch((err) => {
        // Preserve only operational metadata: this is enough to diagnose a failed
        // workspace request without logging account, membership, or clinical data.
        console.error("Institution workspace request failed", {
          operation: err?.workspaceOperation || "Institution workspace request",
          code: err?.code || "",
          message: err?.message || "Unable to load institution workspace.",
        });
        if (alive) setError(err instanceof Error ? err : new Error(String(err || "Unable to load institution workspace.")));
      });
    return () => {
      alive = false;
    };
  }, [selectedInstitutionId, refreshKey, section]);

  useEffect(() => {
    const resolvedInstitutionId = String(workspace?.activeInstitution?.id || "").trim();
    if (
      workspace?.status !== "ready" ||
      !resolvedInstitutionId ||
      selectedInstitutionId === resolvedInstitutionId
    ) {
      return;
    }
    const params = new URLSearchParams(location.search);
    params.set("institution", resolvedInstitutionId);
    navigate(
      {
        pathname: location.pathname,
        search: `?${params.toString()}`,
      },
      { replace: true },
    );
  }, [location.pathname, location.search, navigate, selectedInstitutionId, workspace]);

  const refreshWorkspace = () => setRefreshKey((key) => key + 1);

  const medicalInstitutionRoute = useMemo(() => {
    const match = location.pathname.match(/^\/institutions\/medical-institutions(?:\/([^/]+))?\/?$/);
    return match ? { facilityId: match[1] || "" } : null;
  }, [location.pathname]);
  const clinicianRoute = useMemo(() => {
    const match = location.pathname.match(/^\/institutions\/clinicians(?:\/([^/]+))?\/?$/);
    return match ? { rosterId: match[1] || "" } : null;
  }, [location.pathname]);
  const patientRoute = useMemo(() => {
    const match = location.pathname.match(/^\/institutions\/patients(?:\/([^/]+))?\/?$/);
    return match ? { relationshipId: match[1] || "" } : null;
  }, [location.pathname]);
  const serviceRoute = useMemo(() => {
    const match = location.pathname.match(/^\/institutions\/services(?:\/([^/]+))?\/?$/);
    return match ? { serviceId: match[1] || "" } : null;
  }, [location.pathname]);

  if (location.pathname === "/institutions") {
    return <Navigate to="/institutions/home" replace />;
  }
  // The Institutions catch-all can otherwise render a repeated legacy segment
  // (`/institutions/institutions/...`). Canonicalise before resolving a page,
  // retaining the authorised Institution context from the query string.
  if (location.pathname.startsWith("/institutions/institutions/")) {
    return <Navigate to={{ pathname: location.pathname.replace(/^\/institutions\/institutions\//, "/institutions/"), search: location.search }} replace />;
  }

  if (mohDisposition.checking && auth?.user?.id && auth?.profileResolved !== false) {
    return <RouteFallback label={t("institutionWorkspace.loading", "Loading Medical Institutions…")} />;
  }

  if (mohDisposition.shouldResumeOnboarding && !explicitMoHExploreArrival) {
    return <Navigate to="/onboarding/moh" replace />;
  }

  if (mohDisposition.hasInactiveMembership) {
    return <Forbidden workspace={{ userRoles: ["moh_admin"] }} />;
  }

  if (error) {
    return <WorkspaceLoadingFailure error={error} onRetry={refreshWorkspace} />;
  }

  if (!workspace) {
    return (
      <div className="institutions-loading-surface">
        <RouteFallback label={t("institutionWorkspace.loading", "Loading Medical Institutions…")} />
      </div>
    );
  }
  if (workspace.status === "signed_out") return <Navigate to="/login" replace />;
  if (workspace.status === "forbidden") return <Forbidden workspace={workspace} />;
  if (workspace.status === "empty") {
    return (
      <PageShell workspace={workspace} title={t("institutionShell.navigation.medicalInstitutions", "Medical Institutions")} iconSrc={ICONS.logo}>
        <SetupState title={t("institutionWorkspace.setupPending", "Setup pending")}>
          {t("institutionWorkspace.setupBody", "A Super Admin must create or assign this Institution before you can start using the control centre.")}
        </SetupState>
      </PageShell>
    );
  }

  if (section === "home") return <InstitutionHome workspace={workspace} onInstitutionSelect={selectInstitution} />;
  if (section === "dashboard") return <Dashboard workspace={workspace} onInstitutionSelect={selectInstitution} />;
  if (section === "help") return <Navigate to={{ pathname: "/institutions/docs", search: location.search }} replace />;
  if (section === "docs") return <InstitutionHelp workspace={workspace} onInstitutionSelect={selectInstitution} />;
  if (section === "notifications") return <InstitutionNotifications workspace={workspace} onInstitutionSelect={selectInstitution} />;
  if (location.pathname.replace(/\/$/, '') === '/institutions/settings/integrations-interoperability') {
    return canAccessInstitutionConfiguration(workspace)
      ? <PageShell workspace={workspace} title={t("institutionSettings.title", "Settings")} icon={Settings} iconSrc={ICONS.settings} pageTopBarTitle={t("settings.preferences.integrations.title", "Integrations • Interoperability")} pageTopBarNavigation={INSTITUTION_TOP_BAR_NAVIGATION.BACK} pageTopBarBackTo="/institutions/settings"><IntegrationsInteroperability role="institution" institution={workspace.activeInstitution} embedded /></PageShell>
      : <Forbidden workspace={workspace} />;
  }
  if (section === "settings") {
    return canAccessInstitutionConfiguration(workspace)
      ? <SettingsView workspace={workspace} />
      : <Forbidden workspace={workspace} />;
  }
  if (section === "account") return <InstitutionAccountView workspace={workspace} onInstitutionSelect={selectInstitution} />;
  if (section === "feedback") return <InstitutionFeedbackView workspace={workspace} onInstitutionSelect={selectInstitution} />;
  if (section === "users") return <PageShell workspace={workspace} title={t("institutionPatients.title", "Patients")} icon={Users} iconSrc={ICONS.pagePatients} pageTopBarTitle={t("institutionPatients.title", "Patients")} contentClassName="afrimed-rail afrimed-rail--comfortable pb-24"><InstitutionPatientsList workspace={workspace} onRefresh={refreshWorkspace} /></PageShell>;
  if (section === "manage") return <Navigate to={{ pathname: "/institutions/medical-institutions", search: location.search }} replace />;
  if (section === "facilities") return <Navigate to={{ pathname: "/institutions/medical-institutions", search: location.search }} replace />;
  if (medicalInstitutionRoute?.facilityId === "new") return <MedicalInstitutionCreateView workspace={workspace} />;
  if (medicalInstitutionRoute?.facilityId) return <MedicalInstitutionDetailView workspace={workspace} facilityId={medicalInstitutionRoute.facilityId} />;
  if (medicalInstitutionRoute) return <MedicalInstitutionsRegistryView workspace={workspace} />;
  if (section === "members") return <InstitutionMembersView workspace={workspace} />;
  if (section === "surveillance-team") return <SurveillanceTeamView workspace={workspace} onRefresh={refreshWorkspace} />;
  if (section === "alerts") return <PageShell workspace={workspace} title="Regional Health alerts" iconSrc={ICONS.surveillance} iconMask pageTopBarTitle="Governed synthetic alerts" contentClassName="afrimed-rail afrimed-rail--comfortable pb-24"><InstitutionAlertJourney institutionId={workspace.activeInstitution?.id} /></PageShell>;
  if (section === "follow-ups") return <PageShell workspace={workspace} title="Contact follow-up" iconSrc={ICONS.surveillance} iconMask pageTopBarTitle="Contact follow-up" contentClassName="afrimed-rail afrimed-rail--comfortable pb-24"><InstitutionFollowupWorkspace institutionId={workspace.activeInstitution?.id} /></PageShell>;
  if (section === "regional-health") return <RegionalHealthView workspace={workspace} onInstitutionSelect={selectInstitution} />;
  if (clinicianRoute?.rosterId === "new") return <ClinicianEditor workspace={workspace} onRefresh={refreshWorkspace} />;
  if (clinicianRoute?.rosterId) {
    const clinician = (workspace?.linkedClinicians || []).find((item) => item?.id === clinicianRoute.rosterId);
    if (!clinician) return <PageShell workspace={workspace} title="Clinicians" icon={Stethoscope}><div className="medical-institution-error" role="alert"><p>This clinician relationship is not available in the active parent Institution.</p></div><MedicalInstitutionBackButton label="Back to Clinicians" onClick={() => navigate(clinicianPath(workspace?.activeInstitution?.id))} /></PageShell>;
    return <ClinicianEditor workspace={workspace} clinician={clinician} onRefresh={refreshWorkspace} />;
  }
  if (clinicianRoute) return <CliniciansView workspace={workspace} />;
  if (patientRoute?.relationshipId === "new") return <PageShell workspace={workspace} title="Patients" icon={Users} iconSrc={ICONS.pagePatients} profileDetail contentClassName="afrimed-rail pb-24"><InstitutionPatientEditor workspace={workspace} onRefresh={refreshWorkspace} /></PageShell>;
  if (patientRoute?.relationshipId === "preview" && import.meta.env.DEV === true && location.state?.previewPatient) return <PageShell workspace={workspace} title="Patients" icon={Users} iconSrc={ICONS.pagePatients} profileDetail contentClassName="afrimed-rail pb-24"><InstitutionPatientDetail workspace={workspace} relationship={location.state.previewPatient} preview /></PageShell>;
  if (patientRoute?.relationshipId) {
    const relationship = (workspace?.patients || []).find((item) => item?.id === patientRoute.relationshipId);
    if (!relationship) return <PageShell workspace={workspace} title="Patients" icon={Users} iconSrc={ICONS.pagePatients}><div className="medical-institution-error" role="alert"><p>This patient relationship is not available in the active parent Institution.</p></div><MedicalInstitutionBackButton label="Back to Patients" onClick={() => navigate(patientPath(workspace?.activeInstitution?.id))} /></PageShell>;
    return <PageShell workspace={workspace} title="Patients" icon={Users} iconSrc={ICONS.pagePatients} profileDetail contentClassName="afrimed-rail pb-24"><InstitutionPatientDetail workspace={workspace} relationship={relationship} onRefresh={refreshWorkspace} /></PageShell>;
  }
  if (patientRoute) return <PageShell workspace={workspace} title={t("institutionPatients.title", "Patients")} icon={Users} iconSrc={ICONS.pagePatients} pageTopBarTitle={t("institutionPatients.title", "Patients")} contentClassName="afrimed-rail afrimed-rail--comfortable pb-24"><InstitutionPatientsList workspace={workspace} onRefresh={refreshWorkspace} /></PageShell>;
  if (serviceRoute?.serviceId) return <ServiceEditor workspace={workspace} serviceId={serviceRoute.serviceId} onRefresh={refreshWorkspace} />;
  if (serviceRoute) return <ServicesPage workspace={workspace} />;
  if (section === "services") return <ServicesPage workspace={workspace} />;
  if (section === "payments") return <DocumentsView workspace={workspace} mode="payments" />;
  if (section === "governance") return <DocumentsView workspace={workspace} mode="governance" />;

  return <Navigate to={{ pathname: "/institutions/dashboard", search: location.search }} replace />;
}

export {
  FIGMA_NODES_USED,
  InstitutionStatusFilterChip,
  InstitutionIcon,
  InstitutionSideSheet,
  MedicalInstitutionBackButton,
  PageShell,
  StatusPill,
};
